import * as THREE from 'three';
import { makeGLTFLoader } from './core/gltf.js';
import { loadGLBWithRetry } from './core/net.js';
import { APP_VERSION } from './core/version.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// Device tier + the stage (renderer/scene/camera/lights) live in core
// modules; everything else still lives in this file, deliberately — the
// world/creature/player code is restructured wholesale by the chunked-world
// refactor (design plan step 3), so splitting it along today's architecture
// would just be churn. New systems get their own modules from the start.
import { TIER, LOW, ULTRA_LOW_SHADOWS, cachedTexture } from './core/tier.js';
import { TUNING, poolScale } from './core/tuning.js';
import { schedulePrewarm, prewarmTexture } from './core/prewarm.js';
import { PALETTE, ACCENTS, BUILDING_SCHEMES, ZONE_VISUALS, rgb, css } from './core/palette.js';
import {
  scene, camera, renderer, isContextLost,
  SHADOW_INTERVAL, FOG_BASE_DENSITY,
  hemi, key, fill, rim, floor,
} from './core/stage.js';
import {
  touchInput, HAS_TOUCH, RUN_DEFLECT,
  initTouchControls, setTouchUIVisible, clearMoveTarget, updateTouchMarker,
} from './game/touchControls.js';
// The world systems (design plan step 3): the ground is a sampled heightfield,
// collision is a spatial hash, props are data records streamed as per-chunk
// instanced meshes, and every moving thing registers in the actor registry.
import {
  heightAt, displaceGeometryToTerrain, WORLD_HALF,
  sculpt, redisplaceRegion, getHeightDeltas, setHeightDeltas, clearHeightDeltas,
} from './world/heightfield.js';
import {
  addObstacle, collectObstaclesNear, raycastObstacles, raycastObstacleTops,
  addMovableObstacle, updateMovableObstacle, removeMovableObstacle,
  beginObstacleCapture, endObstacleCapture,
} from './world/collision.js';
import {
  addProp, attachChunksTo, updateChunkStreaming, updateSwayTime, chunkStats,
  addPropLive, removePropById, removePropNear, enumerateProps, resettleProps, movePropRecord, rebuildChunkAt,
  updateOcclusionFade, readOcclusionFadeState, readSwayState,
} from './world/chunks.js';
import { OCCLUSION_FADE_DISABLED, FADE_MIN, easeFade } from './world/occlusionFade.js';
import { markDynamic, freezeStatics } from './world/freezeStatics.js';
import { PROP_PALETTE_BY_KEY, expandProp } from './world/propPalette.js';
import { applyEditLayer } from './world/applyEditLayer.js';
import { loadBakedLayer } from './world/bakedWorld.js';
import { loadKit, kitReady, kitGeometry, kitSize, kitMaterialClone, KIT_KINDS, KIT_STRUCTURE_KINDS } from './world/kit.js';
import { actors, spawnActor, despawnActor } from './world/actors.js';
import { buildWordville, updateWordville } from './world/zones/wordville.js';
import { buildGrove, updateGrove } from './world/zones/grove.js';
import { OBBY_CENTER, OBBY_SUMMIT_R, OBBY_SUMMIT_TOP, buildObby, updateObby } from './world/zones/obby.js';
import { buildNumberton, updateNumberton } from './world/zones/numberton.js';
import { buildBrook, buildFootbridge, buildFishingHole, buildDock, updateWater } from './world/water.js';
import { buildVillageDecor } from './world/villageDecor.js';
import { initClouds, updateClouds } from './world/clouds.js';
// Wallet + wardrobe persistence and the outfit store (new systems, own modules).
import { loadSave, patchSave } from './game/save.js';
import { initOutfits, onChockyReady as outfitsOnChockyReady, equip as equipOutfit, getEquipped as getEquippedOutfit, CATALOG as OUTFIT_CATALOG } from './game/outfits.js';
import { initStore } from './game/store.js';
import { initMinimap } from './game/minimap.js';
import { initQuest } from './game/quest.js';
import { initQuest as initCritterQuest } from './game/critterquest.js';
import { initQuest as initBunnyQuest } from './game/bunnyquest.js';
import { createWizardLightningFx } from './game/wizardLightning.js';

// === BUILD VERSION ===
// APP_VERSION lives in core/version.js (single source shared with the headless
// tests); bump it there on each release. Shown in the badge and the Sun panel.
const DEBUG_LOAD = false;
{
  const vb = document.getElementById('versionBadge');
  if (vb) vb.textContent = APP_VERSION;
  const vp = document.getElementById('sunPanelVersion');
  if (vp) vp.textContent = 'Chockytown ' + APP_VERSION;
  const ov = document.getElementById('optionsVersion');
  if (ov) ov.textContent = APP_VERSION;
}

// === EDITOR INTEGRATION ===
// All dev tooling — the viewer top bar, bone/pose editor, light panels, sign
// editor and sun panel — lives in ./editor/editor.js. Its chunk is async-only
// (a dynamic import below) and kept out of both the service-worker precache and
// HTML preload, so a load that doesn't use it never fetches it (see
// vite.config.js). Core code talks to the editor only through editorHooks; with
// no editor loaded, the no-op defaults apply.
//
// The editor is now available BY DEFAULT in every mode — the no-build page, the
// dev server, AND the published dist/ bundle — so a teacher/dev can just open
// gumball.city and author the world in place (press \ for the editor shell; see
// the top-right HELP button). The locked-down, kid-facing player build is opt-in
// via the ?prod launch flag: it strips all dev tooling (no editor chunk fetch,
// no HELP button). The separate build:player artifact compiles that lock in so
// ?editor=1 cannot re-enable authoring. ?editor=1 stays as an explicit "force
// the editor on" alias for normal editable builds so old links keep working
// even alongside ?prod.
const _editorParams = new URLSearchParams(location.search);
const COMPILED_PLAYER_BUILD = typeof __PLAYER_BUILD__ !== 'undefined' && __PLAYER_BUILD__;
const PROD_PLAYER = COMPILED_PLAYER_BUILD || _editorParams.has('prod');
const EDITOR_REQUESTED = !COMPILED_PLAYER_BUILD && _editorParams.get('editor') === '1';
const EDITOR_ENABLED = !COMPILED_PLAYER_BUILD && (EDITOR_REQUESTED || !PROD_PLAYER);
const editorHooks = {
  // State the main loop reads every frame; the editor module mutates it.
  paused: false,
  autoSpin: false,
  boneOverrides: new Map(),          // bone -> quaternion override
  // True while an object/terrain editor is open. Suppresses tap-to-move so that
  // clicking a building to select it never also walks Chocky to that spot.
  editing: false,
  // Replaced by the editor module when it loads.
  onPlayModeChange(on) {},
  onModelReady() {},
  onDayTimeChange() {},
  handleHotkey(e) { return false; },
  // Opens the editor's Build-tuning panel (ESC menu → "Build tuning"). No-op
  // until the editor chunk loads and replaces it.
  openBuildPanel() {},
  // Latched true once chocky.glb is set up. Because chocky now streams in
  // parallel with the kit/baked world, the model can be ready BEFORE the async
  // editor chunk loads — so onModelReady() may fire into the no-op above. The
  // editor reads this on load and populates itself if it missed the call.
  modelReady: false,
};

// ---- Editor HELP affordance (top-right) -----------------------------------
// When the editor is available (EDITOR_ENABLED — i.e. NOT the ?prod player
// build), reveal the top-right "❓ Help" button (markup in index.html) and wire
// its panel. Visibility is finished in CSS: html.editor-available.kiosk shows
// it, so it rides the clean kiosk view and tucks away the instant the editor
// opens (which drops the kiosk class). Wired here in core so it works whether or
// not the async editor chunk has finished loading.
if (EDITOR_ENABLED) {
  document.documentElement.classList.add('editor-available');
  const helpBtn = document.getElementById('editorHelpBtn');
  const helpPanel = document.getElementById('editorHelpPanel');
  const setHelpOpen = (open) => {
    if (!helpPanel || !helpBtn) return;
    helpPanel.hidden = !open;
    helpBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  };
  helpBtn?.addEventListener('click', () => setHelpOpen(helpPanel.hidden));
  document.getElementById('editorHelpClose')?.addEventListener('click', () => setHelpOpen(false));
  document.getElementById('editorHelpOpen')?.addEventListener('click', () => {
    setHelpOpen(false);
    // Reveal the dev UI even if the editor chunk is still loading, then route
    // through the same path as pressing \ so the unified editor shell opens.
    document.documentElement.classList.remove('kiosk');
    try { dispatchEvent(new KeyboardEvent('keydown', { key: '\\' })); } catch { /* older engines */ }
  });
  // "Player view" link → ?prod, preserving any other launch flags (?spooky=1…).
  const prodLink = document.getElementById('editorHelpProd');
  if (prodLink) {
    const p = new URLSearchParams(location.search);
    p.set('prod', '1'); p.delete('editor');
    prodLink.setAttribute('href', location.pathname + '?' + p.toString());
  }
}

// === CONTENT PROFILE ===
// Cozy is the shipped default (design plan: K–2 kids in classrooms get no
// jumpscares, no damage, no death). The spooky tier — wolf packs, SKULLFACE
// night ambushes, Shell Shocker eggs that disintegrate Chocky — is opt-in via
// ?spooky=1 until per-account profiles exist (teacher/parent toggle).
const SPOOKY = new URLSearchParams(location.search).get('spooky') === '1';
console.log(`[profile] ${SPOOKY ? 'spooky' : 'cozy'}`);
// Wolves and Shell Shockers are now player-toggleable from the ESC (options)
// menu (see setWolvesEnabled / setShellShockersEnabled). Each DEFAULTS to the
// cozy/spooky profile — on only with ?spooky=1, off in cozy — but an explicit
// menu choice persists across reloads and the game.html minigame round-trip via
// localStorage. SKULLFACE is NOT toggleable here — the night jumpscare stays
// opt-in via ?spooky=1. These are `let`, flipped live; every reader is an update
// early-return (updateWolves / updateShellShockers), so toggling takes effect
// next frame. Persisted prefs survive a reload; '1'/'0' string, missing = use
// the profile default.
function loadEnemyPref(key, dflt) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? dflt : v === '1';
  } catch { return dflt; }
}
function saveEnemyPref(key, val) {
  try { localStorage.setItem(key, val ? '1' : '0'); } catch { /* private mode / quota */ }
}
let WOLVES_ENABLED = loadEnemyPref('chocky.wolves', SPOOKY);
let SHELL_SHOCKERS_ENABLED = loadEnemyPref('chocky.shellShockers', SPOOKY);

// Keep CSS surfaces aligned with the Three.js palette. index.html still carries
// identical fallbacks for first paint/no-JS, but this makes src/core/palette.js
// the source of truth after boot without adding any per-frame cost.
const CSS_PALETTE_VARS = Object.freeze({
  '--bg': PALETTE.skyCream,
  '--ink': PALETTE.ink,
  '--muted': PALETTE.muted,
  '--accent': PALETTE.coral,
  '--accent-soft': PALETTE.coralSoft,
  '--panel': PALETTE.panel,
  '--gold-soft': PALETTE.goldSoft,
  '--sky-soft': PALETTE.skyBlueSoft,
  '--sage-soft': PALETTE.sageSoft,
  '--lavender-soft': PALETTE.lavenderSoft,
  '--btn': PALETTE.panel,
  '--btn-hover': PALETTE.goldSoft,
  '--border-soft': PALETTE.border,
});
for (const [name, value] of Object.entries(CSS_PALETTE_VARS)) {
  document.documentElement.style.setProperty(name, css(value));
}


// ---- Procedural ground textures -------------------------------------------
// Two canvas textures give the world a real sense of place: lush meadow grass
// out on the plain, and worn packed earth where the village has trampled it
// flat. Both are generated once at load (no image files to ship) and tiled.

// Highest anisotropic filtering the GPU offers (capped at 16), used on the big
// ground textures the player sees at a grazing angle. Anisotropy keeps the
// grass and packed-earth crisp far into the distance instead of smearing into
// mush a few metres out — it's a texture-fetch quality knob, not extra draw
// calls, and it's already zero-cost on the low tier (kept at 1).
const MAX_ANISO = Math.min(TUNING.maxAniso ?? (LOW ? 1 : 16), renderer.capabilities.getMaxAnisotropy() || 8);

// Seamless meadow grass: layered green noise + scattered blades and clover.
// Everything drawn here MUST wrap toroidally — this tile repeats 20× across
// the field, and any feature that clips at the canvas edge (or any per-tile
// gradient) becomes a grid line the eye locks onto immediately.
function makeGrassTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = LOW ? 128 : 512;
  const ctx = c.getContext('2d');
  const W = c.width, H = c.height;
  // Flat base wash. (This was a vertical gradient once — which put a light-to-
  // dark ramp in every tile and drew a banding line across the meadow every
  // 6 units. Large-scale tonal variation belongs to the macro texture and the
  // terrain vertex tint, which don't repeat per-tile.)
  ctx.fillStyle = css(PALETTE.grassBase);
  ctx.fillRect(0, 0, W, H);
  // Draw a stamp at (x, y) and at whichever toroidal neighbours overlap the
  // canvas, so features crossing an edge continue on the opposite side.
  const wrapped = (x, y, r, draw) => {
    for (let ox = -1; ox <= 1; ox++) {
      for (let oy = -1; oy <= 1; oy++) {
        const px = x + ox * W, py = y + oy * H;
        if (px + r < 0 || px - r > W || py + r < 0 || py - r > H) continue;
        draw(px, py);
      }
    }
  };
  // Soft mottled patches (lighter sun-bleached + darker damp shade).
  const blob = (n, colors, rMin, rMax, alpha) => {
    for (let i = 0; i < n; i++) {
      const bx = Math.random() * W, by = Math.random() * H;
      const r = rMin + Math.random() * (rMax - rMin);
      const col = colors[(Math.random() * colors.length) | 0];
      wrapped(bx, by, r, (x, y) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${col},${alpha})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      });
    }
  };
  blob(LOW ? 14 : 46, [rgb(PALETTE.grassHighlightA), rgb(PALETTE.grassHighlightB)], W * 0.06, W * 0.18, 0.5);  // highlights
  blob(LOW ? 12 : 40, [rgb(PALETTE.grassShadeA), rgb(PALETTE.grassShadeB)], W * 0.05, W * 0.16, 0.45); // shade
  blob(LOW ? 5 : 18, [rgb(PALETTE.grassDry)], W * 0.03, W * 0.08, 0.4);  // dry/yellow
  // Fine blade flecks for close-up grit (kept light on the low tier).
  const blades = LOW ? 220 : 2600;
  const bladeDark = [(PALETTE.grassBladeDark >> 16) & 255, (PALETTE.grassBladeDark >> 8) & 255, PALETTE.grassBladeDark & 255];
  const bladeLight = [(PALETTE.grassBladeLight >> 16) & 255, (PALETTE.grassBladeLight >> 8) & 255, PALETTE.grassBladeLight & 255];
  ctx.lineWidth = 1;
  for (let i = 0; i < blades; i++) {
    const bx = Math.random() * W, by = Math.random() * H;
    const len = 2 + Math.random() * 5;
    const lean = (Math.random() - 0.5) * 2.2;
    const shade = Math.random();
    const sr = (bladeDark[0] + (bladeLight[0] - bladeDark[0]) * shade) | 0;
    const sg = (bladeDark[1] + (bladeLight[1] - bladeDark[1]) * shade) | 0;
    const sb = (bladeDark[2] + (bladeLight[2] - bladeDark[2]) * shade) | 0;
    ctx.strokeStyle = `rgba(${sr},${sg},${sb},0.5)`;
    wrapped(bx, by, len + 3, (x, y) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + lean, y - len);
      ctx.stroke();
    });
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = MAX_ANISO;
  return tex;
}

// Macro variation: a big, soft, low-frequency green mottle that spans the whole
// field only a couple of times. Multiplied over the tiled grass in the shader,
// it modulates large regions lighter/darker (and warmer/cooler) so the eye
// stops reading the repeating tile grid — the classic fix for obvious tiling.
// Kept near mid-grey so the multiply both lifts and drops brightness.
function makeGrassMacroTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = LOW ? 128 : 256;
  const ctx = c.getContext('2d');
  const W = c.width, H = c.height;
  ctx.fillStyle = '#808080';            // neutral: ×2 in shader ≈ no change
  ctx.fillRect(0, 0, W, H);
  // Wrap-aware blobs: draw each one plus its 8 toroidal neighbours so the
  // texture tiles seamlessly even at this large scale.
  const blob = (n, col, rMin, rMax, alpha) => {
    for (let i = 0; i < n; i++) {
      const bx = Math.random() * W, by = Math.random() * H;
      const r = rMin + Math.random() * (rMax - rMin);
      for (let ox = -1; ox <= 1; ox++) for (let oy = -1; oy <= 1; oy++) {
        const x = bx + ox * W, y = by + oy * H;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${col},${alpha})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
    }
  };
  blob(LOW ? 3 : 7, rgb(PALETTE.grassMacroWarm), W * 0.18, W * 0.34, 0.5);   // warm sun-bleached swells
  blob(LOW ? 3 : 7, rgb(PALETTE.grassMacroCool), W * 0.16, W * 0.32, 0.5);    // cool damp hollows
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  // Linear (NOT sRGB): we sample it by hand in the shader as a multiplier, so
  // mid-grey (0.5) must stay 0.5 — ×2 then lands on a neutral 1.0.
  tex.colorSpace = THREE.NoColorSpace;
  return tex;
}

// Packed village earth: warm tan dirt with scuffs, pebbles and trodden patches.
// The texture fades to transparent at the rim (baked radial alpha) so the disc
// blends seamlessly into the surrounding grass instead of leaving a hard edge.
function makeTownGroundTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = LOW ? 128 : 512;
  const ctx = c.getContext('2d');
  const W = c.width, H = c.height, cx = W / 2, cy = H / 2;
  // Dirt body.
  const base = ctx.createRadialGradient(cx, cy, 0, cx, cy, W * 0.5);
  base.addColorStop(0, css(PALETTE.townDirtMid));
  base.addColorStop(0.6, css(PALETTE.pathWarm));
  base.addColorStop(0.85, css(PALETTE.townDirtRim));
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, W, H);
  // Mottled scuffs and damp spots.
  for (let i = 0; i < (LOW ? 22 : 70); i++) {
    const x = Math.random() * W, y = Math.random() * H;
    const r = 6 + Math.random() * 34;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    const dark = Math.random() < 0.5;
    g.addColorStop(0, dark ? `rgba(${rgb(PALETTE.townDirtDark)},0.45)` : `rgba(${rgb(PALETTE.townDirtLight)},0.4)`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // Pebbles + grit.
  for (let i = 0; i < (LOW ? 36 : 320); i++) {
    const x = Math.random() * W, y = Math.random() * H;
    const r = 0.6 + Math.random() * 2.2;
    const mix = Math.random();
    const dr = (PALETTE.pebbleDark >> 16) & 255, dg = (PALETTE.pebbleDark >> 8) & 255, db = PALETTE.pebbleDark & 255;
    const lr = (PALETTE.pebbleLight >> 16) & 255, lg = (PALETTE.pebbleLight >> 8) & 255, lb = PALETTE.pebbleLight & 255;
    ctx.fillStyle = `rgba(${(dr + (lr - dr) * mix) | 0},${(dg + (lg - dg) * mix) | 0},${(db + (lb - db) * mix) | 0},0.7)`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Baked radial alpha — fully opaque core, feathering out near the edge so the
  // worn clearing dissolves into the meadow.
  const fade = ctx.createRadialGradient(cx, cy, W * 0.30, cx, cy, W * 0.5);
  fade.addColorStop(0, 'rgba(0,0,0,1)');
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, H);
  ctx.globalCompositeOperation = 'source-over';
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = MAX_ANISO;
  return tex;
}

// Larger play-mode floor (hidden until Play mode). Now 120×120 so distant
// scenery in the fog has ground under it — now carpeted in tiling grass.
const grassTex = makeGrassTexture();
grassTex.repeat.set(20, 20);   // ~6 units per tile across the 120-unit plane
const grassMacroTex = makeGrassMacroTexture();
const grassMat = new THREE.MeshLambertMaterial({
  color: 0xffffff, map: grassTex,
  vertexColors: true,   // terrain bakes a height-aware tint per vertex
});
// Anti-tiling, three layers deep (each kills a different scale of repeat):
//   1. The detail tile itself is toroidal (no seams, no per-tile gradient).
//   2. De-tiled sampling below: the SAME tile fetched twice — the second
//      rotated ~31° and rescaled — blended by a mid-frequency mask, so no
//      feature ever recurs on the 6-unit grid. The mask reuses the macro
//      texture (no new asset).
//   3. The macro multiply: low-frequency light/dark swells spanning the field
//      ~2.5×, plus the per-vertex terrain tint (see heightfield.js).
// Cost: 4 small-texture fetches on the grass (was 2). Lambert + one ground
// plane keeps that affordable even on UHD 600.
grassMat.onBeforeCompile = (shader) => {
  shader.uniforms.macroMap = { value: grassMacroTex };
  // Low tier keeps the original 2-fetch budget (UHD 600 is fill-rate bound and
  // the grass covers half the screen): seamless tile + macro + vertex tint
  // already kill the worst tiling there. Mid+ adds the dual-sample de-tile.
  const sampling = LOW
    ? `{
      diffuseColor *= texture2D( map, vMapUv );
      vec3 grassMacro = texture2D( macroMap, vMapUv * 0.125 ).rgb;
      diffuseColor.rgb *= grassMacro * 2.0;
      }`
    : `{
      vec4 grassA = texture2D( map, vMapUv );
      vec4 grassB = texture2D( map, mat2( 0.857, -0.515, 0.515, 0.857 ) * vMapUv * 1.27 + vec2( 0.37, 0.59 ) );
      float deTile = smoothstep( 0.34, 0.66, texture2D( macroMap, vMapUv * 0.31 + vec2( 0.5, 0.13 ) ).g );
      diffuseColor *= mix( grassA, grassB, deTile );
      vec3 grassMacro = texture2D( macroMap, vMapUv * 0.125 ).rgb;
      diffuseColor.rgb *= grassMacro * 2.0;
      }`;
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <map_pars_fragment>',
      '#include <map_pars_fragment>\nuniform sampler2D macroMap;')
    .replace('#include <map_fragment>', sampling);
};
// The play-mode ground is the terrain heightfield: a displaced, segmented
// plane (gentle hills outside the village plateau — see world/heightfield.js).
// The shadow overlay shares the SAME displaced geometry so shadows lie on the
// slopes instead of a phantom flat plane.
const terrainGeo = displaceGeometryToTerrain(
  new THREE.PlaneGeometry(120, 120, LOW ? 64 : 112, LOW ? 64 : 112)
);
const playFloor = new THREE.Mesh(
  terrainGeo,
  grassMat
);
playFloor.rotation.x = -Math.PI / 2;
playFloor.receiveShadow = true;
playFloor.position.y = -0.001;
playFloor.visible = false;
scene.add(playFloor);

// Worn-earth clearing under the village, blending into the grass at its rim.
const townGround = new THREE.Mesh(
  new THREE.CircleGeometry(15, 48),
  new THREE.MeshLambertMaterial({
    color: 0xffffff, map: cachedTexture('townGround', makeTownGroundTexture),
    transparent: true, depthWrite: false,
  })
);
townGround.rotation.x = -Math.PI / 2;
townGround.position.y = 0.0005;     // hair above the grass to avoid z-fighting
townGround.receiveShadow = true;
townGround.visible = false;
scene.add(townGround);

// Shadow overlay (shares the terrain geometry — see above)
const playShadowOverlay = new THREE.Mesh(
  terrainGeo,
  new THREE.ShadowMaterial({ opacity: 0.45 })
);
playShadowOverlay.rotation.x = -Math.PI / 2;
playShadowOverlay.position.y = 0.001;
playShadowOverlay.receiveShadow = true;
playShadowOverlay.visible = false;
scene.add(playShadowOverlay);

// Subtle grid lines on the play floor for spatial reference
const grid = new THREE.GridHelper(120, 60, 0xcccccc, 0xdddddd);
grid.material.transparent = true;
grid.material.opacity = 0.1;
grid.visible = false;
scene.add(grid);

// ============= VILLAGE =============
// Houses, trees, well, paths — Chocky's little world to explore.
const propsGroup = new THREE.Group();
propsGroup.visible = false;
scene.add(propsGroup);

// Hand-placed blob shadows for hero props: depth cue without expanding the
// real shadow-map workload. They are batched into one InstancedMesh after the
// village/zones build, so dozens of soft grounding marks cost one draw call.
const contactShadowSpecs = [];
function addContactShadow(x, z, sx = 1.2, sz = 0.8, yaw = 0) {
  contactShadowSpecs.push({ x, z, sx, sz, yaw });
}
// A soft radial falloff baked once: opaque core fading to nothing at the rim,
// so a blob shadow feathers into the ground like real ambient contact occlusion
// instead of stamping a hard-edged disc. One tiny cached texture shared by every
// blob — still a single InstancedMesh draw call for the whole set.
function makeContactShadowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0,    'rgba(255,255,255,1)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.82)');
  g.addColorStop(1,    'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  return tex;
}
function flushContactShadows() {
  if (!contactShadowSpecs.length) return;
  // A flat quad carrying the soft radial-alpha map (was a hard-edged circle).
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.rotateX(-Math.PI / 2);   // lie flat; instances add only a Y spin
  const mat = new THREE.MeshBasicMaterial({
    color: PALETTE.contactShadow, map: cachedTexture('contactShadow', makeContactShadowTexture),
    transparent: true, opacity: LOW ? 0.2 : 0.26,
    depthWrite: false, fog: true,
  });
  const mesh = new THREE.InstancedMesh(geo, mat, contactShadowSpecs.length);
  const dummy = new THREE.Object3D();
  for (let i = 0; i < contactShadowSpecs.length; i++) {
    const s = contactShadowSpecs[i];
    dummy.position.set(s.x, heightAt(s.x, s.z) + 0.012, s.z);
    dummy.rotation.set(0, s.yaw, 0);           // geo is pre-laid flat; only spin
    // Unit plane shares the old r=0.5 footprint; ×1.4 lets the soft feathered rim
    // extend past where the old hard disc ended so the solid core stays similar.
    dummy.scale.set(s.sx * 1.4, 1, s.sz * 1.4);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  }
  mesh.renderOrder = -1;
  propsGroup.add(mesh);
}

// Painted sky wash: one inside-out sphere with a tiny vertical gradient texture.
// The scene.background remains the authoritative solid color for fog/lights; this
// dome multiplies that color into a lighter zenith and warmer horizon in play mode.
function makeSkyWashTexture() {
  const c = document.createElement('canvas');
  // Taller ramp + an extra mid stop gives the dome a softer zenith→horizon
  // falloff (a cool-white high sky easing through a pale band into a warmer
  // horizon), so the sky reads less like two flat colours meeting in a line.
  c.width = 4; c.height = 256;
  const ctx = c.getContext('2d');
  const H = c.height, W = c.width;
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0,    '#ffffff');   // zenith
  g.addColorStop(0.40, '#f9fcff');   // cool high sky
  g.addColorStop(0.64, '#f6f1e7');   // pale mid
  g.addColorStop(0.85, '#f0e2cc');   // warming
  g.addColorStop(1,    '#ead6b8');   // warm horizon
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Ordered dither: a ±1.5/255 per-pixel wobble breaks the smooth ramp into
  // imperceptible noise so 8-bit output never shows the tell-tale horizontal
  // banding across a large gradient. Baked once — zero runtime cost.
  const img = ctx.getImageData(0, 0, W, H);
  const px = img.data;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      const d = (((x + y) & 1) ? 1.5 : -1.5);
      px[i]     = Math.max(0, Math.min(255, px[i] + d));
      px[i + 1] = Math.max(0, Math.min(255, px[i + 1] + d));
      px[i + 2] = Math.max(0, Math.min(255, px[i + 2] + d));
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const skyWashMat = new THREE.MeshBasicMaterial({
  map: cachedTexture('skyWash', makeSkyWashTexture),
  color: PALETTE.skyCream,
  side: THREE.BackSide,
  fog: false,
  depthWrite: false,
});
const skyWash = new THREE.Mesh(new THREE.SphereGeometry(78, LOW ? 16 : 24, LOW ? 8 : 12), skyWashMat);
skyWash.visible = false;
scene.add(skyWash);

function makeLandmarkTexture(icon, color = '#f7efd7') {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.fillRect(0, 0, 256, 256);
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(58,40,24,0.38)';
  ctx.lineWidth = 10;
  ctx.font = '900 150px "Apple Color Emoji","Segoe UI Emoji",ui-sans-serif,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.strokeText(icon, 128, 138);
  ctx.fillText(icon, 128, 138);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const heroLandmarks = [];   // distant fog-silhouette beacons, faded by distance
const HERO_FADE_NEAR = 12;  // fully invisible within this radius of the player
const HERO_FADE_FAR = 26;   // full beacon opacity beyond it
function addHeroLandmark(x, z, icon, color, scale = 5.4) {
  const base = 0.82;        // crisper through the fog, so each zone is sightable from the hub
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: cachedTexture(`landmark-${icon}-${color}`, () => makeLandmarkTexture(icon, color)),
    transparent: true, opacity: base, depthWrite: false, fog: true,
  }));
  // Float them higher so they clear the treeline/fog and read as beacons you can
  // steer toward from across the map, not props sitting in the grass.
  const baseY = heightAt(x, z) + scale * 0.7;
  sprite.position.set(x, baseY, z);
  sprite.scale.set(scale, scale, 1);
  markDynamic(sprite);   // updateHeroLandmarks bobs/breathes it
  propsGroup.add(sprite);
  // No contact shadow: these are meant to read as far-off markers in the fog,
  // and the fade below takes them to nothing up close — a lone ground shadow
  // under an invisible sprite would just look like a stain.
  heroLandmarks.push({ sprite, base, x, z, baseY, scale, phase: heroLandmarks.length * 1.7 });
  return sprite;
}
// Keep the beacons reading as distant wayfinding, never as giant translucent
// icons you walk under: fade each to zero as the player nears it, but give the
// far-off ones a gentle bob + breathe so they catch the eye and invite the walk.
// A handful of landmarks, so this is cheap; runs in both play and editor modes.
let _heroT = 0;
function updateHeroLandmarks(dt = 0) {
  _heroT += dt;
  const span = HERO_FADE_FAR - HERO_FADE_NEAR;
  for (const h of heroLandmarks) {
    const d = Math.hypot(player.pos.x - h.x, player.pos.z - h.z);
    const t = Math.min(1, Math.max(0, (d - HERO_FADE_NEAR) / span));
    const o = h.base * t;
    h.sprite.material.opacity = o;
    h.sprite.visible = o > 0.01;
    if (o > 0.01) {
      const b = Math.sin(_heroT * 0.8 + h.phase);
      h.sprite.position.y = h.baseY + b * 0.22 * t;                 // gentle bob
      const s = h.scale * (1 + Math.sin(_heroT * 0.6 + h.phase) * 0.03 * t);
      h.sprite.scale.set(s, s, 1);                                  // slow breathe
    }
  }
}
// Collidable props register into the spatial hash (world/collision.js);
// queries replaced the old global obstacles[] linear scans.
const swayTargets = [];    // tree foliage that gets gentle wind
const smokeEmitters = [];  // chimneys puffing smoke

// ---- Bumpable props: when Chocky walks up against one, fire onEnter; when he
// wanders off, fire onLeave. Used by the welcome sign, lampposts and the
// wishing well. Hysteresis (the +0.4 release ring) stops edge-flicker.
const bumpZones = [];      // {x, z, r, inside, sourceId, onEnter, onLeave}
// Checkpoint helpers use these village affordances to decide when Chocky is
// somewhere safe enough to return to after a manual escape/regeneration.
const villagePathSegments = [];  // {x1,z1,x2,z2} collected by buildPath()
const villageLandmarks = [];     // {x,z,r} safe areas around huts, well, signs, etc.
function addVillageLandmark(x, z, r) {
  villageLandmarks.push({ x, z, r });
}
function addBumpZone(x, z, r, sourceId, onEnter, onLeave) {
  bumpZones.push({ x, z, r, inside: false, sourceId: sourceId || null, onEnter: onEnter || null, onLeave: onLeave || null });
}
function updateBumpZones() {
  for (const b of bumpZones) {
    const d = Math.hypot(player.pos.x - b.x, player.pos.z - b.z);
    if (!b.inside && d < b.r) {
      b.inside = true;
      if (b.onEnter) b.onEnter(b.sourceId);
    } else if (b.inside && d > b.r + 0.4) {
      b.inside = false;
      if (b.onLeave) b.onLeave(b.sourceId);
    }
  }
}

// The little message bubble shared by bump interactions. A duration auto-hides
// it (timed message, e.g. the welcome sign); duration 0 keeps it up until
// something hides it (a sticky prompt, e.g. the wishing well while you stand by).
let _bumpMsgTimer = 0;
let _bumpMsgSourceId = null;
const domById = (id) => document.getElementById(id);
const setDomText = (id, text) => { const el = domById(id); if (el) el.textContent = text; return el; };
const setDomHtml = (id, html) => { const el = domById(id); if (el) el.innerHTML = html; return el; };
const setDomDisplay = (id, display) => { const el = domById(id); if (el) el.style.display = display; return el; };
function showBumpMsg(text, hint, duration, sourceId) {
  const msg = domById('bumpMsg');
  const textEl = setDomText('bumpMsgText', text);
  const h = domById('bumpMsgHint');
  if (!msg || !textEl || !h) return;
  if (hint) { h.innerHTML = hint; h.style.display = 'block'; }
  else h.style.display = 'none';
  msg.style.display = 'block';
  _bumpMsgTimer = duration || 0;
  _bumpMsgSourceId = sourceId || null;
}
function hideBumpMsg(sourceId) {
  if (sourceId && _bumpMsgSourceId && sourceId !== _bumpMsgSourceId) return;
  setDomDisplay('bumpMsg', 'none');
  _bumpMsgTimer = 0;
  _bumpMsgSourceId = null;
}
function updateBumpMsg(dt) {
  if (_bumpMsgTimer > 0) {
    _bumpMsgTimer -= dt;
    if (_bumpMsgTimer <= 0) hideBumpMsg(_bumpMsgSourceId);
  }
}

// Low-level builder: a single box. Collidable boxes go in obstacles[].
function addBox(x, y, z, w, h, d, color, collidable = true) {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  propsGroup.add(m);
  if (collidable) {
    addObstacle({
      minX: x - w/2, maxX: x + w/2,
      minY: y - h/2, maxY: y + h/2,
      minZ: z - d/2, maxZ: z + d/2,
    });
  }
  return m;
}
function addSphere(x, y, z, r, color, collidable = false) {
  const m = new THREE.Mesh(
    new THREE.SphereGeometry(r, LOW ? 8 : 12, LOW ? 6 : 8),
    new THREE.MeshLambertMaterial({ color })
  );
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  propsGroup.add(m);
  if (collidable) {
    addObstacle({
      minX: x - r, maxX: x + r,
      minY: y - r, maxY: y + r,
      minZ: z - r, maxZ: z + r,
    });
  }
  return m;
}
function addCyl(x, y, z, r, h, color, collidable = true) {
  const m = new THREE.Mesh(
    new THREE.CylinderGeometry(r, r, h, LOW ? 8 : 12),
    new THREE.MeshLambertMaterial({ color })
  );
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  propsGroup.add(m);
  if (collidable) {
    addObstacle({
      minX: x - r, maxX: x + r,
      minY: y - h/2, maxY: y + h/2,
      minZ: z - r, maxZ: z + r,
    });
  }
  return m;
}

const windowGlowMats = [];
const _windowDay = new THREE.Color(PALETTE.windowDay);
const _windowNight = new THREE.Color(PALETTE.windowNight);
const _windowGlow = new THREE.Color();
const _windowFlicker = new THREE.Color();
// A baked "glass sheen" the window panes multiply their tint through, so the
// flat blue rectangles read as glass catching the sky: a soft top-to-bottom
// falloff plus a diagonal reflection streak across the upper corner. Kept near-
// white (a subtle luminance map, not a colour) so the day tint (sky blue) and
// the night glow lerp still come through cleanly — the sheen only shapes the
// brightness. One shared 64² canvas, baked once; no runtime cost, every tier.
function makeWindowGlassTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0,   '#ffffff');   // top pane catches more sky
  g.addColorStop(0.6, '#f0f2f5');
  g.addColorStop(1,   '#dadde2');   // sill in soft shade
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  // Diagonal reflection streak across the upper-left corner.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.translate(20, 22);
  ctx.rotate(-0.7);
  const s = ctx.createLinearGradient(0, -30, 0, 30);
  s.addColorStop(0,   'rgba(255,255,255,0)');
  s.addColorStop(0.5, 'rgba(255,255,255,0.5)');
  s.addColorStop(1,   'rgba(255,255,255,0)');
  ctx.fillStyle = s;
  ctx.fillRect(-6, -34, 10, 68);
  ctx.restore();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
function makeWindowMaterial() {
  const mat = new THREE.MeshBasicMaterial({
    color: PALETTE.windowDay, fog: true,
    map: cachedTexture('windowGlass', makeWindowGlassTexture),
  });
  windowGlowMats.push(mat);
  return mat;
}

function mixHexColors(a, b, t) {
  return new THREE.Color(a).lerp(new THREE.Color(b), t).getHex();
}
function shadeHexColor(hex, scalar) {
  return new THREE.Color(hex).multiplyScalar(scalar).getHex();
}

// ===== Movable structures (terrain editor) ================================
// Houses, the well and the store are normally a loose pile of boxes added
// straight to propsGroup. captureStructure() wraps a builder so everything it
// adds becomes ONE named THREE.Group pivoted at (x, 0, z) on the ground: the
// structure editor (# key — see src/editor/editor.js) can then translate,
// rotate or scale it as a unit and print the transforms to paste back into the
// layout. The meshes keep their original world position, so nothing moves
// until an author drags the gizmo.
// Editor builds capture the structure's obstacle AABBs as movable records so
// worldEdit can keep collision aligned while dragging; kid builds register the
// same boxes as static obstacles with no editor-only bookkeeping.
const editableStructures = [];   // [{ group, name }] — used by the structure editor
function captureStructure(name, x, z, buildFn) {
  const start = propsGroup.children.length;
  // Capture the collision boxes this builder registers as MOVABLE obstacles so
  // the structure editor can drag the building and its collision follows. Only
  // in editor-enabled builds — the kid build keeps plain static obstacles.
  if (EDITOR_ENABLED) beginObstacleCapture();
  buildFn();
  const captured = EDITOR_ENABLED ? endObstacleCapture() : [];
  const group = new THREE.Group();
  group.position.set(x, 0, z);
  for (const m of propsGroup.children.slice(start)) {
    m.position.x -= x;
    m.position.z -= z;
    group.add(m);                 // reparents the mesh out of propsGroup
  }
  // Stash each captured obstacle's group-local AABB; worldEdit recomputes the
  // world AABB from the group transform on every move.
  for (const ob of captured) {
    ob._local = { minX: ob.minX - x, maxX: ob.maxX - x, minY: ob.minY, maxY: ob.maxY, minZ: ob.minZ - z, maxZ: ob.maxZ - z };
  }
  group.userData.obstacles = captured;
  // Kid build: a structure is a dozen separate Lambert boxes = a dozen draw
  // calls, and the village has ~10 of them — the single biggest draw-call sink
  // on Chromebooks. Bake them into ONE vertex-coloured mesh. Skipped in editor
  // builds so the structure editor keeps loose, individually-movable meshes.
  if (!EDITOR_ENABLED) mergeStructureGroup(group);
  propsGroup.add(group);
  registerStructureFade(group);
  // Kid build: structures never move (the fade animates material opacity, not
  // transforms, and there's no gizmo). Snapshot the matrices once and opt the
  // subtree out of three.js' per-frame matrix walk — same win as the frozen
  // chunk nodes (world/chunks.js freezeStaticObject). Editor builds keep it live
  // so the structure gizmo can drag the group.
  if (!EDITOR_ENABLED) { group.updateMatrixWorld(true); group.matrixWorldAutoUpdate = false; }
  editableStructures.push({ group, name });
  return group;
}

// Diorama occlusion: register a structure (a hut, the well, the store) so it
// ghosts to translucent when it blocks the camera's view of Chocky — the
// counterpart to the foliage fade in chunks.js, for the meshes main.js owns.
// Works whether the group was merged into one vertex-coloured mesh (kid build)
// or left as loose Lambert boxes (no-build / editor build): we collect every
// opaque Lambert material in the group and fade them together, and bound the
// whole structure with one world-space AABB for a precise segment test. Bounding
// maths run once here (build time), so the per-frame updateStructureFade stays
// allocation-free. transparent is flipped on now — a no-op for the renderer
// until opacity drops, and it never triggers a shader recompile.
const fadeStructures = [];   // [{ mats:[], minX, maxX, minY, maxY, minZ, maxZ }]
const _structBox = new THREE.Box3(), _structBoxTmp = new THREE.Box3();
function registerStructureFade(group) {
  if (typeof __EDITOR_BUILD__ !== 'undefined' && __EDITOR_BUILD__) return; // never in the editor build
  const mats = [];
  group.updateMatrixWorld(true);
  let has = false;
  _structBox.makeEmpty();
  group.traverse((o) => {
    // Textured Lamberts are included: kit buildings (world/kit.js) are single
    // atlas-textured meshes, and each carries its own material CLONE precisely
    // so this fade can animate opacity per-structure without ghosting every
    // building that shares the atlas.
    if (o.isMesh && o.material && o.material.isMeshLambertMaterial && o.geometry) {
      if (!mats.includes(o.material)) mats.push(o.material);
      o.geometry.computeBoundingBox();
      _structBoxTmp.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
      _structBox.union(_structBoxTmp);
      has = true;
    }
  });
  if (!has || !mats.length) return;
  for (const m of mats) m.transparent = true;
  fadeStructures.push({
    mats,
    minX: _structBox.min.x, maxX: _structBox.max.x,
    minY: _structBox.min.y, maxY: _structBox.max.y,
    minZ: _structBox.min.z, maxZ: _structBox.max.z,
  });
}

const STRUCT_FADE_MARGIN = 0.04;
function segmentIntersectsStructureBox(s, ax, ay, az, abx, aby, abz) {
  let t0 = 0.04, t1 = 0.96;
  const minX = s.minX - STRUCT_FADE_MARGIN, maxX = s.maxX + STRUCT_FADE_MARGIN;
  const minY = s.minY - STRUCT_FADE_MARGIN, maxY = s.maxY + STRUCT_FADE_MARGIN;
  const minZ = s.minZ - STRUCT_FADE_MARGIN, maxZ = s.maxZ + STRUCT_FADE_MARGIN;
  if (Math.abs(abx) < 1e-8) { if (ax < minX || ax > maxX) return false; }
  else { const inv = 1 / abx; let tn = (minX - ax) * inv, tf = (maxX - ax) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) return false; }
  if (Math.abs(aby) < 1e-8) { if (ay < minY || ay > maxY) return false; }
  else { const inv = 1 / aby; let tn = (minY - ay) * inv, tf = (maxY - ay) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) return false; }
  if (Math.abs(abz) < 1e-8) { if (az < minZ || az > maxZ) return false; }
  else { const inv = 1 / abz; let tn = (minZ - az) * inv, tf = (maxZ - az) * inv; if (tn > tf) { const s = tn; tn = tf; tf = s; } if (tn > t0) t0 = tn; if (tf < t1) t1 = tf; if (t0 > t1) return false; }
  return true;
}

function updateStructureFade(camPos, lookX, lookY, lookZ, dt) {
  if (OCCLUSION_FADE_DISABLED || !fadeStructures.length) return;
  const ax = camPos.x, ay = camPos.y, az = camPos.z;
  const abx = lookX - ax, aby = lookY - ay, abz = lookZ - az;
  for (const s of fadeStructures) {
    // Test the eye->Chocky segment against the structure's actual world-space
    // bounds instead of a broad bounding sphere. That keeps nearby huts opaque
    // unless their visible volume really crosses the sightline to Chocky's head.
    const target = segmentIntersectsStructureBox(s, ax, ay, az, abx, aby, abz) ? FADE_MIN : 1;
    const m0 = s.mats[0];
    if (m0.opacity === target) continue;   // already settled → skip the whole structure
    let next = easeFade(m0.opacity, target, dt);
    if (Math.abs(next - target) < 0.004) next = target;
    for (const m of s.mats) m.opacity = next;
  }
}

// Collapse a captured structure's plain opaque Lambert meshes into a single
// merged, vertex-coloured mesh (one draw call). Anything textured or otherwise
// special (sprites, lights, sub-groups) is left as its own child. Collision is
// already registered in world space, so this only touches what gets drawn.
const _structColor = new THREE.Color();
function mergeStructureGroup(group) {
  const mergeable = [];
  const rest = [];
  for (const child of group.children) {
    if (child.isMesh && child.material && child.material.isMeshLambertMaterial &&
        !child.material.map && child.geometry) {
      mergeable.push(child);
    } else {
      rest.push(child);
    }
  }
  if (mergeable.length < 2) return;   // nothing to gain from merging one mesh

  const geos = [];
  let castShadow = false, receiveShadow = false;
  for (const child of mergeable) {
    const geo = child.geometry.clone();
    child.updateMatrix();
    geo.applyMatrix4(child.matrix);     // bake local transform into vertices + normals
    geo.deleteAttribute('uv');          // keep attribute sets identical across inputs
    _structColor.copy(child.material.color);
    const n = geo.attributes.position.count;
    const colors = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      colors[i * 3] = _structColor.r; colors[i * 3 + 1] = _structColor.g; colors[i * 3 + 2] = _structColor.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geos.push(geo);
    castShadow = castShadow || child.castShadow;
    receiveShadow = receiveShadow || child.receiveShadow;
  }

  const merged = mergeGeometries(geos, false);
  for (const g of geos) g.dispose();
  if (!merged) return;   // mismatched attributes — leave the loose meshes untouched

  // Merge succeeded: the originals are no longer drawn, so free their resources.
  for (const child of mergeable) {
    group.remove(child);
    child.geometry.dispose();
    child.material.dispose();
  }
  const mesh = new THREE.Mesh(merged, new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  group.add(mesh);
}

// Live, removable tree for the world editor: trunk + foliage props plus a
// MOVABLE collision box (so Delete can take it back out). Mirrors buildTree.
function placeTreeLive(x, z, scale = 1) {
  const r = 0.16 * scale;
  const gy = heightAt(x, z);
  const ob = addMovableObstacle({ minX: x - r, maxX: x + r, minY: gy, maxY: gy + 0.9 * scale, minZ: z - r, maxZ: z + r });
  const trunkId = addPropLive('trunk', { x, z, scale });
  const foliageId = addPropLive('foliage', { x, z, scale, leafScale: scale * 1.04, leafLift: 0.9, phase: Math.random() * Math.PI * 2, color: PALETTE.leafBase });
  return { ids: [trunkId, foliageId], ob };
}

// House: walls + roof + door + windows. Optional chimney with smoke.
// Returns the movable group (registered with the structure editor).
function buildHouse(x, z, w = 2.4, d = 2.4, wallH = 1.5, wallColor = PALETTE.cream, roofColor = PALETTE.roofTerracotta, withChimney = false, name = null, decor = null) {
  addContactShadow(x, z, w * 1.25, d * 1.15, 0);
  const group = captureStructure(name || `House @ ${x.toFixed(0)},${z.toFixed(0)}`, x, z, () => {
    const addWindow = (wx, wy, wz, ww, wh, wd) => {
      const win = new THREE.Mesh(new THREE.BoxGeometry(ww, wh, wd), makeWindowMaterial());
      win.position.set(wx, wy, wz);
      win.castShadow = false;
      win.receiveShadow = true;
      propsGroup.add(win);
      const onXSide = ww < wd;
      if (onXSide) {
        addBox(wx, wy, wz, ww * 1.4, wh * 0.12, wd * 1.08, PALETTE.cream, false);
        addBox(wx, wy, wz, ww * 1.4, wh * 1.05, wd * 0.12, PALETTE.cream, false);
        addBox(wx, wy - wh * 0.62, wz, ww * 1.7, wh * 0.12, wd * 1.36, PALETTE.woodLight, false);
      } else {
        addBox(wx, wy, wz, ww * 0.12, wh * 1.05, wd * 1.4, PALETTE.cream, false);
        addBox(wx, wy, wz, ww * 1.05, wh * 0.12, wd * 1.4, PALETTE.cream, false);
        addBox(wx, wy - wh * 0.62, wz, ww * 1.36, wh * 0.12, wd * 1.7, PALETTE.woodLight, false);
      }
    };
    // Walls
    addBox(x, wallH/2, z, w, wallH, d, wallColor);
    // Pitched roof — two stacked boxes for a stepped look
    addBox(x, wallH + 0.18, z, w + 0.3, 0.18, d + 0.3, roofColor);
    addBox(x, wallH + 0.42, z, w * 0.65, 0.18, d * 0.65, roofColor);
    const roofTrim = shadeHexColor(roofColor, 0.82);
    addBox(x, wallH + 0.55, z, w * 0.72, 0.055, 0.08, roofTrim, false);
    addBox(x, wallH + 0.2, z + d / 2 + 0.18, w + 0.44, 0.055, 0.08, roofTrim, false);
    addBox(x, wallH + 0.2, z - d / 2 - 0.18, w + 0.44, 0.055, 0.08, roofTrim, false);
    // Door (non-collidable, just decorative — slightly inset into the wall)
    addBox(x, 0.35, z + d/2 + 0.011, 0.45, 0.7, 0.02, PALETTE.doorDark, false);
    const doorPanel = mixHexColors(PALETTE.doorDark, PALETTE.woodLight, 0.32);
    addBox(x, 0.43, z + d/2 + 0.026, 0.29, 0.32, 0.018, doorPanel, false);
    addBox(x, 0.65, z + d/2 + 0.028, 0.22, 0.055, 0.02, PALETTE.woodLight, false);
    // Doorknob
    addSphere(x + 0.18, 0.35, z + d/2 + 0.045, 0.025, PALETTE.goldSoft, false);
    // Windows: separate materials so the day/night cycle can warm them at
    // dusk, with tiny cream mullions to read at kid-camera distance.
    addWindow(x - w/2 - 0.011, 0.95, z + 0.4, 0.02, 0.32, 0.32);
    addWindow(x - w/2 - 0.011, 0.95, z - 0.4, 0.02, 0.32, 0.32);
    addWindow(x + w/2 + 0.011, 0.95, z + 0.4, 0.02, 0.32, 0.32);
    // Chimney + smoke source
    if (withChimney) {
      const cx = x + w/2 - 0.4, cz = z;
      addBox(cx, wallH + 0.7, cz, 0.32, 0.55, 0.32, PALETTE.woodLight);
      addBox(cx, wallH + 1.0, cz, 0.42, 0.08, 0.42, shadeHexColor(roofColor, 0.65), false);
      smokeEmitters.push({ x: cx, y: wallH + 1.06, z: cz, t: Math.random() * 2 });
    }
    addHouseDecor(x, z, w, d, wallH, decor);
  });
  // Remember the spawn params so the editor can duplicate this structure and so
  // it round-trips through the edit layer (structuresAdded).
  group.userData.buildSpec = { type: 'house', x, z, w, d, wallH, wallColor, roofColor, withChimney, decor };
  return group;
}

// A building from the handmade kit (world/kit.js): one atlas-textured mesh
// standing in for the pile of boxes buildHouse assembles. Wrapped in the same
// captureStructure so collision capture, the structure editor, occlusion fade
// and the baked round-trip all behave identically to a procedural house.
// Returns null when the kit didn't load — callers fall back to their
// procedural builder, so a failed fetch degrades to today's village.
function buildKitStructure(kind, x, z, { name = null, yaw = 0, spec = null } = {}) {
  const geo = kitReady() ? kitGeometry(kind) : null;
  if (!geo) return null;
  const size = kitSize(kind);
  const conf = KIT_KINDS[kind] || {};
  addContactShadow(x, z, size.w * 0.72, size.d * 0.68, 0);
  addKitBaseAccents(kind, x, z, size, yaw);
  const gy = heightAt(x, z);
  const group = captureStructure(name || `${kind} @ ${x.toFixed(0)},${z.toFixed(0)}`, x, z, () => {
    // Clone: the fade animates THIS building's opacity (see registerStructureFade).
    const mesh = new THREE.Mesh(geo, kitMaterialClone());
    mesh.position.set(x, gy, z);
    if (yaw) mesh.rotation.y = yaw;
    mesh.castShadow = !ULTRA_LOW_SHADOWS;
    mesh.receiveShadow = true;
    propsGroup.add(mesh);
    // Footprint AABB, slightly inset so Chocky can brush past overhanging
    // eaves. Rotate the footprint with the model before taking the enclosing
    // AABB, so yawed kit landmarks don't leave phantom corners behind.
    const inset = conf.inset ?? 0.1;
    const hw = Math.max(0.05, size.w / 2 - inset);
    const hd = Math.max(0.05, size.d / 2 - inset);
    const cos = Math.cos(yaw), sin = Math.sin(yaw);
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [cx, cz] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]]) {
      const wx = x + cx * cos - cz * sin;
      const wz = z + cx * sin + cz * cos;
      minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
      minZ = Math.min(minZ, wz); maxZ = Math.max(maxZ, wz);
    }
    addObstacle({ minX, maxX, minY: gy, maxY: gy + size.h, minZ, maxZ });
    if (conf.smoke) smokeEmitters.push({ x: x + conf.smoke.x, y: gy + conf.smoke.y + 0.25, z: z + conf.smoke.z, t: Math.random() * 2 });
  });
  group.userData.buildSpec = spec || { type: kind, x, z };
  group.userData.kitSize = size;
  return group;
}

// Small, cheap dressing for handmade GLB structures. The models carry the main
// silhouette; these existing instanced props just seat them into the terrain so
// they feel planted rather than dropped on top of the heightfield.
function addKitBaseAccents(kind, x, z, size, yaw = 0) {
  const r = Math.max(size.w, size.d) * 0.5 + 0.18;
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  const place = (ox, oz, fn) => fn(x + ox * cos - oz * sin, z + ox * sin + oz * cos);
  const flower = (px, pz, color, h = 0.24, s = 0.68) => {
    addProp('flowerStem', { x: px, z: pz, h });
    addProp('flowerHead', { x: px, z: pz, h, s, color });
  };
  if (kind === 'wizard_tower') {
    for (let i = 0; i < 5; i++) place((i - 2) * 0.42, r + i * 0.06, (px, pz) => addProp('pathStone', { x: px, z: pz, r: 0.18 + i * 0.015 }));
    for (const s of [-1, 1]) place(s * (r + 0.18), 0.2, (px, pz) => flower(px, pz, PALETTE.lavender, 0.26, 0.72));
  } else if (kind === 'adventure101_castle') {
    for (let i = 0; i < 4; i++) place((i - 1.5) * 0.48, r + 0.18, (px, pz) => addProp('pathStone', { x: px, z: pz, r: 0.2 }));
    for (const s of [-1, 1]) place(s * (r + 0.25), r * 0.15, (px, pz) => addProp('tinyFlag', { x: px, z: pz, scale: 0.75, yaw: yaw + s * 0.45 }));
  } else if (kind === 'numberzoo_barn') {
    for (const [ox, oz] of [[-r, 0.2], [r, -0.1], [0.2, r]]) place(ox, oz, (px, pz) => addProp('grassTuft', { x: px, z: pz, scale: 0.7, yaw }));
  } else if (kind === 'chocky_closet') {
    for (const s of [-1, 1]) place(s * (r + 0.05), r * 0.45, (px, pz) => flower(px, pz, s < 0 ? PALETTE.coral : PALETTE.skyBlue, 0.24, 0.7));
  } else if (kind === 'well') {
    for (let i = 0; i < 4; i++) {
      const a = yaw + i * Math.PI * 0.5 + 0.25;
      flower(x + Math.cos(a) * r, z + Math.sin(a) * r, [PALETTE.gold, PALETTE.skyBlue, PALETTE.coral, PALETTE.lavender][i], 0.22, 0.62);
    }
  } else if (kind === 'house_small' || kind === 'house_big' || kind === 'log_cabin') {
    for (const s of [-1, 1]) place(s * (r + 0.12), r * 0.48, (px, pz) => addProp('grassTuft', { x: px, z: pz, scale: 0.55, yaw: yaw + s }));
  }
}

// ---- runtime structure factory (editor "Add house" / "Duplicate" + baked) ---
// Wraps buildHouse (or a kit building, when spec.type names one) →
// captureStructure, which already registers collision (movable in editor
// builds, static in kid builds) and pushes to editableStructures. Returns
// that record plus a dispose() for editor undo.
function createStructure(spec = {}) {
  const { x = 0, z = 0, w, d, wallH, wallColor, roofColor, withChimney, decor, name } = spec;
  let group = null;
  if (spec.type && spec.type !== 'house' && KIT_KINDS[spec.type]) {
    // Kit building. Falls through to a procedural house of comparable
    // footprint when the kit failed to load, so a baked world never has holes.
    group = buildKitStructure(spec.type, x, z, { name, spec: { ...spec } });
    if (!group) {
      const fb = KIT_KINDS[spec.type].fallback;
      group = buildHouse(x, z, fb.w, fb.d, Math.min(fb.h * 0.75, 1.8), wallColor, roofColor, false, name, decor);
      group.userData.buildSpec = { ...spec };   // keep the kit type for re-export
    }
  } else {
    group = buildHouse(x, z, w, d, wallH, wallColor, roofColor, withChimney, name, decor);
  }
  if (spec.rot !== undefined) group.rotation.y = spec.rot;
  if (spec.scale !== undefined) group.scale.setScalar(spec.scale);
  const rec = editableStructures[editableStructures.length - 1];
  rec.dispose = () => {
    propsGroup.remove(rec.group);
    for (const ob of (rec.group.userData.obstacles || [])) removeMovableObstacle(ob);
    const i = editableStructures.indexOf(rec);
    if (i >= 0) editableStructures.splice(i, 1);
  };
  return rec;
}

function addHouseDecor(x, z, w, d, wallH, decor) {
  const theme = typeof decor === 'string' ? decor : decor?.theme;
  if (!theme) return;
  const frontZ = z + d / 2 + 0.04;
  const roofY = wallH + 0.62;
  const roofZ = z + d * 0.08;
  const sideX = x - w / 2 - 0.08;
  const addFrontBox = (dx, y, ww, hh, color) => addBox(x + dx, y, frontZ, ww, hh, 0.04, color, false);
  const addRotBox = (bx, by, bz, bw, bh, bd, color, ry = 0, rz = 0) => {
    const m = addBox(bx, by, bz, bw, bh, bd, color, false);
    m.rotation.y = ry;
    m.rotation.z = rz;
    return m;
  };

  if (theme === 'pop-safari') {
    // Leafy pennants plus soft paw dots over the door.
    for (let i = 0; i < 5; i++) {
      const dx = (i - 2) * 0.28;
      addRotBox(x + dx, wallH + 0.08, frontZ + 0.01, 0.18, 0.08, 0.035, i % 2 ? PALETTE.leafBase : PALETTE.leafDark, 0, (i - 2) * 0.18);
    }
    addFrontBox(-0.42, 0.86, 0.16, 0.12, PALETTE.doorDark);
    addFrontBox(-0.52, 0.98, 0.06, 0.06, PALETTE.doorDark);
    addFrontBox(-0.42, 1.02, 0.06, 0.06, PALETTE.doorDark);
    addFrontBox(-0.32, 0.98, 0.06, 0.06, PALETTE.doorDark);
  } else if (theme === 'numberzoo') {
    // Three counting blocks and abacus beads beside the doorway.
    const baseX = x - 0.72;
    for (let i = 0; i < 3; i++) {
      const bx = baseX + i * 0.22;
      addBox(bx, 0.16, frontZ + 0.02, 0.2, 0.2, 0.2, [PALETTE.coral, PALETTE.skyBlue, PALETTE.gold][i], false);
      addBox(bx, 0.18, frontZ + 0.14, 0.09, 0.025, 0.02, PALETTE.doorDark, false);
    }
    addBox(x + 0.68, 0.58, frontZ, 0.08, 0.62, 0.04, PALETTE.woodDark, false);
    addBox(x + 0.98, 0.58, frontZ, 0.08, 0.62, 0.04, PALETTE.woodDark, false);
    for (let i = 0; i < 4; i++) addSphere(x + 0.76 + i * 0.06, 0.62 + (i % 2) * 0.12, frontZ + 0.02, 0.045, i % 2 ? PALETTE.gold : PALETTE.coral, false);
  } else if (theme === 'adventure-101') {
    addBox(sideX, 0.18, z + 0.55, 0.42, 0.28, 0.32, PALETTE.wood, false);
    addBox(sideX, 0.36, z + 0.55, 0.34, 0.08, 0.24, PALETTE.doorDark, false);
    addBox(x + w * 0.24, roofY + 0.35, roofZ, 0.05, 0.7, 0.05, PALETTE.woodDark, false);
    addBox(x + w * 0.43, roofY + 0.48, roofZ, 0.34, 0.2, 0.04, PALETTE.coral, false);
    addFrontBox(0.48, 1.05, 0.36, 0.06, PALETTE.goldSoft);
    addFrontBox(0.48, 1.05, 0.06, 0.36, PALETTE.goldSoft);
  } else if (theme === 'last-word') {
    for (let i = 0; i < 3; i++) addBox(x - 0.78, 0.12 + i * 0.09, frontZ + 0.02, 0.42 - i * 0.06, 0.08, 0.2, [PALETTE.skyBlue, PALETTE.coralSoft, PALETTE.goldSoft][i], false);
    addRotBox(x + 0.5, wallH + 0.24, frontZ, 0.08, 0.55, 0.035, 0xf5f0d8, 0, -0.55);
    addBox(x + 0.62, wallH + 0.45, frontZ, 0.18, 0.08, 0.035, PALETTE.doorDark, false);
  } else if (theme === 'math-chef') {
    addBox(x + w * 0.27, roofY + 0.2, z - d * 0.1, 0.22, 0.42, 0.22, PALETTE.stoneDark, false);
    addBox(x + w * 0.27, roofY + 0.44, z - d * 0.1, 0.32, 0.08, 0.32, PALETTE.doorDark, false);
    addBox(x, wallH + 0.02, frontZ, 1.1, 0.14, 0.06, PALETTE.goldSoft, false);
    addBox(x, wallH - 0.11, frontZ + 0.01, 1.0, 0.12, 0.04, PALETTE.coral, false);
    addBox(x + 0.82, 0.35, frontZ + 0.22, 0.48, 0.12, 0.38, PALETTE.wood, false);
  } else if (theme === 'royal-feast') {
    addBox(x, roofY + 0.18, roofZ, 0.62, 0.14, 0.12, PALETTE.goldSoft, false);
    for (let i = 0; i < 3; i++) addBox(x + (i - 1) * 0.22, roofY + 0.34, roofZ, 0.12, 0.22, 0.12, PALETTE.goldSoft, false);
    addFrontBox(0, wallH + 0.02, 1.15, 0.16, PALETTE.lavender);
    addFrontBox(-0.32, wallH - 0.12, 0.18, 0.12, PALETTE.goldSoft);
    addFrontBox(0.32, wallH - 0.12, 0.18, 0.12, PALETTE.goldSoft);
    addFrontBox(0.62, 0.94, 0.26, 0.34, PALETTE.skyBlue);
    addFrontBox(0.62, 0.94, 0.06, 0.34, PALETTE.goldSoft);
  }
}

// ===== WORLD PROPS ==========================================================
// Trees / bushes / rocks / stones / flowers are plain data records registered
// with the chunk system (world/chunks.js), which builds per-chunk
// InstancedMesh objects with real frustum culling and streams them around the
// player.

// Flower-patch centres double as butterfly habitats (see BIRDS + BUTTERFLIES).
// Each entry: { x, z, r } — r is the patch radius butterflies wander within.
const flowerPatches = [];

function buildTree(x, z, scale = 1) {
  // Trunk collision (no individual mesh — rendered by the chunk system)
  const r = 0.16 * scale;
  const gy = heightAt(x, z);
  addObstacle({
    minX: x - r, maxX: x + r,
    minY: gy,    maxY: gy + 0.9 * scale,
    minZ: z - r, maxZ: z + r,
  });
  addProp('trunk', { x, z, scale });
  // A wider spread of leaf hues — warm sun-touched greens, deep shade greens and
  // a couple of cooler/olive tones — so a stand of trees reads as many
  // individuals instead of one repeated green blob. Pure data (per-instance
  // colour is already uploaded once at chunk build), so it costs nothing extra
  // at runtime, on any tier.
  const leafColors = [
    PALETTE.leafBase, PALETTE.leafDark, PALETTE.leafTip,
    0x7faa5f, 0x6a9850, 0x88b862, 0x5f8a48, 0x9cc36e,
  ];
  addProp('foliage', {
    x, z, scale,
    // A touch more scale spread gives the canopy silhouette more variety.
    leafScale: scale * (0.88 + Math.random() * 0.26),
    leafLift: 0.84 + Math.random() * 0.13,
    phase: Math.random() * Math.PI * 2,
    color: leafColors[(Math.random() * leafColors.length) | 0],
  });
}

// Cobblestone path: line of low flat circles. Instanced — see buildInstancedMeshes.
function buildPath(points) {
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, z1] = points[i], [x2, z2] = points[i+1];
    villagePathSegments.push({ x1, z1, x2, z2 });
    const steps = Math.ceil(Math.hypot(x2 - x1, z2 - z1) / 0.5);
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const px = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 0.1;
      const pz = z1 + (z2 - z1) * t + (Math.random() - 0.5) * 0.1;
      const r = 0.18 + Math.random() * 0.05;
      const warm = Math.random() < 0.55 ? PALETTE.stone : (Math.random() < 0.5 ? PALETTE.stoneLight : PALETTE.pathWarm);
      addProp('pathStone', { x: px, z: pz, r, color: warm });
    }
  }
}

function buildWell(x, z) {
  // Handmade well from the kit when it loaded; the procedural stone ring
  // otherwise. The bump-zone interaction is coordinate-based and unaffected.
  const kit = buildKitStructure('well', x, z, { name: 'Wishing well' });
  if (kit) return kit;
  addContactShadow(x, z, 1.7, 1.25, 0.2);
  return captureStructure('Wishing well', x, z, () => {
    // Stone ring (5 narrow tall stones around the rim)
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const sx = x + Math.cos(a) * 0.65;
      const sz = z + Math.sin(a) * 0.65;
      addBox(sx, 0.25, sz, 0.22, 0.5, 0.22, 0x888076);
    }
    // Dark water disk inside (slightly recessed)
    addCyl(x, 0.05, z, 0.55, 0.04, 0x2a4a6a, false);
    const wellGlint = new THREE.Mesh(
      new THREE.RingGeometry(0.18, 0.22, LOW ? 12 : 18, 1, 0.2, Math.PI * 1.05),
      new THREE.MeshBasicMaterial({ color: 0xeaffff, transparent: true, opacity: 0.34, depthWrite: false, side: THREE.DoubleSide, fog: true })
    );
    wellGlint.rotation.x = -Math.PI / 2;
    wellGlint.rotation.z = -0.35;
    wellGlint.position.set(x - 0.12, 0.085, z - 0.08);
    propsGroup.add(wellGlint);
    // Two posts and a beam (well roof support)
    addBox(x - 0.6, 0.9, z, 0.08, 1.3, 0.08, PALETTE.woodDark);
    addBox(x + 0.6, 0.9, z, 0.08, 1.3, 0.08, PALETTE.woodDark);
    addBox(x, 1.55, z, 1.4, 0.08, 0.18, PALETTE.woodDark);
    // Tiny roof on top
    addBox(x, 1.7, z, 1.5, 0.15, 0.5, PALETTE.roofTerracotta);
  });
}

function buildFence(x1, z1, x2, z2) {
  const len = Math.hypot(x2 - x1, z2 - z1);
  const segs = Math.ceil(len / 0.5);
  const angle = Math.atan2(z2 - z1, x2 - x1);
  // Collision half-thickness. The player is pushed R (0.35) clear of the box
  // edge, so a thin pad keeps the wall reading as slim as the visual posts.
  const PAD = 0.1;
  let prevX = x1, prevZ = z1, prevGy = heightAt(x1, z1);
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const px = x1 + (x2 - x1) * t;
    const pz = z1 + (z2 - z1) * t;
    const gy = heightAt(px, pz);
    // Posts are instanced by the chunk system
    addProp('fencePost', { x: px, z: pz });
    // One thin solid AABB per segment between consecutive posts, so the fence
    // blocks the player's path. It only rises to the post top (gy + 0.6), which
    // sits well under the ~1.5-unit jump apex — so a fence stops a walk but is
    // cleanly cleared with a jump. Stairstepped AABBs follow diagonal fences too.
    if (i > 0) {
      const baseY = Math.min(prevGy, gy);
      addObstacle({
        minX: Math.min(prevX, px) - PAD, maxX: Math.max(prevX, px) + PAD,
        minY: baseY,                     maxY: baseY + 0.6,
        minZ: Math.min(prevZ, pz) - PAD, maxZ: Math.max(prevZ, pz) + PAD,
      });
    }
    prevX = px; prevZ = pz; prevGy = gy;
  }
  // Rails are instanced too (unit box stretched per instance) — every rail
  // used to be its own mesh + draw call.
  const mx = (x1 + x2) / 2, mz = (z1 + z2) / 2;
  addProp('fenceRail', { x: mx, z: mz, len, yaw: -angle });
}

// ===== Layout the village =====
// Open central plaza with spawn at origin. Well to the south. Houses fan
// out around the perimeter. Paths connect things. Trees fill the corners.
// SPAWN AT (0,0) IS DELIBERATELY KEPT CLEAR.

// Kick off chocky.glb's download NOW — before the two blocking top-level awaits
// (loadKit just below, loadBakedLayer later) — so the model the kid is watching
// the loading screen for streams IN PARALLEL with the kit and baked world
// instead of queued behind them. The promise is consumed far below (search
// chockyGltfPromise.then), after all the model-setup code it needs is defined.
// The #loading progress helpers live here too, since onProgress fires during
// this fetch. All retry logic lives in loadGLBWithRetry (core/net.js); this only
// paints DOM.
const _loadStart = performance.now();
let _loadSlowShown = false;
function _showSlowHint() {
  if (_loadSlowShown) return;
  const loading = document.getElementById('loading');
  if (!loading || loading.style.display === 'none') return;  // already loaded
  _loadSlowShown = true;
  const hint = document.getElementById('loadingHint');
  if (hint) hint.textContent = 'slow connection, still loading…';
}
// Fires even if zero bytes ever arrive (a truly dead link never ticks progress).
setTimeout(_showSlowHint, 6000);
function updateLoadingProgress(p) {
  const bar = document.getElementById('loadingBar');
  const fill = document.getElementById('loadingBarFill');
  if (bar && fill) {
    if (p.lengthComputable && p.total > 0) {
      bar.classList.remove('indeterminate');
      fill.style.width = Math.round((p.loaded / p.total) * 100) + '%';
    } else {
      // No Content-Length: keep the animated sweep and show a byte counter.
      bar.classList.add('indeterminate');
      const msg = document.getElementById('loadingMsg');
      if (msg) msg.textContent = `loading chocky… ${(p.loaded / 1024) | 0} KB`;
    }
  }
  if (performance.now() - _loadStart > 6000) _showSlowHint();
}
// Auto-retry transient failures (3 tries, 12s timeout each) before falling back
// to the manual "Try again" dialog — a dropped packet on flaky Wi-Fi self-heals.
const chockyGltfPromise = loadGLBWithRetry(makeGLTFLoader(), 'chocky.glb', {
  retries: 3,
  timeoutMs: 12000,
  onProgress: updateLoadingProgress,
});

// The handmade building/prop kit must be in hand before any builder below
// runs — buildings pick GLB or procedural at build time. Top-level await
// (like the baked-layer await before chunk build): resolves false on any
// failure, and every consumer falls back to the procedural builders.
await loadKit();

// Sign helper: a canvas-textured plane held up by a small wooden post.
// The sign faces toward the world origin (the village spawn).
function makeSignTexture(text) {
  // Drawn in a fixed 1024×384 coordinate space; the low tier renders it at
  // half resolution (signs are ~1.5 MB of VRAM each at full size, and the
  // world now has nearly a dozen of them).
  const W = 1024, H = 384;
  const SCALE = LOW ? 0.5 : 1;
  const c = document.createElement('canvas');
  c.width = W * SCALE; c.height = H * SCALE;
  const ctx = c.getContext('2d');
  ctx.scale(SCALE, SCALE);
  // Wooden board
  ctx.fillStyle = '#c89968';
  ctx.fillRect(0, 0, W, H);
  // Dark heavy border for readability
  ctx.strokeStyle = '#3a2818';
  ctx.lineWidth = 24;
  ctx.strokeRect(12, 12, W - 24, H - 24);
  // Inner highlight
  ctx.strokeStyle = '#e0b88a';
  ctx.lineWidth = 4;
  ctx.strokeRect(40, 40, W - 80, H - 80);
  // Icons-first wayfinding for younger kids: a big pictogram at the left gives
  // the destination meaning before the word is decoded. Drawn into the same
  // canvas texture, so it costs no extra draw calls and stays no-build-safe.
  const upper = String(text).toUpperCase();
  const icon =
    upper.includes('WORD') || upper.includes('LAST') ? '📚' :
    upper.includes('EXPLORER') || upper.includes('OUTFITTER') || upper.includes('CLOSET') ? '⛑️' :
    upper.includes('GROVE') || upper.includes('LANDMARK') || upper.includes('WORLD') ? '🧭' :
    upper.includes('OBBY') || upper.includes('PEAK') ? '⛰' :
    upper.includes('MATH') || upper.includes('NUMBER') ? '🔢' :
    upper.includes('SAFARI') || upper.includes('ZOO') ? '🐾' :
    upper.includes('FEAST') || upper.includes('CHEF') ? '🍳' :
    upper.includes('GREEN') ? '🌼' :
    '⭐';
  ctx.save();
  ctx.font = '900 118px "Apple Color Emoji","Segoe UI Emoji",ui-sans-serif,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(255,255,255,0.42)';
  ctx.shadowBlur = 10;
  ctx.fillText(icon, 112, H / 2 + 2);
  ctx.restore();
  // Text — large, very heavy, with subtle shadow for contrast
  ctx.fillStyle = '#3a2818';
  let fontSize = 200;
  ctx.font = `900 ${fontSize}px ui-sans-serif, system-ui, -apple-system, "Segoe UI"`;
  while (ctx.measureText(text).width > W - 250 && fontSize > 60) {
    fontSize -= 8;
    ctx.font = `900 ${fontSize}px ui-sans-serif, system-ui, -apple-system, "Segoe UI"`;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Drop shadow for legibility
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillText(text, W / 2 + 60, H / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = LOW ? 2 : 8;       // crisp at oblique angles
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const signGroups = [];   // [{group, name, id}] — used by the sign editor
const hutSignFootprints = [];

function resolveHutSignOverlap(sx, sz, yaw) {
  const HALF_W = 1.08;
  const HALF_D = 0.18;
  const PAD = 0.22;
  let x = sx, z = sz;
  const rightX = Math.cos(yaw);
  const rightZ = -Math.sin(yaw);
  for (let attempt = 0; attempt < 10; attempt++) {
    let hit = false;
    for (const fp of hutSignFootprints) {
      if (Math.abs(x - fp.x) < HALF_W + fp.halfW + PAD && Math.abs(z - fp.z) < HALF_D + fp.halfD + PAD) {
        hit = true;
        break;
      }
    }
    if (!hit) break;
    const dir = attempt % 2 === 0 ? 1 : -1;
    const step = 0.24 * (1 + Math.floor(attempt / 2));
    x = sx + rightX * dir * step;
    z = sz + rightZ * dir * step;
  }
  hutSignFootprints.push({ x, z, halfW: HALF_W, halfD: HALF_D });
  return { x, z };
}

function signIdFrom(name, x, z) {
  return `${String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'sign'}@${x.toFixed(1)},${z.toFixed(1)}`;
}

// Manually-placed sign positions (via the ! editor). When a name is here it
// wins over the auto-calculated "in front of hut, facing spawn" default.
const SIGN_PLACEMENT = {
  "POP SAFARI":    { x: -5.19, y:  0.55, z: -5.19, yaw:  0.785 },
  "NUMBERZOO":     { x:  4.95, y:  0.69, z: -5.26, yaw: -0.666 },
  "ADVENTURE 101": { x: -4.51, y: -0.56, z:  6.09, yaw:  1.560 },
  "LAST WORD":     { x:  6.10, y:  0.84, z:  6.11, yaw:  0.911 },
  "MATH CHEF":     { x: -7.42, y:  0.18, z:  0.62, yaw:  1.571 },
  "ROYAL FEAST":   { x:  8.90, y:  0.67, z:  0.00, yaw:  0.038 },
};

// `placement` ({x, y, z, yaw}) wins over the SIGN_PLACEMENT table — zones use
// it for rooftop signs, and it sidesteps name collisions. All signs register
// in signGroups either way, so the ! editor can nudge any of them. Each sign
// gets a stable id separate from its display text so same-named signs can save
// and reload independently in the world-edit layer.
function addHutSign(x, z, name, placement = null, id = null) {
  const signId = id || placement?.id || signIdFrom(name, x, z);
  let sx, sy = 0, sz, yaw;
  const override = placement || SIGN_PLACEMENT[signId] || SIGN_PLACEMENT[name];
  if (override) {
    sx = override.x; sy = override.y; sz = override.z; yaw = override.yaw;
  } else {
    // Fallback: auto-place in front of hut, facing the spawn point
    const dx = -x, dz = -z;
    const dist = Math.hypot(dx, dz) || 1;
    const nx = dx / dist, nz = dz / dist;
    sx = x + nx * 1.6;
    sz = z + nz * 1.6;
    yaw = Math.atan2(nx, nz);
  }

  if (!id) {
    const clearSpot = resolveHutSignOverlap(sx, sz, yaw);
    sx = clearSpot.x; sz = clearSpot.z;
  }

  const group = new THREE.Group();
  group.position.set(sx, sy, sz);
  group.rotation.y = yaw;
  group.userData = { gameName: name, signId };

  // Post (local coords inside the group)
  const post = new THREE.Mesh(
    new THREE.BoxGeometry(0.10, 1.2, 0.10),
    new THREE.MeshLambertMaterial({ color: PALETTE.woodDark })
  );
  post.position.y = 0.6;
  post.castShadow = true;
  group.add(post);

  // A solid post so Chocky can't stroll straight through sign posts (the board
  // itself is decorative). Editor builds make it a MOVABLE obstacle that follows
  // the sign gizmo — the way structures do — while kid builds register a plain
  // static box. A ground sign blocks a walker; a rooftop sign sits too high up
  // to ever touch one, so registering it unconditionally is harmless.
  const POST_HALF = 0.07;
  const postAABB = {
    minX: sx - POST_HALF, maxX: sx + POST_HALF,
    minY: sy,             maxY: sy + 1.2,
    minZ: sz - POST_HALF, maxZ: sz + POST_HALF,
  };
  if (EDITOR_ENABLED) {
    const ob = addMovableObstacle(postAABB);
    ob._local = { minX: -POST_HALF, maxX: POST_HALF, minY: 0, maxY: 1.2, minZ: -POST_HALF, maxZ: POST_HALF };
    group.userData.obstacles = [ob];
  } else {
    addObstacle(postAABB);
  }

  // The canvas texture itself carries the high-contrast wooden backing,
  // border, icon and label, so readability costs no extra sign draw call.
  const tex = makeSignTexture(name);
  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(2.0, 0.75),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide })
  );
  sign.position.y = 1.5;
  group.add(sign);

  propsGroup.add(group);
  signGroups.push({ group, name, id: signId });
  return group;
}

// ---- runtime sign factory + live text edit (editor + baked-world) ----------
// Create a brand-new sign at runtime (the authored SIGN_PLACEMENT table only
// covers the village huts). Returns the signGroups record plus a dispose()
// closure so the editor can undo/remove it without reaching into propsGroup.
function createSign({ text = 'SIGN', x = 0, z = 0, y = 0, yaw = 0, id = null } = {}) {
  addHutSign(x, z, text, { x, y, z, yaw }, id);
  const rec = signGroups[signGroups.length - 1];
  rec.dispose = () => {
    propsGroup.remove(rec.group);
    for (const ob of (rec.group.userData.obstacles || [])) removeMovableObstacle(ob);
    const i = signGroups.indexOf(rec);
    if (i >= 0) signGroups.splice(i, 1);
  };
  return rec;
}

// Swap a sign's board texture to new text in place (works for procedural and
// editor-created signs). The board is the only child mesh with a .map.
function setSignText(signRec, text) {
  const group = signRec?.group || signRec;
  if (!group) return;
  let board = null;
  group.traverse((o) => { if (!board && o.isMesh && o.material && o.material.map) board = o; });
  if (!board) return;
  const old = board.material.map;
  board.material.map = makeSignTexture(text);
  board.material.needsUpdate = true;
  if (old) old.dispose();
  if (signRec && signRec.name !== undefined) signRec.name = text;
  group.userData.gameName = text;
}

// ---- Directional signposts --------------------------------------------------
// A wooden post with one or more arrow boards pointing the way to other
// places — the first wayfinding the world has had.
function makeArrowSignTexture(text, flip) {
  const W = 512, H = 144;
  const SCALE = LOW ? 0.5 : 1;
  const c = document.createElement('canvas');
  c.width = W * SCALE; c.height = H * SCALE;
  const ctx = c.getContext('2d');
  ctx.scale(SCALE, SCALE);
  // Pointed board (mirror the SHAPE for left-pointing arrows, not the text).
  if (flip) { ctx.translate(W, 0); ctx.scale(-1, 1); }
  ctx.beginPath();
  ctx.moveTo(10, 18);
  ctx.lineTo(W - 78, 18);
  ctx.lineTo(W - 12, H / 2);
  ctx.lineTo(W - 78, H - 18);
  ctx.lineTo(10, H - 18);
  ctx.closePath();
  ctx.fillStyle = '#c89968';
  ctx.fill();
  ctx.lineWidth = 10;
  ctx.strokeStyle = '#3a2818';
  ctx.stroke();
  if (flip) ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  ctx.fillStyle = '#3a2818';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Shrink the label until it fits the board. Two-word signs like "VILLAGE
  // GREEN" overflow the fixed 64px width and clip against the panel edge
  // otherwise; the arrow point eats the right margin, so keep the budget snug.
  let fontPx = 64;
  ctx.font = `900 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
  const MAX_TEXT_W = 360;
  while (fontPx > 26 && ctx.measureText(text).width > MAX_TEXT_W) {
    fontPx -= 3;
    ctx.font = `900 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
  }
  ctx.fillText(text, W / 2 + (flip ? 30 : -30), H / 2 + 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = LOW ? 2 : 8;
  return tex;
}

// boards: [{ text, yaw, flip }] — yaw faces the board; flip points the arrow
// left instead of right. Boards stack down the post.
function buildSignpost(x, z, boards, name = null, id = null) {
  addContactShadow(x, z, 0.72, 0.42, 0.25);
  const gy = heightAt(x, z);
  const signName = name || `SIGNPOST: ${boards.map(b => b.text).join(' / ')}`;
  const signId = id || signIdFrom(signName, x, z);
  const group = new THREE.Group();
  group.position.set(x, 0, z);
  group.userData = { signId };

  const post = addBox(x, gy + 0.8, z, 0.1, 1.6, 0.1, PALETTE.woodDark);
  post.position.x -= x;
  post.position.z -= z;
  group.add(post);
  let y = gy + 1.38;
  for (const b of boards) {
    const geom = new THREE.PlaneGeometry(1.7, 0.48);
    const front = new THREE.Mesh(
      geom,
      new THREE.MeshBasicMaterial({ map: makeArrowSignTexture(b.text, !!b.flip), transparent: true, side: THREE.FrontSide })
    );
    front.position.set(0, y, 0.012);
    front.rotation.y = b.yaw;
    group.add(front);

    const back = new THREE.Mesh(
      geom.clone(),
      new THREE.MeshBasicMaterial({ map: makeArrowSignTexture(b.text, !b.flip), transparent: true, side: THREE.FrontSide })
    );
    back.position.set(0, y, -0.012);
    back.rotation.y = b.yaw + Math.PI;
    group.add(back);
    y -= 0.55;
  }
  propsGroup.add(group);
  signGroups.push({ group, name: signName, id: signId });
  return group;
}

// A little crop of mushrooms (2–4) tucked at (x, z) — one instanced draw per
// chunk however many there are.
function buildMushroomCluster(x, z, n = 3, rng = Math.random) {
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2;
    const d = 0.15 + rng() * 0.45;
    addProp('mushroom', {
      x: x + Math.cos(a) * d,
      z: z + Math.sin(a) * d,
      yaw: rng() * Math.PI * 2,
      scale: 0.7 + rng() * 0.7,
    });
  }
}

// Six houses — each is a GAME HUT. Tracked in gameHuts[] for proximity dialog.
// Only slugs in this set have an embeddable build; the other huts are advertised
// as coming soon so Enter never routes players into a dead placeholder.
const PLAYABLE_GAME_SLUGS = new Set(['pop-safari']);
// Some huts are doorways out to other worlds rather than embeddable games.
// Walk up to one and press ENTER to travel to the linked site.
const GATEWAY_URLS = {
  'numberzoo':     'https://choco.zone',       // Numberzoo (placeholder host for now)
  'adventure-101': 'https://adventure101.net', // Adventure 101
};
const gameHuts = [];
const hutCoins = [];   // remember roof-coin positions for later (placed after addCoin is defined)
function buildGameHut(x, z, w, d, wallH, wallC, roofC, smoke, gameName, gameSlug, signPlacement = null, decor = null, kind = null) {
  // Kit building when a kind is named and the kit loaded; the procedural hut
  // otherwise. The themed wall doodads (addHouseDecor) are procedural-only —
  // the handmade models carry their theme in the art itself. Chimney smoke
  // follows the MODEL's chimney (KIT_KINDS smoke offset), not the old flag.
  const kitGroup = kind ? buildKitStructure(kind, x, z, { name: gameName }) : null;
  if (kitGroup) {
    const size = kitGroup.userData.kitSize;
    w = size.w; d = size.d; wallH = size.h - 0.51;   // keep the derived offsets below meaningful
  } else if (kind) {
    const fb = KIT_KINDS[kind]?.fallback;
    if (fb) { w = fb.w; d = fb.d; wallH = Math.min(fb.h * 0.75, 1.8); }
    buildHouse(x, z, w, d, wallH, wallC, roofC, smoke, gameName, decor);
  } else {
    buildHouse(x, z, w, d, wallH, wallC, roofC, smoke, gameName, decor);
  }
  addHutSign(x, z, gameName, signPlacement);
  addVillageLandmark(x, z, Math.max(w, d) * 0.75 + 1.4);
  // Coin floats above the roof peak (roof top is at wallH + 0.51).
  // Stored for placement after addCoin is defined below.
  hutCoins.push({ x, y: wallH + 1.1, z });
  // Place the entrance trigger right at the building's doorway. The visual door
  // is always on the +z face of the house (see buildHouse), so anchor the
  // trigger there and just in front of it — the dialog should only pop when the
  // player is standing at the door, not anywhere near the hut.
  const entranceX = x;
  const entranceZ = z + d / 2 + 0.5;
  // A tiny welcome mat made from the existing instanced path-stone pool: it
  // visually anchors every doorway without adding a new draw call on Chromebooks.
  addProp('pathStone', { x: entranceX, z: entranceZ - 0.08, r: 0.34, color: mixHexColors(wallC, PALETTE.goldSoft, 0.35) });
  addProp('pathStone', { x: entranceX, z: entranceZ + 0.18, r: 0.24, color: PALETTE.sand });
  gameHuts.push({ x, z, entranceX, entranceZ, name: gameName, slug: gameSlug, playable: PLAYABLE_GAME_SLUGS.has(gameSlug), gateway: GATEWAY_URLS[gameSlug] || null });
  addEntrancePlantCluster(x, z, w, d, gameSlug);
  addHutThemeAccents(x, z, w, d, gameSlug);
}

function addEntrancePlantCluster(x, z, w, d, seed = '') {
  const frontZ = z + d / 2 + 0.22;
  const side = w / 2 + 0.18;
  const colors = [PALETTE.coral, PALETTE.gold, PALETTE.skyBlue, PALETTE.lavender];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  for (const sign of [-1, 1]) {
    const bx = x + sign * side;
    buildGrassTuft(bx, frontZ, 0.55 + ((hash + sign) & 3) * 0.08);
    const fx = bx + sign * 0.18;
    const fz = frontZ + 0.08;
    const h = 0.2 + (Math.abs(hash + sign) % 3) * 0.04;
    addProp('flowerStem', { x: fx, z: fz, h });
    addProp('flowerHead', { x: fx, z: fz, h, color: colors[Math.abs(hash + sign) % colors.length], s: 0.75 });
  }
}

function addHutThemeAccents(x, z, w, d, slug = '') {
  const frontZ = z + d / 2 + 0.52;
  const sideX = w / 2 + 0.42;
  if (slug === 'pop-safari') {
    for (const [ox, oz, r] of [[-0.18, 0, 0.09], [0.06, 0.16, 0.07], [0.22, -0.03, 0.06]]) {
      addProp('pathStone', { x: x - sideX + ox, z: frontZ + oz, r });
    }
  } else if (slug === 'numberzoo') {
    addProp('crate', { x: x + sideX, z: frontZ, scale: 0.62, yaw: 0.35 });
    addProp('barrel', { x: x + sideX + 0.38, z: frontZ - 0.08, scale: 0.58, yaw: -0.2 });
  } else if (slug === 'adventure-101') {
    for (let i = 0; i < 3; i++) addProp('pathStone', { x: x - 0.35 + i * 0.35, z: frontZ + 0.25 + i * 0.08, r: 0.16 });
    addProp('rock', { x: x - sideX, z: frontZ + 0.12, scale: 0.55, yaw: 0.7 });
  } else if (slug === 'math-chef') {
    addProp('marketBasket', { x: x + sideX, z: frontZ, scale: 0.72, yaw: -0.35 });
    addProp('crate', { x: x + sideX + 0.34, z: frontZ - 0.05, scale: 0.55, yaw: 0.15 });
  } else if (slug === 'royal-feast') {
    for (const s of [-1, 1]) addProp('tinyFlag', { x: x + s * sideX, z: frontZ, scale: 0.72, yaw: s * 0.4 });
  } else if (slug === 'last-word') {
    addProp('flowerBox', { x: x - sideX, z: frontZ, scale: 0.72, yaw: 0.25 });
  }
}

// Each hut names a kit model kind (last arg) — the handmade GLB used when the
// kit loads. The w/d/wallH/color args remain the procedural-fallback recipe.
//                  x    z    w    d    wallH wallColor   roofColor   smoke  game name           slug
buildGameHut( -6, -6,  2.4, 2.4, 1.4,  BUILDING_SCHEMES.popSafari.wall, BUILDING_SCHEMES.popSafari.roof, true,  'POP SAFARI',    'pop-safari', null, { theme: 'pop-safari' }, 'house_small');
buildGameHut(  6, -6,  2.6, 2.0, 1.5,  BUILDING_SCHEMES.numberZoo.wall, BUILDING_SCHEMES.numberZoo.roof, false, 'NUMBERZOO',     'numberzoo', null, { theme: 'numberzoo' }, 'numberzoo_barn');
buildGameHut( -6,  6,  2.2, 2.6, 1.3,  BUILDING_SCHEMES.adventureBlue.wall, BUILDING_SCHEMES.adventureBlue.roof, true,  'ADVENTURE 101', 'adventure-101', null, { theme: 'adventure-101' }, 'log_cabin');
buildGameHut(  6,  6,  2.0, 2.0, 1.6,  BUILDING_SCHEMES.wordCream.wall, BUILDING_SCHEMES.wordCream.roof, false, 'LAST WORD',     'last-word', null, { theme: 'last-word' }, 'house_small');
buildGameHut( -9,  0,  2.2, 2.4, 1.4,  BUILDING_SCHEMES.mathChef.wall, BUILDING_SCHEMES.mathChef.roof, true,  'MATH CHEF',     'math-chef', null, { theme: 'math-chef' }, 'house_big');
buildGameHut(  9,  0,  2.4, 2.2, 1.5,  BUILDING_SCHEMES.royalFeast.wall, BUILDING_SCHEMES.royalFeast.roof, false, 'ROYAL FEAST',   'royal-feast', null, { theme: 'royal-feast' }, 'house_big');

// Kit-model landmarks (assetpack2). Both are structures: buildKitStructure
// samples heightAt for the base and registers an inset collision box, so they
// seat on the rolling terrain outside the flat village plateau. Placed here at
// top level because the quest/zone ctxs don't expose buildKitStructure.
buildKitStructure('wizard_tower', -20, 4, { name: "Wizard's Tower", yaw: 0.35 });          // beside Wizard Zip's meadow
buildKitStructure('adventure101_castle', -11, 11, { name: 'Adventure Castle', yaw: -0.5 }); // backdrop behind the ADVENTURE 101 hut

// Well in the south-center, well away from spawn
buildWell(0, -4);
addVillageLandmark(0, -4, 2.4);

// ---- Wishing Well interaction. Bump it and, if you're carrying coins, it asks
// if you'd like to throw one in. Tossing one (Enter, with Y kept as a legacy
// alias) spends a coin. Right now that's all it does — wishes get hooked up later.
let nearWell = false;
let _wellSplash = 0;     // seconds the "Sploosh!" message lingers after a toss
let _wellState = null;   // 'prompt' | 'nocoin' | 'splash' — what the bubble shows
let _wellPity = 0;       // wishes since the last gift (pity-guarantees a drop)
const WELL_INTERACT_KBD = '<kbd style="background:#ff6b6b; color:white; padding:2px 8px; border-radius:4px; font-family:ui-monospace,monospace;">ENTER</kbd>';
function setWellState(s) {
  if (s === _wellState) return;   // avoid rewriting the DOM every frame
  _wellState = s;
  if (s === 'prompt')      showBumpMsg('THE WISHING WELL', HAS_TOUCH ? 'Throw a Coin? &nbsp;Tap this card!' : `Throw a Coin? &nbsp;Press ${WELL_INTERACT_KBD}`, 0, 'well');
  else if (s === 'nocoin') showBumpMsg('THE WISHING WELL', 'Come back with a coin to make a wish!', 0, 'well');
}
function updateWell(dt) {
  if (!nearWell) return;
  if (_wellSplash > 0) {
    _wellSplash -= dt;
    if (_wellSplash > 0) return;   // still showing the splash message
    _wellState = null;             // splash over — fall through and re-prompt
  }
  setWellState(coinInventory > 0 ? 'prompt' : 'nocoin');
}
// Cosmetics the well can grant as a "wish come true" — the normally-bought gear
// (price > 0), never the quest-locked rewards or the default cream body.
const WELL_PRIZES = OUTFIT_CATALOG.filter(it => it.price > 0 && !it.quest);

// Toss a coin in (called from the keydown handler when Enter/Y is pressed near
// the well). Design plan (§ wishing well as a coin sink with a rare gear drop):
// each wish spends a coin and rolls for a free cosmetic, with a pity guarantee
// so a patient kid is never denied. Once every prize is owned the well just
// returns the coin with a sparkle — nothing left to win, and no coin fountain.
function throwCoinInWell() {
  if (!nearWell || coinInventory <= 0) return;
  coinInventory--;
  updateCoinUI();
  _wellPity++;
  _wellState = 'splash';

  const owned = new Set(loadSave().owned);
  const prizes = WELL_PRIZES.filter(it => !owned.has(it.id));
  const win = prizes.length > 0 && (Math.random() < 0.2 || _wellPity >= 5);

  if (win) {
    const item = prizes[(Math.random() * prizes.length) | 0];
    owned.add(item.id);
    patchSave({ owned: [...owned] });
    // Wear it only if that slot is bare, so we never knock off a chosen look.
    const eq = getEquippedOutfit();
    if (item.slot === 'head' ? !eq.head : eq.body === 'cream') equipOutfit(item.slot, item.id);
    _wellPity = 0;
    _wellSplash = 3.4;
    spawnDust(0, heightAt(0, -4) + 0.7, -4, 22, 1.7);
    triggerFace('grin', 1.3);
    showBumpMsg('✨ Your wish came true! ✨',
      `The well bubbles up a gift — the ${item.name} ${item.emoji || '🎁'}! Find it in Chocky's Closet.`, 0, 'well');
  } else if (prizes.length === 0) {
    // Everything's already won: return the coin so the well never dead-ends.
    coinInventory++;
    updateCoinUI();
    _wellSplash = 2.4;
    showBumpMsg('✨ Sparkle! ✨', "You've wished for everything! The well returns your coin. 🪙", 0, 'well');
  } else {
    _wellSplash = 2.2;
    showBumpMsg('Sploosh! 🪙', 'Your coin sinks into the deep… keep wishing!', 0, 'well');
  }
}
addBumpZone(0, -4, 1.15, 'well',
  () => { nearWell = true; },
  sourceId => { nearWell = false; _wellSplash = 0; _wellState = null; hideBumpMsg(sourceId); });

// Paths radiating from spawn out to each landmark
buildPath([[0, 0], [-2.5, -2.5], [-5, -5]]);    // SW path
buildPath([[0, 0], [ 2.5, -2.5], [ 5, -5]]);    // SE path
buildPath([[0, 0], [-2.5,  2.5], [-5,  5]]);    // NW path
buildPath([[0, 0], [ 2.5,  2.5], [ 5,  5]]);    // NE path
buildPath([[0, 0], [0, -2], [0, -3.5]]);        // path to well

// Decorative fences around the perimeter — solid (block the path), 0.6 tall so
// a jump clears them. Corner accents only; the mid-side gaps stay walkable.
buildFence(-9, -9, -9, -2);     // west south
buildFence(-9,  9, -9,  2);     // west north
buildFence( 9, -9,  9, -2);     // east south
buildFence( 9,  9,  9,  2);     // east north
buildFence(-9, -9, -2, -9);     // south west
buildFence( 9, -9,  2, -9);     // south east
buildFence(-9,  9, -2,  9);     // north west
buildFence( 9,  9,  2,  9);     // north east

// Trees in the corners, away from paths
buildTree(-8.5,  3, 1.1);
buildTree(-7,    4.2, 0.9);
buildTree( 8.5,  3, 1.2);
buildTree( 7,    4.2, 0.85);
buildTree(-8.5, -3, 1.0);
buildTree( 8.5, -3, 1.0);
buildTree(-3,   8.5, 0.95);
buildTree( 3,   8.5, 1.1);
buildTree( 0,   8.7, 1.3);     // big tree at the north edge

// A handmade wooden trestle platform as an instanced chunk prop plus a
// standable flat-top collision AABB. Visual and AABB use the same KIT_KINDS
// dims whether the kit loaded or the fallback box renders, so what a kid
// lands on always matches what they see. Also on the obby ctx and behind the
// editor's prop palette (worldEdit places these through placeKitPlatform too).
function placeKitPlatform(kind, x, z, yaw = 0, floatTopY = null) {
  // Unknown kind (e.g. stale save/edit-layer data referencing a removed kind):
  // log and skip rather than crash the whole world build.
  if (!KIT_KINDS[kind]) {
    console.warn(`[world] placeKitPlatform: unknown kit kind "${kind}" — skipped`);
    return null;
  }
  // Size the standable AABB from the real model when the kit loaded, so the
  // landing surface tracks the visible platform; the fallback box otherwise.
  const dims = (kitReady() && kitSize(kind)) || KIT_KINDS[kind].fallback;
  // Floating pad: place by its SURFACE height (what the player stands on), so
  // callers position by the landing top, not the base. The prop compose honours
  // an explicit d.y over the sampled ground; the AABB top matches it exactly.
  if (floatTopY != null) {
    const baseY = floatTopY - dims.h;
    addProp(kind, { x, z, yaw, y: baseY });
    return addObstacle({
      minX: x - dims.w / 2, maxX: x + dims.w / 2,
      minY: baseY, maxY: floatTopY,
      minZ: z - dims.d / 2, maxZ: z + dims.d / 2,
    });
  }
  addProp(kind, { x, z, yaw });
  addKitPropAccents(kind, x, z, yaw);
  const gy = heightAt(x, z);
  // Full footprint (no inset): the top face is a landing target — an inset
  // would make the visible edge overhang a phantom drop.
  return addObstacle({
    minX: x - dims.w / 2, maxX: x + dims.w / 2,
    minY: gy, maxY: gy + dims.h,
    minZ: z - dims.d / 2, maxZ: z + dims.d / 2,
  });
}

function addKitPropAccents(kind, x, z, yaw = 0) {
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  const place = (ox, oz, type, d = {}) => addProp(type, { x: x + ox * cos - oz * sin, z: z + ox * sin + oz * cos, ...d });
  if (kind === 'haybale') {
    for (const [ox, oz, scale] of [[-0.36, 0.22, 0.48], [0.38, -0.1, 0.42], [0.05, -0.34, 0.36]]) {
      place(ox, oz, 'grassTuft', { scale, yaw: yaw + ox });
    }
  } else if (kind === 'bunny_hutch') {
    place(-0.42, 0.28, 'flowerStem', { h: 0.2 });
    place(-0.42, 0.28, 'flowerHead', { h: 0.2, s: 0.58, color: PALETTE.coral });
    place(0.42, -0.18, 'pathStone', { r: 0.13 });
  } else if (kind.startsWith('platform_')) {
    place(0.34, -0.32, 'pathStone', { r: 0.14 });
  }
}

// Platforming corner in the NORTH plaza area (away from spawn): a climb up
// wooden trestles — small (0.67) → mid (1.21) → tall (1.66) → back down.
// Neighbouring tops stay ≤0.55 apart, comfortably inside a single jump.
placeKitPlatform('platform_small', -2.5, 4.5, 0.2);
placeKitPlatform('platform_mid',   -1.5, 5.2, -0.1);
placeKitPlatform('platform_tall',   0,   5.8, 0);
placeKitPlatform('platform_mid',    1.5, 5.2, 0.15);
placeKitPlatform('platform_small',  2.5, 4.5, -0.2);

// North-meadow farm cluster (assetpack2): the Bunny Wrangler's spare hutch and a
// hay bale, beside the existing (deliberately empty) quest hutch at (4.6,11).
// placeKitPlatform doubles as the general "instanced kit prop + standable AABB"
// helper — a kid hopping onto the hutch/bale is harmless play.
placeKitPlatform('bunny_hutch', 7, 13, -0.5);
placeKitPlatform('haybale', 8.6, 12.4, 0.3);

// A bench by the well
addBox(-1.5, 0.2, -4,  0.9, 0.05, 0.25, PALETTE.woodDark);    // seat
addBox(-1.9, 0.1, -4,  0.06, 0.2, 0.25, PALETTE.woodDark);    // left leg
addBox(-1.1, 0.1, -4,  0.06, 0.2, 0.25, PALETTE.woodDark);    // right leg

// Signpost at spawn pointing visitors around — bump it for a friendly welcome.
const welcomeSignGroup = new THREE.Group();
welcomeSignGroup.position.set(1.0, 0, 0.5);
welcomeSignGroup.userData = { signId: 'welcome-sign' };
const welcomePost = addBox(0.8, 0.5, 0.5, 0.08, 1.0, 0.08, PALETTE.woodDark);     // post
welcomePost.position.x -= welcomeSignGroup.position.x;
welcomePost.position.z -= welcomeSignGroup.position.z;
welcomeSignGroup.add(welcomePost);
const welcomeBoard = addBox(1.2, 0.85, 0.5, 0.6, 0.2, 0.04, PALETTE.woodLight, false);  // sign board
welcomeBoard.position.x -= welcomeSignGroup.position.x;
welcomeBoard.position.z -= welcomeSignGroup.position.z;
welcomeSignGroup.add(welcomeBoard);
propsGroup.add(welcomeSignGroup);
signGroups.push({ group: welcomeSignGroup, name: 'WELCOME SIGN', id: 'welcome-sign' });
addVillageLandmark(1.0, 0.5, 1.8);
addBumpZone(1.0, 0.5, 0.9, 'welcome-sign', sourceId => {
  showBumpMsg('WELCOME TO CHOCABLOC!', 'Stay awhile… stay forever!', 4, sourceId);
});

// Signpost at the village's east exit: the way to Wordville.
buildSignpost(10.6, 1.6, [{ text: 'WORDVILLE', yaw: 0.6 }], 'SIGNPOST: WORDVILLE', 'signpost-wordville');
// Signpost at the village's west exit: the way out to the Grove oasis (the
// north side of the road — Pip's meadow signpost sits on the south side).
buildSignpost(-10.6, 1.6, [{ text: 'THE GROVE', yaw: -0.6 }], 'SIGNPOST: THE GROVE', 'signpost-grove');
// Signpost at the village's south exit: the way down to the Village Green (the
// picnic terminus — see src/world/villageDecor.js). Sits just outside the fence
// gap, beside the south road, so the south cardinal reads as purposeful too.
buildSignpost(1.6, -10.6, [{ text: 'VILLAGE GREEN', yaw: 0.1 }], 'SIGNPOST: VILLAGE GREEN', 'signpost-greensouth');
addProp('starAccent', { x: 0.55, z: -1.05, scale: 0.75, yaw: 0.25 });
addProp('starAccent', { x: -0.65, z: -2.05, scale: 0.6, yaw: -0.45 });

// ============= DISTANT SCENERY =============
// Paths winding OUT of the village, fading into the fog. Trees + bushes +
// rocks scattered in the 12-32 unit ring around the village.

function buildBush(x, z, scale = 1, berries = false) {
  addProp(berries ? 'berryBush' : 'bush', { x, z, scale });
}
function buildRock(x, z, scale = 1) {
  const colors = [PALETTE.stone, PALETTE.stoneLight, PALETTE.stoneDark, 0x9da18f];
  addProp('rock', { x, z, scale, yaw: Math.random() * Math.PI * 2, color: colors[(Math.random() * colors.length) | 0] });
}
// Tuft of tall grass — instanced, so a whole meadow of them adds one draw per
// chunk. Random yaw keeps clumps from looking stamped.
function buildGrassTuft(x, z, scale = 1) {
  addProp('grassTuft', { x, z, scale, yaw: Math.random() * Math.PI * 2 });
}
// Simple town props are all "drop one instanced record with a random yaw" —
// one factory instead of seven identical wrappers. Signature stays
// (x, z, scale?, yaw?) so every call site is unchanged.
const propBuilder = (kind) => (x, z, scale = 1, yaw = Math.random() * Math.PI * 2) =>
  addProp(kind, { x, z, scale, yaw });
const buildCrate = propBuilder('crate');
const buildBarrel = propBuilder('barrel');
const buildMailbox = propBuilder('mailbox');
const buildFlowerBox = propBuilder('flowerBox');
const buildTinyFlag = propBuilder('tinyFlag');
const buildMarketBasket = propBuilder('marketBasket');
const buildLilyPad = propBuilder('lilyPad');
const buildStarAccent = propBuilder('starAccent');

// Authored-feeling town clutter. These are still chunk props (one shared
// geometry/material per type per chunk), but the placement is deterministic and
// clustered around landmarks instead of sprayed randomly.
function buildTownPropCluster(cx, cz, items, yaw = 0) {
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  for (const item of items) {
    const [ox, oz, build, scale = 1, extraYaw = 0] = item;
    const x = cx + ox * cos - oz * sin;
    const z = cz + ox * sin + oz * cos;
    build(x, z, scale, yaw + extraYaw);
  }
}

function scatterTownProps() {
  // Door clusters sit to one side of each entrance trigger, leaving a clear
  // straight approach to the gameHuts[].entranceX/Z bump checks.
  const doorBuilders = [
    [buildCrate, buildTinyFlag],
    [buildBarrel, buildMailbox],
    [buildFlowerBox, buildMarketBasket],
  ];
  for (let i = 0; i < gameHuts.length; i++) {
    const h = gameHuts[i];
    const side = i % 2 === 0 ? -1 : 1;
    const yaw = h.x < 0 ? -0.25 : 0.25;
    const [primary, secondary] = doorBuilders[i % doorBuilders.length];
    buildTownPropCluster(h.entranceX, h.entranceZ, [
      [side * 1.15, 0.28, primary, 0.9, yaw],
      [side * 1.55, 0.55, secondary, 0.8, yaw + 0.45],
      [side * 1.35, 0.92, buildGrassTuft, 0.75, yaw + 1.2],
    ], 0);
  }

  // Path-edge clusters: offsets are perpendicular to the line of travel, so
  // the path stones stay readable and walkable down the centre.
  const pathEdgeClusters = [
    { x: -2.7, z: -2.0, yaw: -Math.PI / 4, side: 1,  build: buildMarketBasket },
    { x:  2.6, z: -2.1, yaw:  Math.PI / 4, side: -1, build: buildCrate },
    { x: -2.7, z:  2.2, yaw:  Math.PI / 4, side: 1,  build: buildBarrel },
    { x:  2.7, z:  2.2, yaw: -Math.PI / 4, side: -1, build: buildFlowerBox },
    { x:  0.9, z: -2.5, yaw:  0,           side: 1,  build: buildTinyFlag },
  ];
  for (const c of pathEdgeClusters) {
    const ox = -Math.sin(c.yaw) * c.side;
    const oz =  Math.cos(c.yaw) * c.side;
    buildTownPropCluster(c.x + ox * 0.9, c.z + oz * 0.9, [
      [0, 0, c.build, 0.85, c.yaw],
      [0.38, 0.18, buildGrassTuft, 0.7, c.yaw + 0.7],
      [-0.34, 0.22, buildFlowerBox, 0.65, c.yaw - 0.4],
    ], c.yaw);
  }

  // Little arrangements around the well, welcome sign, and wayfinding posts.
  buildTownPropCluster(0, -4, [
    [1.25, -0.95, buildBarrel, 0.85, 0.3],
    [1.65, -0.7, buildCrate, 0.75, -0.4],
    [-1.15, 0.95, buildMarketBasket, 0.85, 0.6],
  ], 0);
  buildTownPropCluster(1.0, 0.5, [
    [0.75, 0.45, buildMailbox, 0.8, -0.7],
    [1.05, 0.05, buildTinyFlag, 0.85, 0.2],
  ], 0);
  buildTownPropCluster(10.6, 1.6, [
    [-0.9, 0.7, buildCrate, 0.75, 0.4],
    [-1.2, 0.45, buildMarketBasket, 0.75, -0.2],
  ], 0.35);
  buildTownPropCluster(-10.6, 1.6, [
    [0.9, 0.7, buildBarrel, 0.75, -0.5],
    [1.2, 0.45, buildTinyFlag, 0.8, 0.4],
  ], -0.35);
}

// A little bed of wildflowers: a scatter of thin green stems topped with
// bright blossoms. Built via two InstancedMesh pools so a dozen beds add only
// two draw calls. Each bed is also registered as a butterfly habitat so the
// critters actually congregate where the flowers are.
const FLOWER_COLORS = ACCENTS;
function buildFlowerPatch(cx, cz, count = 7, radius = 0.9, colors = FLOWER_COLORS) {
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = Math.sqrt(Math.random()) * radius;   // sqrt → even area fill
    const x = cx + Math.cos(a) * d;
    const z = cz + Math.sin(a) * d;
    const h = 0.18 + Math.random() * 0.22;          // stem height
    const col = colors[(Math.random() * colors.length) | 0];
    addProp('flowerStem', { x, z, h });
    addProp('flowerHead', { x, z, h, color: col, s: 0.7 + Math.random() * 0.7 });
  }
  // A couple of grass tufts around the bed's edge make it read as a lush
  // meadow patch rather than flowers on bare ground.
  const tufts = LOW ? 2 : 3;
  for (let i = 0; i < tufts; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = radius * (0.6 + Math.random() * 0.6);
    buildGrassTuft(cx + Math.cos(a) * d, cz + Math.sin(a) * d, 0.7 + Math.random() * 0.6);
  }
  flowerPatches.push({ x: cx, z: cz, r: radius + 0.6 });
}

// Flower beds tucked beside huts, the well and the plaza — bright spots of
// colour that give the butterflies somewhere to belong.
buildFlowerPatch(-4.2, -3.4, 9, 1.0);
buildFlowerPatch( 3.8, -3.6, 8, 0.9);
buildFlowerPatch(-3.6,  3.8, 9, 1.0);
buildFlowerPatch( 4.0,  3.6, 8, 0.9);
buildFlowerPatch(-7.4, -1.8, 7, 0.8);
buildFlowerPatch( 7.4,  1.8, 7, 0.8);
buildFlowerPatch( 1.6, -3.2, 6, 0.7);   // by the well
buildFlowerPatch(-1.8,  2.4, 6, 0.7);   // edge of plaza
scatterTownProps();

// Four roads leading out of the village. Each extends from the village
// fence to ~28 units, where the fog has fully eaten them.
buildPath([[ 0, -9.5],  [ 0, -14], [ 0, -20], [ 0, -27]]);   // S road
buildPath([[ 0,  9.5],  [ 0,  14], [ 0,  20], [ 0,  27]]);   // N road
buildPath([[-9.5, 0],   [-14, 0],  [-20, 0],  [-27, 0]]);    // W road
buildPath([[ 9.5, 0],   [ 14, 0],  [ 20, 0],  [ 27, 0]]);    // E road
// Two diagonal roads continuing past the corner trees
buildPath([[-9, -9], [-13, -13], [-18, -18], [-25, -25]]);
buildPath([[ 9,  9], [ 13,  13], [ 18,  18], [ 25,  25]]);

// Trees lining the cardinal roads (rough line of trees on each side)
function lineTrees(x1, z1, x2, z2, count, sideOffset, scaleRange = [0.9, 1.4]) {
  for (let i = 0; i <= count; i++) {
    const t = i / count;
    const px = x1 + (x2 - x1) * t;
    const pz = z1 + (z2 - z1) * t;
    // Perpendicular offset
    const dx = x2 - x1, dz = z2 - z1;
    const len = Math.hypot(dx, dz) || 1;
    const ox = -dz / len * sideOffset;
    const oz =  dx / len * sideOffset;
    // Pseudo-random jitter so they don't look ruler-straight
    const jx = ((i * 73) % 17 - 8) * 0.1;
    const jz = ((i * 41) % 13 - 6) * 0.1;
    const sc = scaleRange[0] + (i % 7) / 7 * (scaleRange[1] - scaleRange[0]);
    buildTree(px + ox + jx, pz + oz + jz, sc);
  }
}
lineTrees(0, -12, 0, -27, 5,  2.2);   // S road, west side
lineTrees(0, -12, 0, -27, 5, -2.2);   // S road, east side
lineTrees(0,  12, 0,  27, 5,  2.2);   // N road, west side
lineTrees(0,  12, 0,  27, 5, -2.2);   // N road, east side
lineTrees(-12, 0, -27, 0, 5,  2.2);   // W road, north side
lineTrees(-12, 0, -27, 0, 5, -2.2);   // W road, south side
lineTrees( 12, 0,  27, 0, 5,  2.2);   // E road, north side
lineTrees( 12, 0,  27, 0, 5, -2.2);   // E road, south side
// Frame the two diagonal roads (previously bare, so they faded into random
// scatter) and carry the south tree line past the Village Green, so every road
// reads as leading somewhere.
lineTrees(-13, -13, -25, -25, 4,  2.0);   // SW diagonal
lineTrees(-13, -13, -25, -25, 4, -2.0);
lineTrees( 13,  13,  25,  25, 4,  2.0);   // NE diagonal
lineTrees( 13,  13,  25,  25, 4, -2.0);
lineTrees( 0, -27, 0, -33, 2,  2.2);      // south road, past the green
lineTrees( 0, -27, 0, -33, 2, -2.2);

// Icons-first road edging: tiny, instanced accent breadcrumbs help young
// players read "this road goes somewhere" before they can parse every sign.
function roadAccentTrail(points, accent, seedOffset = 0) {
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, z1] = points[i], [x2, z2] = points[i + 1];
    const dx = x2 - x1, dz = z2 - z1;
    const len = Math.hypot(dx, dz) || 1;
    const ox = -dz / len, oz = dx / len;
    const steps = Math.max(2, Math.floor(len / 2.6));
    for (let s = 1; s <= steps; s++) {
      const t = s / (steps + 1);
      const side = ((s + seedOffset) % 2) ? 1 : -1;
      const x = x1 + dx * t + ox * side * 1.45;
      const z = z1 + dz * t + oz * side * 1.45;
      if (accent === 'word') buildFlowerPatch(x, z, 0.36, 3);
      else if (accent === 'grove') buildGrassTuft(x, z, 0.78);
      else if (accent === 'obby') buildRock(x, z, 0.34);
      else buildMushroomCluster(x, z, 2, Math.random);
    }
  }
}
roadAccentTrail([[ 9.5, 0], [14, 0], [20, 0], [27, 0]], 'word', 0);
roadAccentTrail([[-9.5, 0], [-14, 0], [-20, 0], [-27, 0]], 'grove', 1);
roadAccentTrail([[0, 9.5], [0, 14], [0, 20], [0, 27]], 'obby', 2);
roadAccentTrail([[0, -9.5], [0, -14], [0, -20], [0, -27], [0, -33]], 'green', 3);

// Scattered detail in the 12-32 unit ring (between roads). Seeded PRNG so
// the layout is the same every page load.
let _seed = 1337;
const rnd = () => { _seed = (_seed * 16807) % 2147483647; return _seed / 2147483647; };
function nearAnyRoad(x, z) {
  // Avoid placing scenery on/very near the cardinal roads (so paths stay clear)
  return Math.abs(x) < 1.6 || Math.abs(z) < 1.6 ||
         Math.abs(Math.abs(x) - Math.abs(z)) < 1.6;   // diagonals
}
// Slightly thinned (was 50) now that the diagonals are tree-lined and the ring
// carries three authored landmarks (src/world/villageDecor.js) to anchor the eye.
for (let i = 0; i < 40; i++) {
  const angle = rnd() * Math.PI * 2;
  const dist = 13 + rnd() * 20;          // 13 → 33 units
  const x = Math.cos(angle) * dist;
  const z = Math.sin(angle) * dist;
  if (nearAnyRoad(x, z)) continue;
  const kind = rnd();
  if (kind < 0.55)      buildTree(x, z, 0.7 + rnd() * 0.9);
  else if (kind < 0.85) buildBush(x, z, 0.8 + rnd() * 0.7);
  else                  buildRock(x, z, 0.7 + rnd() * 0.8);
}

// Mushroom crops in the tree shade (seeded like the rest of the scatter)
for (let i = 0; i < 11; i++) {
  const angle = rnd() * Math.PI * 2;
  const dist = 12 + rnd() * 18;
  const x = Math.cos(angle) * dist;
  const z = Math.sin(angle) * dist;
  if (nearAnyRoad(x, z)) continue;
  buildMushroomCluster(x, z, 2 + ((rnd() * 3) | 0), rnd);
}

// Clumps of tall grass scattered through the meadow ring — small clusters so
// the ground feels lived-in rather than mown. Instanced: one draw per chunk.
const GRASS_CLUMPS = LOW ? 32 : 52;
for (let i = 0; i < GRASS_CLUMPS; i++) {
  const angle = rnd() * Math.PI * 2;
  const dist = 11 + rnd() * 22;
  const x = Math.cos(angle) * dist;
  const z = Math.sin(angle) * dist;
  if (nearAnyRoad(x, z)) continue;
  const clump = 2 + ((rnd() * 3) | 0);
  for (let k = 0; k < clump; k++) {
    buildGrassTuft(x + (rnd() - 0.5) * 0.9, z + (rnd() - 0.5) * 0.9, 0.7 + rnd() * 0.7);
  }
}

// A few BIG landmark trees at the very edges of visibility
buildTree(  0,  32, 2.4);
buildTree(  0, -32, 2.4);
buildTree( 32,   0, 2.4);
buildTree(-32,   0, 2.4);
buildTree( 24,  24, 2.0);
buildTree(-24, -24, 2.2);



// Smoke particles (separate from dust — wispier and slower). Pooled like the
// dust so the chimneys never allocate/dispose a Material every half-second.
const smokeGeo = new THREE.SphereGeometry(0.12, 6, 4);
const SMOKE_POOL = poolScale(LOW ? 8 : 28);
const smokePool = [];
let _smokeIdx = 0;
for (let i = 0; i < SMOKE_POOL; i++) {
  const mat = new THREE.MeshBasicMaterial({ color: 0xcccccc, transparent: true, opacity: 0, fog: true });
  const p = new THREE.Mesh(smokeGeo, mat);
  p.visible = false;
  p.userData = { vy: 0, life: 0, max: 3.0, scale0: 1, sx: 1, sy: 1, sz: 1, spin: 0 };
  scene.add(p);
  smokePool.push(p);
}
function emitSmoke(emitter) {
  const p = smokePool[_smokeIdx];
  _smokeIdx = (_smokeIdx + 1) % SMOKE_POOL;
  const d = p.userData;
  p.position.set(
    emitter.x + (Math.random() - 0.5) * 0.1,
    emitter.y,
    emitter.z + (Math.random() - 0.5) * 0.1
  );
  d.vy = 0.4 + Math.random() * 0.2;
  d.life = 3.0; d.max = 3.0;
  d.scale0 = 0.5 + Math.random() * 0.3;
  d.sx = 0.75 + Math.random() * 0.65;
  d.sy = 0.85 + Math.random() * 0.45;
  d.sz = 0.75 + Math.random() * 0.65;
  d.spin = (Math.random() - 0.5) * 0.9;
  p.rotation.y = Math.random() * Math.PI * 2;
  p.scale.set(d.scale0 * d.sx, d.scale0 * d.sy, d.scale0 * d.sz);
  p.material.opacity = 0.55;
  p.visible = true;
}
function updateSmoke(dt) {
  for (const e of smokeEmitters) {
    // Chimneys past the fog wall don't puff. The particle pool is shared
    // (LOW: 8 puffs total) — without this gate, far-off zone chimneys
    // silently starve the chimneys the player is actually looking at.
    if (gameMode) {
      const dx = e.x - player.pos.x, dz = e.z - player.pos.z;
      if (dx * dx + dz * dz > 1600) continue;   // > 40 units — fog-hidden
    }
    e.t -= dt;
    if (e.t <= 0) { emitSmoke(e); e.t = 0.5 + Math.random() * 0.4; }
  }
  for (let i = 0; i < smokePool.length; i++) {
    const p = smokePool[i];
    if (!p.visible) continue;
    const d = p.userData;
    d.life -= dt;
    if (d.life <= 0) { p.visible = false; p.material.opacity = 0; continue; }
    p.position.y += d.vy * dt;
    p.position.x += Math.sin(d.life * 1.5) * 0.04 * dt;
    p.rotation.y += d.spin * dt;
    const t = d.life / d.max;
    const puff = d.scale0 * (1 + (1 - t) * 2.5);
    p.material.opacity = 0.55 * t;
    p.scale.set(puff * d.sx, puff * d.sy * (1 + (1 - t) * 0.35), puff * d.sz);
  }
}

// Spinning gold coins. Players hunt for them — one above each game hut roof
// plus one on the tall central staircase block.
const coins = [];
let coinCheerCd = 0;   // cooldown so back-to-back pickups don't restart the cheer
// Shared geometry/material across all coins (saves draw calls and memory).
// Low tier uses cheaper lighting and a single mesh per coin; mid/high keep the
// shiny MeshStandardMaterial + highlight dot look.
const coinGeo = new THREE.CylinderGeometry(0.22, 0.22, 0.05, LOW ? 10 : 16);
const coinMat = LOW
  ? new THREE.MeshLambertMaterial({ color: 0xffd34a })
  : new THREE.MeshStandardMaterial({ color: 0xffd34a, emissive: 0x553300, emissiveIntensity: 0.6, metalness: 0.85, roughness: 0.25 });
const coinDotGeo = LOW ? null : new THREE.SphereGeometry(0.04, 6, 4);
const coinDotMat = LOW
  ? new THREE.MeshBasicMaterial({ color: 0xffeb88, fog: true })
  : new THREE.MeshStandardMaterial({ color: 0xffeb88, emissive: 0xaa6600, emissiveIntensity: 1 });

// once=true makes a one-shot coin (quest treasure): collected once, gone for
// good — no respawn, no reset when play mode restarts.
function addCoin(x, y, z, once = false) {
  const group = new THREE.Group();
  group.position.set(x, y, z);
  const mesh = new THREE.Mesh(coinGeo, coinMat);
  mesh.rotation.z = Math.PI / 2;
  mesh.castShadow = true;
  group.add(mesh);
  if (coinDotGeo) {
    const dot = new THREE.Mesh(coinDotGeo, coinDotMat);
    dot.position.x = 0.001;
    group.add(dot);
  }
  markDynamic(group);   // updateCoin spins/bobs it
  propsGroup.add(group);
  coins.push({ group, baseY: y, collected: false, respawnTimer: 0, once });
  return group;
}

// Terrain-following placement helpers — the ground is heightAt(x, z), never
// y=0, so anything placed by map coordinates samples the heightfield.
function addCoinAt(x, z, lift = 1.0) { addCoin(x, heightAt(x, z) + lift, z); }
function addCoinRing(cx, cz, r, n, lift = 1.0, a0 = 0) {
  for (let i = 0; i < n; i++) {
    const a = a0 + (i * Math.PI * 2) / n;
    addCoinAt(cx + Math.cos(a) * r, cz + Math.sin(a) * r, lift);
  }
}
function addCoinLine(points, lift = 1.0) {
  for (const [x, z] of points) addCoinAt(x, z, lift);
}

// Coin on the tall central staircase block (the "easy" one)
addCoin(0, 0.7 + 1.4 + 0.5, 5.8);
// One coin floating above each game hut roof — collected by jumping
// (hut roof coins are placed after the ZONES block below — zones add huts too)

function updateCoin(dt, t) {
  if (coinCheerCd > 0) coinCheerCd -= dt;
  for (let i = 0; i < coins.length; i++) {
    const c = coins[i];
    if (!c.collected) {
      c.group.rotation.y += dt * 3.5;
      c.group.position.y = c.baseY + Math.sin(t * 2.5 + i * 0.7) * 0.08;
      // Generous collection radius so roof coins are reachable from a good jump
      const dx = player.pos.x - c.group.position.x;
      const dz = player.pos.z - c.group.position.z;
      const dy = (player.pos.y + 0.8) - c.group.position.y;   // player center is ~0.8 above feet
      if (Math.hypot(dx, dz) < 0.7 && Math.abs(dy) < 1.2) {
        c.collected = true;
        c.respawnTimer = c.once ? Infinity : 8;
        if (c.once) propsGroup.remove(c.group);
        coinInventory++;
        coinTotalCollected++;
        updateCoinUI();
        spawnDust(c.group.position.x, c.group.position.y, c.group.position.z, 20, 1.5);
        triggerFace('grin', 0.7);   // delighted by the catch
        // A quick baked Cheer when he grabs one with his feet on the ground —
        // skipped mid-jump (roof coins) so it never fights the jump, and while a
        // wolf is on him. The emote is idle-biased, so it ends as he moves on.
        if (!wolfChasing && coinCheerCd <= 0 && triggerEmote('Cheer')) coinCheerCd = 2.5;
        c.group.visible = false;
      }
    } else {
      c.respawnTimer -= dt;
      if (c.respawnTimer <= 0) {
        c.collected = false;
        c.group.visible = true;
      }
    }
  }
}

// The wallet persists (localStorage via game/save.js) so store purchases mean
// something across reloads and the game.html round-trip.
const _wallet = loadSave();
let coinInventory = _wallet.coins;
let coinTotalCollected = _wallet.totalCoins;
function updateCoinUI() {
  const el = document.getElementById('coinScore');
  if (el) el.textContent = `🪙 ${coinInventory} · total ${coinTotalCollected}`;
  patchSave({ coins: coinInventory, totalCoins: coinTotalCollected });
}

// ============= BIRDS + BUTTERFLIES =============
// Lightweight sprite critters that add life to the village. Sprites always
// face the camera so they look right from any angle — no shader work needed.

function makeBirdTexture() {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 32;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 64, 32);
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  // Soaring V shape
  ctx.moveTo(4, 18);
  ctx.quadraticCurveTo(20, 4, 32, 14);
  ctx.quadraticCurveTo(44, 4, 60, 18);
  ctx.quadraticCurveTo(44, 12, 32, 16);
  ctx.quadraticCurveTo(20, 12, 4, 18);
  ctx.closePath();
  ctx.fill();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeButterflyTexture(c1, c2) {
  return cachedTexture(`butterfly:${c1}:${c2}`, () => {
  const c = document.createElement('canvas');
  c.width = 48; c.height = 48;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 48, 48);
  const cx = 24;
  const wing = (px, py, rx, ry, rot) => {
    ctx.beginPath(); ctx.ellipse(px, py, rx, ry, rot, 0, Math.PI * 2); ctx.fill();
  };
  // Two pairs of wings (upper larger, lower smaller), mirrored about the body
  ctx.fillStyle = c1;
  wing(cx - 9, 18, 9, 12, -0.3);
  wing(cx + 9, 18, 9, 12,  0.3);
  wing(cx - 7, 31, 7,  9,  0.35);
  wing(cx + 7, 31, 7,  9, -0.35);
  // Lighter pattern spots on each wing
  ctx.fillStyle = c2;
  wing(cx - 10, 16, 3.5, 5, -0.3);
  wing(cx + 10, 16, 3.5, 5,  0.3);
  wing(cx - 7, 31, 2.5, 3.5, 0);
  wing(cx + 7, 31, 2.5, 3.5, 0);
  // Body + head
  ctx.fillStyle = '#2c1d12';
  ctx.beginPath(); ctx.ellipse(cx, 26, 1.8, 11, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx, 14, 2.2, 0, Math.PI * 2); ctx.fill();
  // Antennae
  ctx.strokeStyle = '#2c1d12'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cx, 13); ctx.quadraticCurveTo(cx - 4, 7, cx - 6, 6); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx, 13); ctx.quadraticCurveTo(cx + 4, 7, cx + 6, 6); ctx.stroke();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
  });
}

const birdTex = cachedTexture('bird', makeBirdTexture);
const birds = [];
for (let i = 0; i < 3; i++) {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: birdTex, transparent: true, fog: true }));
  sprite.scale.set(1.4, 0.7, 1);
  markDynamic(sprite);   // updateBirds flies it
  propsGroup.add(sprite);
  birds.push({
    sprite,
    cx: (Math.random() - 0.5) * 12,
    cz: (Math.random() - 0.5) * 12,
    radius: 7 + Math.random() * 5,
    altitude: 8 + Math.random() * 3,
    speed: 0.4 + Math.random() * 0.3,
    phase: Math.random() * Math.PI * 2,
    flapPhase: Math.random() * Math.PI * 2,
  });
}

const butterflyPalettes = [
  ['#ff9a3a', '#ffd27a'],
  ['#f4d83a', '#fff2a0'],
  ['#5aa6ff', '#bfe0ff'],
  ['#ff7aaa', '#ffd0e0'],
  ['#9a7aff', '#d8c8ff'],
  ['#ff6b6b', '#ffc2c2'],
  ['#ffffff', '#cfe8ff'],
];
// Butterflies are an OCCASIONAL accent, not a permanent swarm. We keep a small
// pool of sprites that sit dormant ('idle') most of the time. A spawn director
// (updateButterflies) wakes one up every so often: it flutters in (fades up),
// wanders a flower bed for a while, then flutters away (rises + fades out) and
// returns to the pool. Only a few are ever on-screen at once, so they read as a
// delightful surprise rather than overpowering the scene — and the low sprite
// count keeps the frame budget comfortably inside 60fps on Chromebooks.
const butterflies = [];
const BFLY_POOL = poolScale(LOW ? 2 : 5);  // max simultaneously on screen
function butterflyHome() {
  if (flowerPatches.length) return flowerPatches[(Math.random() * flowerPatches.length) | 0];
  return { x: 0, z: 0, r: 6 };
}
for (let i = 0; i < BFLY_POOL; i++) {
  const pal = butterflyPalettes[i % butterflyPalettes.length];
  const tex = makeButterflyTexture(pal[0], pal[1]);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, fog: true, opacity: 0 });
  const sprite = new THREE.Sprite(mat);
  const bs = 0.32 + Math.random() * 0.16;       // per-butterfly base size
  sprite.scale.set(bs, bs, 1);
  sprite.visible = false;
  markDynamic(sprite);   // updateButterflies flutters it
  propsGroup.add(sprite);
  butterflies.push({
    sprite, mat, bs,
    home: butterflyHome(),
    x: 0, y: 1, z: 0,
    vx: 0, vy: 0, vz: 0,                  // smoothed velocity (floaty steering)
    tx: 0, ty: 1, tz: 0,                  // target
    speed: 0.7 + Math.random() * 0.7,
    flutter: 5 + Math.random() * 4,       // lateral weave frequency
    flapPhase: Math.random() * Math.PI * 2,
    bank: 0,
    opacity: 0,                           // fades 0..1 on arrive, back to 0 on leave
    mode: 'idle',                         // 'idle' | 'arrive' | 'cruise' | 'rest' | 'leave'
    timer: 0,
    life: 0,                              // seconds left before it flutters away
  });
}
// Spawn director: stagger the first arrival a little, then trickle them in with
// gaps so the meadow is often empty and a butterfly is a small event.
let butterflySpawnTimer = 2 + Math.random() * 4;
function spawnButterfly() {
  const b = butterflies.find(x => x.mode === 'idle');
  if (!b) return;
  b.home = butterflyHome();
  // Flutter in from just outside the bed at a gentle height.
  const a = Math.random() * Math.PI * 2;
  const r = b.home.r + 0.8 + Math.random() * 1.2;
  b.x = b.home.x + Math.cos(a) * r;
  b.z = b.home.z + Math.sin(a) * r;
  b.y = 1.2 + Math.random() * 1.0;
  b.vx = b.vy = b.vz = 0;
  b.bank = 0;
  b.opacity = 0;
  b.mode = 'arrive';
  b.life = 9 + Math.random() * 9;        // visit lasts ~9–18s, then it leaves
  b.mat.opacity = 0;
  b.sprite.visible = true;
  butterflyNewTarget(b);
}

function updateBirds(dt, t) {
  for (let i = 0; i < birds.length; i++) {
    const b = birds[i];
    const angle = b.phase + t * b.speed;
    b.sprite.position.set(
      b.cx + Math.cos(angle) * b.radius,
      b.altitude + Math.sin(t * 0.5 + b.phase) * 0.4,
      b.cz + Math.sin(angle) * b.radius
    );
    // Wing flap = quick Y squash
    b.sprite.scale.y = 0.7 * (0.6 + Math.abs(Math.sin(t * 9 + b.flapPhase)) * 0.7);
  }
}

// Pick the next place a butterfly wants to be. Mostly within its home bed, but
// every so often it migrates to a different bed (so the population reshuffles).
function butterflyNewTarget(b) {
  if (flowerPatches.length && Math.random() < 0.2) {
    b.home = flowerPatches[(Math.random() * flowerPatches.length) | 0];
  }
  const a = Math.random() * Math.PI * 2;
  const d = Math.random() * b.home.r;
  b.tx = b.home.x + Math.cos(a) * d;
  b.tz = b.home.z + Math.sin(a) * d;
  // Mostly low cruising near the flowers; occasionally a high, looping flit.
  b.ty = (Math.random() < 0.25) ? 1.6 + Math.random() * 1.1
                                : 0.5 + Math.random() * 0.7;
}

function updateButterflies(dt, t) {
  // ---- Spawn director: occasionally wake a dormant butterfly. We keep the
  // active count low and leave gaps so the meadow is frequently empty.
  butterflySpawnTimer -= dt;
  if (butterflySpawnTimer <= 0) {
    const active = butterflies.reduce((n, b) => n + (b.mode !== 'idle' ? 1 : 0), 0);
    // Soft cap below the pool size most of the time so it never looks swarmy.
    const softCap = LOW ? 2 : 3;
    if (active < softCap) spawnButterfly();
    // Longer gaps when several are already out; shorter when the meadow is bare.
    butterflySpawnTimer = (active <= 0 ? 4 : 8) + Math.random() * 10;
  }

  for (let i = 0; i < butterflies.length; i++) {
    const b = butterflies[i];
    if (b.mode === 'idle') continue;
    b.timer -= dt;
    b.life -= dt;

    // Fade opacity toward the target for the current phase (in on arrive, out
    // on leave, fully visible while it's around).
    const targetOpacity = (b.mode === 'leave') ? 0 : 1;
    b.opacity += (targetOpacity - b.opacity) * Math.min(1, dt * 2.2);
    b.mat.opacity = b.opacity;

    // Once its visit is up, transition to leaving: pick a faraway, high exit
    // point so it flutters up and off rather than vanishing in place.
    if (b.mode !== 'leave' && b.life <= 0) {
      b.mode = 'leave';
      const a = Math.random() * Math.PI * 2;
      b.tx = b.home.x + Math.cos(a) * (b.home.r + 6);
      b.tz = b.home.z + Math.sin(a) * (b.home.r + 6);
      b.ty = 2.4 + Math.random() * 1.6;
      b.timer = 6;
    }
    // Fully faded out and gone — return to the pool.
    if (b.mode === 'leave' && b.opacity < 0.02) {
      b.mode = 'idle';
      b.sprite.visible = false;
      b.mat.opacity = 0;
      continue;
    }

    // ---- RESTING: perched on a blossom, wings opening and closing slowly ----
    if (b.mode === 'rest') {
      b.vx *= 0.8; b.vz *= 0.8;
      b.x += b.vx * dt; b.z += b.vz * dt;
      b.y += (b.ty - b.y) * Math.min(1, dt * 4);
      const fold = 0.22 + Math.abs(Math.sin(t * 2.0 + b.flapPhase)) * 0.4;
      b.sprite.scale.x = b.bs * fold;
      b.sprite.scale.y = b.bs;
      b.mat.rotation = 0;
      b.sprite.position.set(b.x, b.y, b.z);
      if (b.timer <= 0) { b.mode = 'cruise'; butterflyNewTarget(b); b.timer = 2 + Math.random() * 3; }
      continue;
    }

    // ---- CRUISING: floaty steering toward the target with a weaving path ----
    const dx = b.tx - b.x, dz = b.tz - b.z;
    const dist = Math.hypot(dx, dz) || 1e-4;
    const nx = dx / dist, nz = dz / dist;
    // A perpendicular weave makes the path flutter instead of arrowing straight.
    const weave = Math.sin(t * b.flutter + b.flapPhase) * 0.85;
    const desVx = (nx - nz * weave) * b.speed;
    const desVz = (nz + nx * weave) * b.speed;
    const desVy = (b.ty - b.y) * 1.2 + Math.sin(t * 3 + b.flapPhase) * 0.28;
    // Smooth toward the desired velocity — inertia gives lazy, looping turns.
    const k = Math.min(1, dt * 3.2);
    const prevVx = b.vx;
    b.vx += (desVx - b.vx) * k;
    b.vy += (desVy - b.vy) * k;
    b.vz += (desVz - b.vz) * k;
    b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
    if (b.y < 0.12) b.y = 0.12;

    // Bank into turns by tilting the sprite (uses change in heading).
    const targetBank = Math.max(-0.7, Math.min(0.7, (b.vx - prevVx) * 6));
    b.bank += (targetBank - b.bank) * Math.min(1, dt * 6);
    b.mat.rotation = b.bank;

    // Wing flap: quick X-squash, faster the harder it's flying.
    const flapSpeed = 16 + Math.hypot(b.vx, b.vz) * 6;
    b.sprite.scale.x = b.bs * (0.32 + Math.abs(Math.sin(t * flapSpeed + b.flapPhase)) * 0.78);
    b.sprite.scale.y = b.bs;
    b.sprite.position.set(b.x, b.y, b.z);

    // Leaving: just keep flying toward the exit; the opacity fade despawns it.
    if (b.mode === 'leave') continue;

    // Arriving: once it reaches the bed, it's a normal resident from here on.
    if (b.mode === 'arrive' && dist < 0.6) { b.mode = 'cruise'; b.timer = 1.5 + Math.random() * 2; }

    // Reached the target (or timed out): rest on a flower or pick a new spot.
    if (dist < 0.4 || b.timer <= 0) {
      if (b.ty < 1.1 && Math.random() < 0.45) {
        b.mode = 'rest';
        b.ty = 0.16 + Math.random() * 0.14;     // settle to blossom height
        b.timer = 1.6 + Math.random() * 2.6;
      } else {
        butterflyNewTarget(b);
        b.timer = 2.5 + Math.random() * 3;
      }
    }
  }
}

// ---- Dragonflies skimming the water (day) ----
// A small pool of sprites that patrol the map's water bodies (the south brook,
// the SW fishing hole and the Grove oasis), so the ponds read as alive. Same
// cheap sprite discipline as the butterflies: a tiny pool, fog-aware, no
// per-frame allocation. They skim a lazy figure-eight just over the surface.
const WATER_POINTS = [{ x: 0, z: -15.2 }, { x: -22, z: -20 }, { x: -44, z: 7 }];
function makeDragonflyTexture() {
  return cachedTexture('dragonfly', () => {
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d');
    g.clearRect(0, 0, 64, 64);
    // Four translucent wings.
    g.fillStyle = 'rgba(180,225,235,0.55)';
    for (const [wx, wy] of [[22, 26], [42, 26], [24, 38], [40, 38]]) {
      g.beginPath(); g.ellipse(wx, wy, 12, 5, wx < 32 ? -0.5 : 0.5, 0, Math.PI * 2); g.fill();
    }
    // Slim body.
    g.fillStyle = '#4a8a6a';
    g.fillRect(30, 18, 4, 30);
    g.fillStyle = '#6ab0d0';
    g.fillRect(30, 18, 4, 8);
    return new THREE.CanvasTexture(c);
  });
}
const dragonflies = [];
const DFLY_POOL = poolScale(LOW ? 1 : 3);
for (let i = 0; i < DFLY_POOL; i++) {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: makeDragonflyTexture(), transparent: true, opacity: 0, depthWrite: false, fog: true,
  }));
  const s = 0.42 + Math.random() * 0.14;
  sprite.scale.set(s, s, 1);
  sprite.visible = false;
  markDynamic(sprite);   // updateDragonflies orbits it
  propsGroup.add(sprite);
  dragonflies.push({
    sprite, home: WATER_POINTS[i % WATER_POINTS.length],
    phase: Math.random() * Math.PI * 2, speed: 0.5 + Math.random() * 0.4,
    r: 1.3 + Math.random() * 0.8, opacity: 0, switchT: 8 + Math.random() * 8,
  });
}
function updateDragonflies(dt, t) {
  for (let i = 0; i < dragonflies.length; i++) {
    const d = dragonflies[i];
    // Fade out at night (they rest); fade in by day, but only when the player is
    // near a water body so distant ponds don't animate for nothing.
    const near = Math.hypot(player.pos.x - d.home.x, player.pos.z - d.home.z) < 34;
    const wantVisible = near && skyNightFactor < 0.4;
    d.opacity += ((wantVisible ? 1 : 0) - d.opacity) * Math.min(1, dt * 2);
    d.sprite.material.opacity = d.opacity * 0.9;
    d.sprite.visible = d.opacity > 0.02;
    if (!d.sprite.visible) {
      // Occasionally migrate to another pond while off-screen.
      d.switchT -= dt;
      if (d.switchT <= 0) { d.home = WATER_POINTS[(Math.random() * WATER_POINTS.length) | 0]; d.switchT = 8 + Math.random() * 8; }
      continue;
    }
    // Lazy figure-eight skim just above the surface.
    const a = t * d.speed + d.phase;
    const x = d.home.x + Math.sin(a) * d.r;
    const z = d.home.z + Math.sin(a * 2) * d.r * 0.6;
    const y = heightAt(x, z) + 0.55 + Math.sin(a * 3) * 0.12;
    d.sprite.position.set(x, y, z);
    d.sprite.material.rotation = Math.cos(a) * 0.4;
  }
}

// ============= AMBIENT LIGHT & ATMOSPHERE =============
// Drifting pollen by day, fireflies + glowing lanterns by night, a sun/moon
// disc and a star field — all faded in and out by the day-night cycle below.
// Everything lives under propsGroup so it only shows with the village (play
// mode), and the counts scale down on the low (Chromebook) tier.

// Soft round glow, reused for motes, fireflies, lantern halos and celestials.
function makeGlowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0,   'rgba(255,255,255,1)');
  g.addColorStop(0.3, 'rgba(255,255,255,0.7)');
  g.addColorStop(1,   'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const glowTex = cachedTexture('glow', makeGlowTexture);

// Soft cloudy puff texture for ground mist — irregular wisps rather than the
// clean round glow, so banks of fog read as drifting vapour.
function makeMistTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d');
  // Stack a handful of offset radial blobs to break up the silhouette.
  for (let i = 0; i < 7; i++) {
    const x = 64 + (Math.random() - 0.5) * 56;
    const y = 64 + (Math.random() - 0.5) * 56;
    const r = 22 + Math.random() * 34;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, 'rgba(255,255,255,0.5)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const mistTex = cachedTexture('mist', makeMistTexture);

// ---- Ground mist ----------------------------------------------------------
// A ring of low, slow-drifting fog banks hugging the plain beyond the village.
// They tint to the sky/fog colour every frame, thicken at dawn/dusk and night,
// and give the wolves a real bank of vapour to slink out of. Lives under
// propsGroup so it only appears in play mode; count scales down on low tier.
const mistBanks = [];
const MIST_COUNT = LOW ? 5 : 18;
const _mistColor = new THREE.Color();
for (let i = 0; i < MIST_COUNT; i++) {
  const mat = new THREE.SpriteMaterial({
    map: mistTex, color: 0xf4f0e8, transparent: true, opacity: 0,
    depthWrite: false, fog: true,
  });
  const s = new THREE.Sprite(mat);
  // Scatter in an annulus around town (the wolf no-man's-land at r≈14–30).
  const ang = Math.random() * Math.PI * 2;
  const rad = 14 + Math.random() * 16;
  const scale = 7 + Math.random() * 7;
  s.scale.set(scale, scale * 0.5, 1);
  markDynamic(s);   // updateGroundMist drifts it
  propsGroup.add(s);
  mistBanks.push({
    sprite: s, mat,
    x: Math.sin(ang) * rad,
    z: Math.cos(ang) * rad,
    y: 0.7 + Math.random() * 0.8,
    phase: Math.random() * Math.PI * 2,
    drift: 0.15 + Math.random() * 0.25,
    baseOpacity: 0.22 + Math.random() * 0.22,
  });
}
function updateGroundMist(dt, t) {
  // Mist is faintest at bright noon, heaviest at night and around dawn/dusk.
  const thickness = 0.55 + skyNightFactor * 0.9;
  _mistColor.copy(scene.fog.color).lerp(_c1.setRGB(1, 1, 1), 0.12);
  for (const m of mistBanks) {
    m.x += Math.sin(t * 0.13 + m.phase) * m.drift * dt;
    m.z += Math.cos(t * 0.11 + m.phase * 1.3) * m.drift * dt;
    m.sprite.position.set(
      m.x,
      m.y + Math.sin(t * 0.4 + m.phase) * 0.12,
      m.z
    );
    const breathe = 0.7 + Math.sin(t * 0.5 + m.phase) * 0.3;
    m.mat.opacity = m.baseOpacity * thickness * breathe;
    m.mat.color.copy(_mistColor);
  }
}


// ---- Distant horizon ridges ----------------------------------------------
// One low-poly, unlit mesh gives the meadow a wrapped-in-valley backdrop without
// extra lights, textures, or per-prop draw calls. It sits just inside the fog
// wall, so the silhouette is visible as atmosphere rather than geometry detail.
const horizonRidgeMat = new THREE.MeshBasicMaterial({
  color: 0x8fa06f, transparent: true, opacity: 0.36, depthWrite: false, fog: true,
  side: THREE.DoubleSide,
});
const horizonRidgeGeo = new THREE.BufferGeometry();
{
  const SEG = LOW ? 28 : 44;
  const radius = 47;
  const positions = new Float32Array((SEG + 1) * 2 * 3);
  for (let i = 0; i <= SEG; i++) {
    const a = (i / SEG) * Math.PI * 2;
    // Deterministic layered sine waves: no stored random table and no seams.
    const ridge = 2.1
      + Math.sin(a * 3.0 + 0.4) * 0.75
      + Math.sin(a * 7.0 - 1.1) * 0.35
      + Math.sin(a * 11.0 + 2.0) * 0.18;
    const x = Math.cos(a) * radius;
    const z = Math.sin(a) * radius;
    const j = i * 6;
    positions[j] = x; positions[j + 1] = -0.55; positions[j + 2] = z;
    positions[j + 3] = x; positions[j + 4] = ridge; positions[j + 5] = z;
  }
  const indices = [];
  for (let i = 0; i < SEG; i++) {
    const b = i * 2;
    indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
  }
  horizonRidgeGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  horizonRidgeGeo.setIndex(indices);
  horizonRidgeGeo.computeBoundingSphere();
}
const horizonRidge = new THREE.Mesh(horizonRidgeGeo, horizonRidgeMat);
horizonRidge.renderOrder = -10;
propsGroup.add(horizonRidge);
function updateHorizonRidge(t) {
  // Warmer and softer by day, cooler and more visible by moonlight. Called at
  // the day/night cadence (12 Hz), not every render frame.
  horizonRidgeMat.color.copy(scene.fog.color).lerp(_c1.setHex(0x4f6f55), 0.34 + t * 0.28);
  horizonRidgeMat.opacity = LOW ? 0.26 + t * 0.10 : 0.30 + t * 0.14;
}

// Shared sky state — written by the day-night cycle, read by the ambience.
let skyNightFactor = 0;                                  // 0 = day, 1 = deep night
const sunDirWorld = new THREE.Vector3(0.3, 1, 0.25).normalize();

// ---- Pollen / dust motes drifting in the light (daytime) ----
// Carmack pass: these ambience bits used to be one Sprite draw per mote/bug.
// On Chromebooks, transparent sprite fill + dozens of draw calls is exactly the
// wrong trade. Keep the motion data in plain JS arrays, but draw each family as
// a single THREE.Points object with one dynamic position buffer.
const pollen = [];
const POLLEN_COUNT = LOW ? 6 : 34;
const pollenPositions = new Float32Array(POLLEN_COUNT * 3);
const pollenGeo = new THREE.BufferGeometry();
const pollenAttr = new THREE.BufferAttribute(pollenPositions, 3);
pollenAttr.setUsage(THREE.DynamicDrawUsage);
pollenGeo.setAttribute('position', pollenAttr);
const pollenMat = new THREE.PointsMaterial({
  map: glowTex, color: 0xfff2c4, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  size: LOW ? 0.13 : 0.11, sizeAttenuation: true,
});
const pollenPoints = new THREE.Points(pollenGeo, pollenMat);
pollenPoints.frustumCulled = false; // tiny drifting points around the camera; one draw is cheaper than culling churn
propsGroup.add(pollenPoints);
for (let i = 0; i < POLLEN_COUNT; i++) {
  const x = (Math.random() - 0.5) * 22;
  const y = 0.3 + Math.random() * 2.8;
  const z = (Math.random() - 0.5) * 22;
  pollen.push({
    x, y, z,
    phase: Math.random() * Math.PI * 2,
    speed: 0.1 + Math.random() * 0.15,
    drift: 0.2 + Math.random() * 0.3,
  });
  pollenPositions[i * 3] = x;
  pollenPositions[i * 3 + 1] = y;
  pollenPositions[i * 3 + 2] = z;
}
function updatePollen(dt, t) {
  const dayVis = 1 - skyNightFactor;
  // At night hide the entire Points draw, not just opacity 0.
  if (dayVis < 0.03) {
    pollenPoints.visible = false;
    return;
  }
  pollenPoints.visible = true;
  for (let i = 0; i < pollen.length; i++) {
    const p = pollen[i];
    p.x += Math.sin(t * 0.3 + p.phase) * p.drift * dt;
    p.y += Math.sin(t * p.speed + p.phase) * 0.5 * dt + 0.02 * dt;
    p.z += Math.cos(t * 0.25 + p.phase * 1.3) * p.drift * dt;
    if (p.y > 3.2) p.y = 0.3;
    const j = i * 3;
    pollenPositions[j] = p.x;
    pollenPositions[j + 1] = p.y;
    pollenPositions[j + 2] = p.z;
  }
  pollenGeo.attributes.position.needsUpdate = true;
  pollenMat.opacity = dayVis * (LOW ? 0.22 : 0.28);
}

// ---- Fireflies near the flower beds (night) ----
const fireflies = [];
const FIREFLY_COUNT = LOW ? 5 : 22;
const fireflyPositions = new Float32Array(FIREFLY_COUNT * 3);
const fireflyGeo = new THREE.BufferGeometry();
const fireflyAttr = new THREE.BufferAttribute(fireflyPositions, 3);
fireflyAttr.setUsage(THREE.DynamicDrawUsage);
fireflyGeo.setAttribute('position', fireflyAttr);
const fireflyMat = new THREE.PointsMaterial({
  map: glowTex, color: 0xcaff8a, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  size: LOW ? 0.22 : 0.19, sizeAttenuation: true,
});
const fireflyPoints = new THREE.Points(fireflyGeo, fireflyMat);
fireflyPoints.frustumCulled = false;
propsGroup.add(fireflyPoints);
for (let i = 0; i < FIREFLY_COUNT; i++) {
  const home = flowerPatches.length ? flowerPatches[i % flowerPatches.length] : { x: 0, z: 0, r: 8 };
  const x = home.x + (Math.random() - 0.5) * home.r * 2;
  const y = 0.3 + Math.random() * 1.0;
  const z = home.z + (Math.random() - 0.5) * home.r * 2;
  fireflies.push({
    x, y, z,
    phase: Math.random() * Math.PI * 2,
    blink: 1.5 + Math.random() * 2.5,
    speed: 0.3 + Math.random() * 0.4,
  });
  fireflyPositions[i * 3] = x;
  fireflyPositions[i * 3 + 1] = y;
  fireflyPositions[i * 3 + 2] = z;
}
function updateFireflies(dt, t) {
  // By day fireflies are invisible — hide the one batched draw and skip sim.
  if (skyNightFactor < 0.02) {
    fireflyMat.opacity = 0;
    fireflyPoints.visible = false;
    return;
  }
  fireflyPoints.visible = true;
  let blinkSum = 0;
  for (let i = 0; i < fireflies.length; i++) {
    const f = fireflies[i];
    f.x += Math.sin(t * f.speed + f.phase) * 0.4 * dt;
    f.z += Math.cos(t * f.speed * 1.1 + f.phase) * 0.4 * dt;
    f.y += Math.sin(t * 1.7 + f.phase) * 0.25 * dt;
    f.y = Math.max(0.2, Math.min(1.6, f.y));
    const j = i * 3;
    fireflyPositions[j] = f.x;
    fireflyPositions[j + 1] = f.y;
    fireflyPositions[j + 2] = f.z;
    const blink = Math.max(0, Math.sin(t * f.blink + f.phase));
    blinkSum += blink * blink;
  }
  fireflyGeo.attributes.position.needsUpdate = true;
  fireflyMat.opacity = skyNightFactor * (0.22 + (blinkSum / fireflies.length) * 0.58);
}

// ---- Bees buzzing tight loops over the flower beds (daytime) ----
// Tiny amber points that orbit flower patches. Batched into a single draw so
// the garden stays alive without taxing low-end integrated GPUs.
const bees = [];
const BEE_COUNT = LOW ? 2 : 6;
const beePositions = new Float32Array(BEE_COUNT * 3);
const beeGeo = new THREE.BufferGeometry();
const beeAttr = new THREE.BufferAttribute(beePositions, 3);
beeAttr.setUsage(THREE.DynamicDrawUsage);
beeGeo.setAttribute('position', beeAttr);
const beeMat = new THREE.PointsMaterial({
  map: glowTex, color: 0xffd24a, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  size: LOW ? 0.10 : 0.08, sizeAttenuation: true,
});
const beePoints = new THREE.Points(beeGeo, beeMat);
beePoints.frustumCulled = false;
propsGroup.add(beePoints);
for (let i = 0; i < BEE_COUNT; i++) {
  const home = flowerPatches.length ? flowerPatches[i % flowerPatches.length] : { x: 0, z: 0, r: 6 };
  bees.push({
    home,
    phase: Math.random() * Math.PI * 2,
    orbit: 0.3 + Math.random() * 0.5,        // loop radius
    speed: 2.2 + Math.random() * 1.6,        // angular speed (fast = buzzy)
    y: 0.4 + Math.random() * 0.5,
  });
}
function updateBees(dt, t) {
  // Bees are a daytime accent — at night hide the batched draw.
  const dayVis = 1 - skyNightFactor;
  if (dayVis < 0.1) {
    beePoints.visible = false;
    return;
  }
  beePoints.visible = true;
  for (let i = 0; i < bees.length; i++) {
    const b = bees[i];
    // Loop centre drifts slowly around the home bed; bee circles it quickly.
    const cx = b.home.x + Math.sin(t * 0.3 + b.phase) * b.home.r * 0.7;
    const cz = b.home.z + Math.cos(t * 0.27 + b.phase * 1.3) * b.home.r * 0.7;
    const a = t * b.speed + b.phase;
    const j = i * 3;
    beePositions[j] = cx + Math.cos(a) * b.orbit;
    beePositions[j + 1] = b.y + Math.sin(t * 3.1 + b.phase) * 0.12;
    beePositions[j + 2] = cz + Math.sin(a) * b.orbit;
  }
  beeGeo.attributes.position.needsUpdate = true;
  beeMat.opacity = dayVis * 0.85;
}

// ---- Lanterns: emissive glass + halo, plus a real point light at night ----
// Bump a lamppost to flick it on/off; when on, a real light glows at the top.
const lanterns = [];
let kitLanternGlowMat = null;
function buildLantern(x, z, nightLight) {
  // The body: the handmade kit lamp-post as an instanced chunk prop (one draw
  // for every lantern in a chunk), or the procedural post + glowing head when
  // the kit didn't load. The glow sprite / point light / bump toggle are
  // per-lantern either way; only the procedural head has an animatable
  // emissive (an instanced shared material can't flicker per lamp — the
  // sprite + light carry the night effect for kit lamps).
  let headMat = null;
  let headY = 1.3;
  const gy = heightAt(x, z);
  if (kitReady() && kitGeometry('lantern')) {
    addProp('lantern', { x, z });
    headY = kitSize('lantern').h - 0.14;   // centre of the kit lamp's head box
    if (!kitLanternGlowMat) {
      kitLanternGlowMat = new THREE.MeshBasicMaterial({ color: PALETTE.lanternCore, transparent: true, opacity: 0.72, fog: true });
    }
    const glowBead = new THREE.Mesh(new THREE.SphereGeometry(0.075, LOW ? 6 : 8, LOW ? 4 : 6), kitLanternGlowMat);
    glowBead.position.set(x, gy + headY, z);
    glowBead.renderOrder = 1;
    propsGroup.add(glowBead);
  } else {
    const post = new THREE.Mesh(
      new THREE.BoxGeometry(0.09, 1.2, 0.09),
      new THREE.MeshLambertMaterial({ color: PALETTE.doorDark })
    );
    post.position.set(x, 0.6, z);
    post.castShadow = !LOW;
    propsGroup.add(post);
    headMat = new THREE.MeshStandardMaterial({
      color: PALETTE.lanternCore, emissive: PALETTE.lanternEmissive, emissiveIntensity: 0, roughness: 0.5, metalness: 0,
    });
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), headMat);
    head.position.set(x, headY, z);
    propsGroup.add(head);
  }
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTex, color: PALETTE.lanternHalo, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  }));
  glow.scale.set(1.1, 1.1, 1);
  glow.position.set(x, gy + headY, z);
  propsGroup.add(glow);
  // Real lights are costly per-fragment, so never on low tier. Off the low
  // tier every lamp gets one (default off) so bumping any of them lights up.
  let light = null;
  if (!LOW) {
    light = new THREE.PointLight(PALETTE.lanternLight, 0, 6, 2);
    light.position.set(x, gy + headY + 0.05, z);   // sits at the top of the post
    propsGroup.add(light);
  }
  const L = { headMat, glow, light, nightLight, on: false, phase: Math.random() * Math.PI * 2 };
  lanterns.push(L);
  const sourceId = `lamp-${lanterns.length - 1}`;
  // Bump to flick this lamp on or off.
  addBumpZone(x, z, 0.75, sourceId, () => {
    L.on = !L.on;
    checkLanternUnlock();
  });
  return L;
}
let lanternUnlockTriggered = false;
function checkLanternUnlock() {
  if (lanternUnlockTriggered || lanterns.length === 0 || !lanterns.every(L => L.on)) return;
  lanternUnlockTriggered = true;
  showBumpMsg('VILLAGE LIGHTS ✨', 'Every lamp is glowing! A little thank-you treasure appears by the well.', 4, 'lantern-unlock');
  addCoinRing(0, -4, 2.65, 8, 0.9, Math.PI / 8);
}
buildLantern(-2.6, -2.6, true);
buildLantern( 2.6, -2.6, true);
buildLantern(-2.6,  2.6, false);
buildLantern( 2.6,  2.6, false);

// ============= ZONES =============
// Themed zones live in src/world/zones/ and build with the same machinery as
// the village. They register before the chunk build below so their scatter
// props stream like everything else.
buildWordville({
  buildGameHut, buildHouse, buildKitStructure, addHutSign, buildPath,
  buildTree, buildBush, buildRock, buildFlowerPatch, buildLantern,
  buildSignpost, buildMushroomCluster,
  addBumpZone, showBumpMsg, addBox, propsGroup,
});
buildGrove({
  buildGameHut, buildHouse, buildKitStructure, addHutSign, buildPath,
  buildTree, buildBush, buildRock, buildFlowerPatch, buildLantern,
  buildSignpost, buildMushroomCluster, buildGrassTuft, buildLilyPad,
  addBumpZone, showBumpMsg, addBox, propsGroup,
});
// Obby Peak (src/world/zones/obby.js): the climb course. Its ctx adds the coin
// spawners (elevated platform/treasure coins) on top of the shared builders.
buildObby({
  buildGameHut, buildHouse, addHutSign, buildPath,
  buildTree, buildBush, buildRock, buildFlowerPatch, buildLantern,
  buildSignpost, buildMushroomCluster, placeKitPlatform,
  addBumpZone, showBumpMsg, addBox, propsGroup,
  addCoin, addCoinAt, addCoinRing, addCoinLine,
});
// Numberton (src/world/zones/numberton.js): the numbers/math zone that finally
// gives the south road a real destination (it used to dead-end at the picnic
// green). Same builder ctx as the other zones.
buildNumberton({
  buildGameHut, buildHouse, buildKitStructure, addHutSign, buildPath,
  buildTree, buildBush, buildRock, buildFlowerPatch, buildLantern,
  buildSignpost, buildMushroomCluster,
  addBumpZone, showBumpMsg, addBox, propsGroup,
});
// Water (src/world/water.js): a shallow brook the south road crosses on a
// footbridge on the way to Numberton, so the journey ties the map together.
buildBrook({ propsGroup }, [[-11, -15], [-5, -15.3], [0, -15.4], [5, -15.3], [11, -15]], 2.1);
buildFootbridge({ propsGroup }, 0, -15.3, 0, 4.2, 2.4);   // carries the south road over the brook
// Fog-line hero silhouettes: cheap billboard landmarks that give every road a
// memorable destination shape without increasing the draw distance.
addHeroLandmark(34.5, 0, '📚', '#fff3c4', 5.2);   // Wordville
addHeroLandmark(-34.5, 0, '🧭', '#d7f0cf', 5.2);  // The Grove
addHeroLandmark(0, 34.5, '⛰', '#e2ddff', 5.5);   // Obby Peak
addHeroLandmark(0, -34.5, '🔢', '#d9ecff', 5.4);  // Numberton

// Village hub enrichment (src/world/villageDecor.js): the town-square
// centrepiece, the Village Green that gives the south road a destination, the
// junction dwell spots, and the outer-ring landmarks. Built through the same
// one-directional ctx contract as the zones, with the extra instanced-prop and
// clutter builders it leans on.
buildVillageDecor({
  buildPath, buildTree, buildBush, buildRock, buildFlowerPatch, buildStarAccent,
  buildMushroomCluster, buildSignpost, buildTownPropCluster, buildLantern, buildLilyPad,
  buildCrate, buildBarrel, buildFlowerBox, buildTinyFlag,
  buildMarketBasket, buildGrassTuft,
  addBox, addCyl, addSphere, addVillageLandmark, addBumpZone, showBumpMsg, propsGroup,
  addProp, kitReady,   // for the GLB maypole (falls back to the procedural one)
  addCoin, addCoinAt, addCoinRing,   // pocket payoffs on the diagonals
  // Water builders for the SW fishing-hole pocket (src/world/water.js).
  buildFishingHole: (x, z, r) => buildFishingHole({ propsGroup }, x, z, r),
  buildDock: (x, z, yaw, len) => buildDock({ propsGroup }, x, z, yaw, len),
});

// ---- Roadside flower habitats: the long walks out to the zones (the r14–26
// band) used to be prop scatter with no motion. A ribbon of flower beds down
// each road registers butterfly + firefly habitats (buildFlowerPatch), so the
// journeys between hub and zones come alive with ambient life. Instanced — ~0
// draw calls. Kept ~2.5u off each centreline so they never sit on the cobbles.
for (const [x, z] of [
  [2.6, 15], [-2.6, 21], [2.4, 27],        // north road (to Obby)
  [2.6, -18], [-2.6, -24], [2.4, -30],     // south road (to Numberton, past the green)
  [15, 2.6], [21, -2.6], [27, 2.4],        // east road (to Wordville)
  [-15, -2.6], [-21, 2.6], [-27, -2.4],    // west road (to the Grove)
]) buildFlowerPatch(x, z, 6, 0.8);

// Re-scatter the fireflies across the FULL habitat list now that every zone,
// pocket and roadside bed is registered — they were seeded at module-load,
// before those beds existed, so without this they'd only glow near the village.
for (let i = 0; i < fireflies.length; i++) {
  const home = flowerPatches[(Math.random() * flowerPatches.length) | 0];
  const f = fireflies[i];
  f.x = home.x + (Math.random() - 0.5) * home.r * 2;
  f.z = home.z + (Math.random() - 0.5) * home.r * 2;
  fireflyPositions[i * 3] = f.x;
  fireflyPositions[i * 3 + 2] = f.z;
}
fireflyAttr.needsUpdate = true;

// ============= OUTFIT STORE =============
// Chocky's Closet (src/game/store.js) builds its shop through the same ctx
// pattern as the zones and sells the catalog in src/game/outfits.js. The
// wallet stays owned by this file — the store only counts and spends.
initOutfits(() => ({ chocky, chockyMat, bones }));
const storeApi = initStore({
  buildHouse, buildKitStructure, addHutSign, buildPath, addVillageLandmark,
  getPlayerPos: () => player.pos,
  isPlaying: () => gameMode,
  coins: {
    count: () => coinInventory,
    spend: (n) => { coinInventory = Math.max(0, coinInventory - n); updateCoinUI(); },
  },
});

// ============= QUEST: THE BURIED COINS =============
// Farmer Pip's digging quest (src/game/quest.js), built through the same ctx
// pattern. The dug-up treasure is one-shot coins (addCoin once=true), so the
// reward can't be farmed by re-entering play mode.
const questApi = initQuest({
  addBumpZone, showBumpMsg, hideBumpMsg,
  addBox, buildFlowerPatch, buildSignpost, propsGroup,
  getPlayerPos: () => player.pos,
  spawnDust, addCamShake, triggerFace,
  spawnQuestCoins: (cx, cz) => {
    addCoin(cx, heightAt(cx, cz) + 0.6, cz, true);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.4;
      const x = cx + Math.cos(a) * 1.2, z = cz + Math.sin(a) * 1.2;
      addCoin(x, heightAt(x, z) + 0.6, z, true);
    }
  },
});
const wizardLightningFx = createWizardLightningFx({ parent: propsGroup });

// ============= QUEST: THE LOST FENNEC =============
// The Grove Keeper's critter-rescue quest (src/game/critterquest.js), out in
// the oasis. Finding Dot is exploring, not jumping, so it needs no landing
// hook — its update() self-samples the player position via getPlayerPos.
const critterQuestApi = initCritterQuest({
  addBumpZone, showBumpMsg, hideBumpMsg,
  addBox, propsGroup,
  getPlayerPos: () => player.pos,
  spawnDust, addCamShake, triggerFace,
});

// ============= QUEST: THE GREAT BUNNY ROUNDUP =============
// A map-wide chase (src/game/bunnyquest.js): the Bunny Wrangler's warren is
// scattered to every corner of Chockytown. Catching a bunny is exploring, not
// jumping, so its update() self-samples the player position. The completion
// reward is a coin burst at the hutch (one-shot coins, can't be farmed).
const bunnyQuestApi = initBunnyQuest({
  addBumpZone, showBumpMsg, hideBumpMsg, propsGroup,
  getPlayerPos: () => player.pos,
  spawnDust, addCamShake, triggerFace,
  spawnQuestCoins: (cx, cz) => {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const x = cx + Math.cos(a) * 1.3, z = cz + Math.sin(a) * 1.3;
      addCoin(x, heightAt(x, z) + 0.6, z, true);
    }
  },
});

// ============= WAYFINDING: MINIMAP + COMPASS + FAST-TRAVEL HOME =============
// The player-facing map (src/game/minimap.js) — design plan step 4. Static
// destinations are declared here (the same places the hero-landmark billboards
// mark) and the live quest objectives are aggregated from each quest's
// getBeacon(). The map only ever reads the world through this ctx; goHome()
// below is the fast-travel primitive.
const MINIMAP_POINTS = [
  { x: 0,   z: 0,   icon: '🏠', color: '#ffd27f', kind: 'home', label: 'Village' },
  { x: 44,  z: 0,   icon: '📚', color: '#f6c945', label: 'Wordville' },
  { x: -44, z: 0,   icon: '🧭', color: '#8fd39a', label: 'The Grove' },
  { x: 0,   z: 44,  icon: '⛰', color: '#b0a4ff', label: 'Obby Peak' },
  { x: 0,   z: -44, icon: '🔢', color: '#8fbfff', label: 'Numberton' },
  // Outer-ring landmarks (src/world/villageDecor.js) — texture, not destinations.
  { x: 0,   z: -22, color: 'rgba(255,224,102,0.9)' },    // Village Green (picnic waypoint)
  { x: 21,  z: 16,  color: 'rgba(210,196,150,0.85)' },   // Lookout Rock
  { x: -20, z: 20,  color: 'rgba(150,196,150,0.85)' },   // Old Orchard
  { x: 22,  z: -18, color: 'rgba(210,150,150,0.85)' },   // Berry Thicket
  { x: -22, z: -20, color: 'rgba(120,190,220,0.9)' },    // Fishing Hole (SW pocket)
];
// Reused each poll so the minimap's beacon read allocates nothing steady-state.
const _minimapBeacons = [];
function activeQuestBeacons(px, pz) {
  _minimapBeacons.length = 0;
  for (const api of [questApi, critterQuestApi, bunnyQuestApi]) {
    const b = api.getBeacon && api.getBeacon(px, pz);
    if (b) _minimapBeacons.push(b);
  }
  return _minimapBeacons;
}
const minimapApi = initMinimap({
  worldHalf: WORLD_HALF,
  points: MINIMAP_POINTS,
  isPlaying: () => gameMode,
  getPlayerPos: () => player.pos,
  getPlayerFacing: () => player.facing,
  getBeacons: (px, pz) => activeQuestBeacons(px, pz),
  goHome: () => goHome(),
});

flushContactShadows();

// Clouds drift over everything (play mode only, like the rest of propsGroup).
initClouds(propsGroup);

// ============= BAKED WORLD BASELINE =============
// Replay the committed world-baked.json edits onto the procedural world BEFORE
// chunks build, in EVERY build (no editor required) — this is how authored
// edits ship to kids. Terrain deltas are set before displacement; props are
// registered via addProp so the build loop below meshes them; signs/structures
// go through the same runtime factories the editor uses. The editor's
// localStorage working copy (if any) layers on top later, only in editor loads.
{
  const baked = await loadBakedLayer();
  if (baked) {
    const spawnBakedProp = (e) => {
      const desc = PROP_PALETTE_BY_KEY[e.type];
      const scale = e.scale || 1;
      // Behavior-routed kit props: a baked lantern gets the full glow/night
      // light/bump treatment (buildLantern registers its own body prop), and
      // solid kit props (platforms) get their static standable AABB. Mirrors
      // the editor's live routing in worldEdit.spawnEntry.
      if (desc?.place === 'lantern') { buildLantern(e.x, e.z, false); return; }
      if (desc?.collision) {
        const c = desc.collision, gy = heightAt(e.x, e.z);
        addObstacle({
          minX: e.x - c.w / 2, maxX: e.x + c.w / 2,
          minY: gy, maxY: gy + c.h,
          minZ: e.z - c.d / 2, maxZ: e.z + c.d / 2,
        });
      }
      if (e.kind === 'tree' || desc?.hasCollision) {
        const r = 0.16 * scale, gy = heightAt(e.x, e.z);
        addObstacle({ minX: e.x - r, maxX: e.x + r, minY: gy, maxY: gy + 0.9 * scale, minZ: e.z - r, maxZ: e.z + r });
      }
      for (const [type, d] of expandProp(e)) addProp(type, d);
    };
    applyEditLayer(baked, {
      applyTerrain: (t) => setHeightDeltas(t),
      removeProp: (id) => removePropById(id),
      addProp: spawnBakedProp,
      addStructure: (s) => createStructure(s),
      addSign: (s) => createSign(s),
      xformStructure: (name, x) => {
        const s = editableStructures.find((s) => s.name === name);
        if (!s) return;
        if (x.pos) s.group.position.set(x.pos[0], x.pos[1], x.pos[2]);
        if (x.rot) s.group.rotation.set(x.rot[0], x.rot[1], x.rot[2]);
        if (x.scale) s.group.scale.set(x.scale[0], x.scale[1], x.scale[2]);
      },
      xformSign: (key, x) => {
        const s = signGroups.find((s) => s.id === key || s.name === key);
        if (!s) return;
        s.group.position.set(x.x, x.y, x.z);
        s.group.rotation.y = x.yaw;
        if (x.text) setSignText(s, x.text);
      },
    });
  }
}

// ============= BUILD WORLD CHUNKS =============
// Every prop record is registered; hand them to the chunk system. Build the
// chunks the player can actually see at spawn synchronously (fog hides
// everything past ~40 units), then let the per-frame streaming budget in
// tick() finish the ring — incremental startup, village first.
attachChunksTo(propsGroup);
{
  let guard = 0;
  while (updateChunkStreaming(0, 0, 45) && guard++ < 64) { /* build spawn surroundings */ }
}
console.log('[perf] world chunks:', JSON.stringify(chunkStats()));

// Roof coins for every game hut — village and zones alike.
for (const hc of hutCoins) addCoin(hc.x, hc.y, hc.z);

// More coin spots around the world (placed here so zone terrain exists):
// a ring around the wishing well (offset 45° to clear the bench + bump zone),
addCoinRing(0, -4, 2.1, 4, 1.0, Math.PI / 4);
// the two medium platforming blocks flanking the tall one,
addCoin(-1.5, 1.75, 5.2);
addCoin( 1.5, 1.75, 5.2);
// a breadcrumb trail along the east road to Wordville,
addCoinLine([[14, 0], [19, 0], [24, 0], [29, 0], [33.5, 0]]);
// a few in the Wordville plaza (heightAt handles the plateau),
addCoinAt(42.9, 6.1);
addCoinAt(45.2, 6.3);
addCoinAt(44, 0);
// and a risk/reward run up the north road into Shell Shocker territory.
addCoinLine([[0, 14], [0, 20], [0, 26], [0, 31]]);
addCoinRing(0, 38, 2.5, 5);
// One above the outfit store's roof, like the game huts (wallH 1.6 + 1.1).
addCoin(-3.4, 2.7, -7.4);
buildLantern( 0,   -3.4, true);     // by the well
function updateLanterns(t) {
  const night = Math.min(1, skyNightFactor * 1.4);
  for (const L of lanterns) {
    // A lamp glows from the night cycle OR from being switched on by a bump.
    const lit = Math.max(L.on ? 1 : 0, night);
    // Gentle candle flicker: two out-of-phase sines so it never looks like a
    // clean pulse. Free — a couple of trig calls per lamp, no allocations.
    const flicker = 0.9 + 0.1 * Math.sin(t * 7 + L.phase) + 0.04 * Math.sin(t * 17 + L.phase * 1.7);
    if (L.headMat) L.headMat.emissiveIntensity = lit * 1.6 * flicker;   // procedural-fallback head only
    L.glow.visible = lit > 0.01;             // skip the additive halo when dark/off
    if (L.glow.visible) L.glow.material.opacity = lit * 0.75 * flicker;
    // Real point light at the top: on when bumped on, or at night for the
    // lanterns flagged to carry one. Bumping lights it any time of day.
    if (L.light) {
      const lightLit = Math.max(L.on ? 1 : 0, L.nightLight ? night : 0);
      L.light.intensity = lightLit * 2.4 * flicker;
    }
  }
}

// ---- Sun disc by day, moon by night (ride opposite ends of the light arc) ----
const sunDisc = new THREE.Sprite(new THREE.SpriteMaterial({
  map: glowTex, color: 0xfff2c0, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
}));
sunDisc.scale.set(7, 7, 1);
markDynamic(sunDisc);   // tracks the sun direction as the day turns
propsGroup.add(sunDisc);
const moonDisc = new THREE.Sprite(new THREE.SpriteMaterial({
  map: glowTex, color: 0xd8e6ff, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
}));
moonDisc.scale.set(4.6, 4.6, 1);
markDynamic(moonDisc);   // tracks the moon direction as the day turns
propsGroup.add(moonDisc);
const CELESTIAL_DIST = 46;
function updateCelestial() {
  // Each disc is a big additive sprite; skip drawing whichever is below the
  // horizon (sun by night, moon by day) instead of blending a transparent quad.
  const sunUp = sunDirWorld.y > 0;
  sunDisc.visible = sunUp;
  if (sunUp) {
    sunDisc.position.copy(sunDirWorld).multiplyScalar(CELESTIAL_DIST);
    sunDisc.material.opacity = sunDirWorld.y * 0.95;
  }
  const moonOpacity = skyNightFactor * Math.max(0, -sunDirWorld.y) * 1.1;
  moonDisc.visible = moonOpacity > 0.001;
  if (moonDisc.visible) {
    moonDisc.position.copy(sunDirWorld).multiplyScalar(-CELESTIAL_DIST);
    moonDisc.material.opacity = moonOpacity;
  }
}

// ---- Star field (fades in at night) ----
const starCount = LOW ? 120 : 280;
const starGeo = new THREE.BufferGeometry();
{
  const pos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const el = 0.1 + Math.random() * 0.9;     // bias toward overhead
    const r = 55;
    const y = el * r;
    const rr = Math.sqrt(Math.max(0, r * r - y * y));
    pos[i*3]   = Math.cos(a) * rr;
    pos[i*3+1] = y;
    pos[i*3+2] = Math.sin(a) * rr;
  }
  starGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
}
const starField = new THREE.Points(starGeo, new THREE.PointsMaterial({
  color: 0xffffff, size: 0.7, sizeAttenuation: false,
  transparent: true, opacity: 0, depthWrite: false, fog: false,
}));
markDynamic(starField);   // slow night-sky rotation
propsGroup.add(starField);

function updateAmbience(dt, t) {
  updatePollen(dt, t);
  updateFireflies(dt, t);
  updateLanterns(t);
  updateCelestial();
  // Stars only exist at night — hide the Points object by day so it's skipped.
  starField.visible = skyNightFactor > 0.02;
  if (starField.visible) {
    // A gentle whole-field shimmer so the sky reads as alive (per-star twinkle
    // would need a per-frame buffer upload — not worth it for a Chromebook).
    starField.material.opacity = skyNightFactor * 0.9 * (0.85 + 0.15 * Math.sin(t * 1.3));
    starField.rotation.y += dt * 0.004;
  }
}

// ============= WOLVES =============
// Voxel wolves lurking in the fog beyond the village. Venture too far
// from town and pairs of glowing eyes emerge from the mist.
const WOLF_SPAWN_DIST = 15;
const WOLF_RETREAT_DIST = 12;
const WOLF_MAX = TUNING.wolfMax ?? (LOW ? 2 : 4);
const WOLF_SPEED = 3.0;
const ZIP_WOLF_ZAP_RADIUS = 6.0;
const wolves = [];
const _zipZapFrom = new THREE.Vector3();
const _zipZapTo = new THREE.Vector3();

// Shared geometry for the realistic grey-wolf build. Still box-built to match
// the village's chunky art style, but assembled with real wolf anatomy:
// a lean countershaded barrel, muscular haunches, a thick lowered neck, a
// tapered muzzle and jointed two-bone legs that actually flex as it moves.
const wolfGeo = {
  saddle:    new THREE.BoxGeometry(0.30, 0.16, 0.52),  // dark fur along the back
  torso:     new THREE.BoxGeometry(0.32, 0.20, 0.54),  // mid-grey barrel
  belly:     new THREE.BoxGeometry(0.30, 0.12, 0.46),  // cream underside
  haunch:    new THREE.BoxGeometry(0.34, 0.30, 0.26),  // muscular rear
  shoulder:  new THREE.BoxGeometry(0.32, 0.27, 0.24),  // front shoulders
  chest:     new THREE.BoxGeometry(0.26, 0.22, 0.16),  // cream chest blaze
  ruff:      new THREE.BoxGeometry(0.26, 0.26, 0.12),  // shaggy ruff at neck base
  neck:      new THREE.BoxGeometry(0.19, 0.20, 0.24),
  skull:     new THREE.BoxGeometry(0.19, 0.17, 0.20),
  brow:      new THREE.BoxGeometry(0.20, 0.06, 0.11),
  muzzle:    new THREE.BoxGeometry(0.115, 0.095, 0.17),
  muzzleTip: new THREE.BoxGeometry(0.085, 0.075, 0.07),
  jaw:       new THREE.BoxGeometry(0.10, 0.05, 0.15),
  cheek:     new THREE.BoxGeometry(0.06, 0.12, 0.13),
  nose:      new THREE.BoxGeometry(0.07, 0.055, 0.04),
  eye:       new THREE.BoxGeometry(0.05, 0.035, 0.025),
  earOuter:  new THREE.BoxGeometry(0.09, 0.14, 0.05),
  earInner:  new THREE.BoxGeometry(0.05, 0.09, 0.03),
  thigh:     new THREE.BoxGeometry(0.095, 0.22, 0.12),
  shank:     new THREE.BoxGeometry(0.075, 0.21, 0.085),
  paw:       new THREE.BoxGeometry(0.10, 0.055, 0.15),
  tailA:     new THREE.BoxGeometry(0.11, 0.11, 0.18),
  tailB:     new THREE.BoxGeometry(0.085, 0.085, 0.16),
  tailC:     new THREE.BoxGeometry(0.055, 0.055, 0.15),
};

function buildVoxelWolf() {
  const group = new THREE.Group();
  // Per-wolf coat variation so a pack isn't a row of clones (timber, grey, dark).
  const tint = 0.82 + Math.random() * 0.32;
  const lam = (hex) => new THREE.MeshLambertMaterial({
    color: new THREE.Color(hex).multiplyScalar(tint),
  });
  // Countershaded grey-wolf palette: dark grizzled back → grey flanks → pale belly.
  const furBack  = lam(0x55514a);
  const furSide  = lam(0x6f695d);
  const furBelly = lam(0xb0a692);
  const furLeg   = lam(0x46413a);
  const furPaw   = lam(0x37332d);
  const furEar   = furBack;
  const earInner = lam(0x8c7f6e);
  const noseMat  = new THREE.MeshLambertMaterial({ color: 0x14110e });

  // Upper body (torso, neck, head, tail) rides on `spine` so it can bob, crouch
  // and tilt while the legs stay planted on the ground.
  const spine = new THREE.Group();
  group.add(spine);

  const saddle = new THREE.Mesh(wolfGeo.saddle, furBack);
  saddle.position.set(0, 0.62, -0.01);
  saddle.castShadow = true; spine.add(saddle);
  const torso = new THREE.Mesh(wolfGeo.torso, furSide);
  torso.position.set(0, 0.52, -0.01);
  torso.castShadow = true; spine.add(torso);
  const belly = new THREE.Mesh(wolfGeo.belly, furBelly);
  belly.position.set(0, 0.45, -0.01);
  spine.add(belly);
  const haunch = new THREE.Mesh(wolfGeo.haunch, furBack);
  haunch.position.set(0, 0.54, -0.25);
  haunch.castShadow = true; spine.add(haunch);
  const shoulder = new THREE.Mesh(wolfGeo.shoulder, furSide);
  shoulder.position.set(0, 0.54, 0.22);
  shoulder.castShadow = true; spine.add(shoulder);
  const chest = new THREE.Mesh(wolfGeo.chest, furBelly);
  chest.position.set(0, 0.44, 0.28);
  spine.add(chest);

  // Neck — a forward-and-down pivot for that low predatory carriage.
  const neck = new THREE.Group();
  neck.position.set(0, 0.60, 0.30);
  neck.rotation.x = 0.40;
  spine.add(neck);
  const ruff = new THREE.Mesh(wolfGeo.ruff, furBack);
  ruff.position.set(0, 0, -0.02);
  ruff.castShadow = true; neck.add(ruff);
  const neckMesh = new THREE.Mesh(wolfGeo.neck, furSide);
  neckMesh.position.set(0, -0.01, 0.12);
  neck.add(neckMesh);

  // Head at the end of the neck (faces +z).
  const head = new THREE.Group();
  head.position.set(0, -0.02, 0.22);
  neck.add(head);
  const skull = new THREE.Mesh(wolfGeo.skull, furSide);
  skull.position.set(0, 0.02, 0.0);
  skull.castShadow = true; head.add(skull);
  const brow = new THREE.Mesh(wolfGeo.brow, furBack);
  brow.position.set(0, 0.09, 0.03);
  head.add(brow);
  const muzzle = new THREE.Mesh(wolfGeo.muzzle, furSide);
  muzzle.position.set(0, -0.02, 0.16);
  head.add(muzzle);
  const muzzleTip = new THREE.Mesh(wolfGeo.muzzleTip, furBack);
  muzzleTip.position.set(0, -0.01, 0.24);
  head.add(muzzleTip);
  const jaw = new THREE.Mesh(wolfGeo.jaw, furBelly);
  jaw.position.set(0, -0.07, 0.14);
  head.add(jaw);
  const cheekL = new THREE.Mesh(wolfGeo.cheek, furBelly);
  cheekL.position.set(-0.10, -0.02, 0.02); head.add(cheekL);
  const cheekR = new THREE.Mesh(wolfGeo.cheek, furBelly);
  cheekR.position.set(0.10, -0.02, 0.02); head.add(cheekR);
  const nose = new THREE.Mesh(wolfGeo.nose, noseMat);
  nose.position.set(0, -0.01, 0.285);
  head.add(nose);

  // Amber, faintly self-lit eyes (read as eyeshine in the dark).
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0xffc24a });
  const eyeL = new THREE.Mesh(wolfGeo.eye, eyeMat);
  eyeL.position.set(-0.07, 0.04, 0.115); head.add(eyeL);
  const eyeR = new THREE.Mesh(wolfGeo.eye, eyeMat);
  eyeR.position.set(0.07, 0.04, 0.115); head.add(eyeR);

  // Big upright triangular ears that swivel.
  const earL = new THREE.Group(); earL.position.set(-0.075, 0.12, -0.03);
  earL.rotation.z = -0.18; head.add(earL);
  const earR = new THREE.Group(); earR.position.set(0.075, 0.12, -0.03);
  earR.rotation.z = 0.18; head.add(earR);
  const addEar = (parent) => {
    const outer = new THREE.Mesh(wolfGeo.earOuter, furEar);
    outer.position.y = 0.07; parent.add(outer);
    const inner = new THREE.Mesh(wolfGeo.earInner, earInner);
    inner.position.z = 0.025; outer.add(inner);
  };
  addEar(earL); addEar(earR);

  // ---- Jointed two-bone legs (hip → knee → paw) ----
  function buildLeg(front) {
    const hip = new THREE.Group();
    const thigh = new THREE.Mesh(wolfGeo.thigh, front ? furSide : furBack);
    thigh.position.y = -0.10; thigh.castShadow = true;
    hip.add(thigh);
    const knee = new THREE.Group();
    knee.position.y = -0.21;
    hip.add(knee);
    const shank = new THREE.Mesh(wolfGeo.shank, furLeg);
    shank.position.set(0, -0.10, front ? 0.0 : 0.01);
    shank.castShadow = true; knee.add(shank);
    const paw = new THREE.Mesh(wolfGeo.paw, furPaw);
    paw.position.set(0, -0.205, 0.03);
    knee.add(paw);
    // Hind legs carry a deeper hock angle than the near-straight forelegs.
    const baseHip = front ? -0.05 : -0.18;
    const baseKnee = front ? 0.12 : 0.42;
    hip.rotation.x = baseHip; knee.rotation.x = baseKnee;
    return { hip, knee, baseHip, baseKnee, front };
  }
  const legFL = buildLeg(true);  legFL.hip.position.set(-0.105, 0.46, 0.18);
  const legFR = buildLeg(true);  legFR.hip.position.set(0.105, 0.46, 0.18);
  const legBL = buildLeg(false); legBL.hip.position.set(-0.115, 0.46, -0.20);
  const legBR = buildLeg(false); legBR.hip.position.set(0.115, 0.46, -0.20);
  group.add(legFL.hip, legFR.hip, legBL.hip, legBR.hip);

  // ---- Bushy, jointed tail (droops, wags, tucks) ----
  const tailBase = new THREE.Group();
  tailBase.position.set(0, 0.56, -0.34);
  tailBase.rotation.x = 0.9;
  spine.add(tailBase);
  const tailSegA = new THREE.Mesh(wolfGeo.tailA, furBack);
  tailSegA.position.z = -0.09; tailSegA.castShadow = true; tailBase.add(tailSegA);
  const tailMid = new THREE.Group();
  tailMid.position.z = -0.18; tailBase.add(tailMid);
  const tailSegB = new THREE.Mesh(wolfGeo.tailB, furBack);
  tailSegB.position.z = -0.08; tailMid.add(tailSegB);
  const tailTip = new THREE.Group();
  tailTip.position.z = -0.16; tailMid.add(tailTip);
  const tailSegC = new THREE.Mesh(wolfGeo.tailC, furPaw);
  tailSegC.position.z = -0.07; tailTip.add(tailSegC);

  // Eyeshine glow rides on the head so it tracks wherever the wolf looks.
  const eyeGlowMat = new THREE.SpriteMaterial({
    map: glowTex, color: 0xffc24a, transparent: true, opacity: 0.55,
    blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  });
  const eyeGlow = new THREE.Sprite(eyeGlowMat);
  eyeGlow.scale.set(0.34, 0.16, 1);
  eyeGlow.position.set(0, 0.04, 0.20);
  head.add(eyeGlow);

  return {
    group, spine, neck, head, tailBase, tailMid, tailTip,
    earL, earR, legs: [legFL, legFR, legBL, legBR],
    eyeGlowMat, eyeMat, baseSpineY: spine.position.y,
  };
}


// Chromebook LOD: two pooled wolves, each only a handful of shared-material
// meshes. The full voxel wolf is charming, but building/disposing 40+ meshes
// per spawn is exactly the sort of CPU + driver spike that shows up as a hitch
// on fanless ChromeOS hardware.
const lowWolfPool = [];
const lowWolfShared = {
  bodyGeo: new THREE.BoxGeometry(0.46, 0.30, 0.82),
  headGeo: new THREE.BoxGeometry(0.24, 0.20, 0.28),
  legGeo:  new THREE.BoxGeometry(0.08, 0.34, 0.08),
  tailGeo: new THREE.BoxGeometry(0.10, 0.10, 0.34),
  earGeo:  new THREE.BoxGeometry(0.07, 0.12, 0.04),
  eyeGeo:  new THREE.BoxGeometry(0.045, 0.03, 0.02),
  furMat:  new THREE.MeshLambertMaterial({ color: 0x625d54 }),
  darkMat: new THREE.MeshLambertMaterial({ color: 0x39352f }),
  eyeMat:  new THREE.MeshBasicMaterial({ color: 0xffc24a }),
};
function buildLowWolf() {
  const group = new THREE.Group();
  group.visible = false;
  const spine = new THREE.Group();
  group.add(spine);

  const body = new THREE.Mesh(lowWolfShared.bodyGeo, lowWolfShared.furMat);
  body.position.set(0, 0.50, 0);
  spine.add(body);

  const neck = new THREE.Group();
  neck.position.set(0, 0.58, 0.46);
  spine.add(neck);
  const head = new THREE.Group();
  head.position.set(0, 0, 0.12);
  neck.add(head);
  const skull = new THREE.Mesh(lowWolfShared.headGeo, lowWolfShared.furMat);
  head.add(skull);
  const eyeL = new THREE.Mesh(lowWolfShared.eyeGeo, lowWolfShared.eyeMat);
  eyeL.position.set(-0.06, 0.03, 0.15); head.add(eyeL);
  const eyeR = new THREE.Mesh(lowWolfShared.eyeGeo, lowWolfShared.eyeMat);
  eyeR.position.set(0.06, 0.03, 0.15); head.add(eyeR);

  const earL = new THREE.Group(); earL.position.set(-0.07, 0.12, -0.02); head.add(earL);
  const earR = new THREE.Group(); earR.position.set(0.07, 0.12, -0.02); head.add(earR);
  const earMeshL = new THREE.Mesh(lowWolfShared.earGeo, lowWolfShared.darkMat); earMeshL.position.y = 0.05; earL.add(earMeshL);
  const earMeshR = new THREE.Mesh(lowWolfShared.earGeo, lowWolfShared.darkMat); earMeshR.position.y = 0.05; earR.add(earMeshR);

  function lowLeg(front, x) {
    const hip = new THREE.Group();
    hip.position.set(x, 0.38, front ? 0.24 : -0.26);
    const knee = new THREE.Group();
    hip.add(knee);
    const mesh = new THREE.Mesh(lowWolfShared.legGeo, lowWolfShared.darkMat);
    mesh.position.y = -0.18;
    knee.add(mesh);
    const baseHip = front ? -0.05 : -0.18;
    const baseKnee = front ? 0.12 : 0.42;
    hip.rotation.x = baseHip; knee.rotation.x = baseKnee;
    group.add(hip);
    return { hip, knee, baseHip, baseKnee, front };
  }
  const legs = [lowLeg(true, -0.12), lowLeg(true, 0.12), lowLeg(false, -0.12), lowLeg(false, 0.12)];

  const tailBase = new THREE.Group();
  tailBase.position.set(0, 0.54, -0.48);
  spine.add(tailBase);
  const tailMid = new THREE.Group(); tailBase.add(tailMid);
  const tailTip = new THREE.Group(); tailMid.add(tailTip);
  const tail = new THREE.Mesh(lowWolfShared.tailGeo, lowWolfShared.darkMat);
  tail.position.z = -0.18; tailMid.add(tail);

  const eyeGlowMat = new THREE.SpriteMaterial({
    map: glowTex, color: 0xffc24a, transparent: true, opacity: 0.45,
    blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  });
  const eyeGlow = new THREE.Sprite(eyeGlowMat);
  eyeGlow.scale.set(0.28, 0.13, 1);
  eyeGlow.position.set(0, 0.03, 0.18);
  head.add(eyeGlow);

  return {
    group, spine, neck, head, tailBase, tailMid, tailTip, earL, earR, legs,
    eyeGlowMat, eyeMat: lowWolfShared.eyeMat, baseSpineY: spine.position.y,
    pooled: true, active: false,
  };
}
function acquireLowWolf() {
  while (lowWolfPool.length < WOLF_MAX) {
    const w = buildLowWolf();
    propsGroup.add(w.group);
    lowWolfPool.push(w);
  }
  const w = lowWolfPool.find(v => !v.active);
  if (!w) return null;
  w.active = true;
  w.group.visible = true;
  return w;
}

function spawnWolf() {
  const toPlayer = Math.atan2(player.pos.x, player.pos.z);
  const angle = toPlayer + (Math.random() - 0.5) * Math.PI * 1.2;
  const dist = 26 + Math.random() * 8;
  const x = Math.sin(angle) * dist;
  const z = Math.cos(angle) * dist;
  const w = LOW ? acquireLowWolf() : buildVoxelWolf();
  if (!w) return;
  w.group.position.set(x, heightAt(x, z), z);
  if (!w.pooled) propsGroup.add(w.group);
  w.x = x;
  w.z = z;
  w.speed = WOLF_SPEED + (Math.random() - 0.5) * 0.6;
  w.phase = Math.random() * Math.PI * 2;
  w.state = 'stalk';
  w.circleAngle = Math.atan2(x - player.pos.x, z - player.pos.z);
  w.actor = spawnActor('wolf', w);
  wolves.push(w);
}

function despawnWolf(i) {
  const w = wolves[i];
  despawnActor(w.actor);
  if (w.pooled) {
    w.active = false;
    w.group.visible = false;
  } else {
    propsGroup.remove(w.group);
    w.group.traverse(c => {
      if (c.material) {
        if (Array.isArray(c.material)) c.material.forEach(mm => mm.dispose());
        else c.material.dispose();
      }
    });
  }
  wolves.splice(i, 1);
}

function clearAllWolves() { while (wolves.length) despawnWolf(0); }

const wolfAshGeo = new THREE.SphereGeometry(1, 6, 4);
const wolfAshMats = [
  new THREE.MeshLambertMaterial({ color: 0x2f2d2a }),
  new THREE.MeshLambertMaterial({ color: 0x48443d }),
  new THREE.MeshLambertMaterial({ color: 0x625c52 }),
];
const wolfAshPiles = [];
function spawnWolfAshPile(x, z) {
  const group = new THREE.Group();
  const y = heightAt(x, z);
  group.userData.life = 8;
  for (let i = 0; i < (LOW ? 8 : 12); i++) {
    const a = Math.random() * Math.PI * 2;
    const rad = Math.random() * 0.42;
    const pebble = new THREE.Mesh(wolfAshGeo, wolfAshMats[(Math.random() * wolfAshMats.length) | 0]);
    const s = 0.035 + Math.random() * 0.08;
    pebble.position.set(x + Math.cos(a) * rad, y + 0.025 + Math.random() * 0.035, z + Math.sin(a) * rad);
    pebble.scale.set(s * (1.2 + Math.random() * 0.8), s * 0.35, s * (1.2 + Math.random() * 0.8));
    group.add(pebble);
  }
  propsGroup.add(group);
  wolfAshPiles.push(group);
  if (wolfAshPiles.length > 8) {
    const old = wolfAshPiles.shift();
    propsGroup.remove(old);
  }
}
function updateWolfAshPiles(dt) {
  for (let i = wolfAshPiles.length - 1; i >= 0; i--) {
    const pile = wolfAshPiles[i];
    pile.userData.life -= dt;
    if (pile.userData.life > 0) continue;
    propsGroup.remove(pile);
    wolfAshPiles.splice(i, 1);
  }
}
function clearWolfAshPiles() {
  while (wolfAshPiles.length) propsGroup.remove(wolfAshPiles.pop());
}

function zipZapWolf(w) {
  const wy = heightAt(w.x, w.z);
  questApi.getWizardZapOrigin(_zipZapFrom);
  _zipZapTo.set(w.x, wy + 0.55, w.z);
  questApi.aimWizardAt(w.x, w.z);
  wizardLightningFx.strike(_zipZapFrom, _zipZapTo);
  spawnWolfAshPile(w.x, w.z);
  spawnDust(w.x, wy + 0.35, w.z, LOW ? 10 : 16, 1.35);
  addCamShake(0.16);
}

// True while a wolf is actively stalking/circling close to the player. Drives
// Chocky's worried face (and suppresses the coin Cheer) without ever taking over
// the skeleton — he must stay free to run.
let wolfChasing = false;

// Diagonal gait phase offsets (FL, FR, BL, BR). A frozen constant — hoisted to
// module scope so the per-wolf, per-frame leg loop reads it instead of
// reallocating an array each frame (GC pressure on Chromebooks).
const WOLF_LEG_PHASE = [0, Math.PI, Math.PI, 0];

function updateWolves(dt, t) {
  if (!WOLVES_ENABLED) { wolfChasing = false; return; }   // cozy default off; ESC-menu / ?spooky opt-in
  const pDist = Math.hypot(player.pos.x, player.pos.z);
  // The far-north plain belongs to the Shell Shockers (see below): no wolves
  // spawn there, and any already on the prowl slink away.
  const inEggZone = player.pos.z > EGG_NORTH_DIST;

  if (!inEggZone && pDist > WOLF_SPAWN_DIST && wolves.length < WOLF_MAX) {
    const urgency = (pDist - WOLF_SPAWN_DIST) * 0.04;
    if (Math.random() < dt * (0.25 + urgency)) spawnWolf();
  }

  let chasing = false;
  const zipPos = questApi.wizardPosition;
  for (let i = wolves.length - 1; i >= 0; i--) {
    const w = wolves[i];
    const dx = player.pos.x - w.x, dz = player.pos.z - w.z;
    const toDist = Math.hypot(dx, dz);
    const wDist = Math.hypot(w.x, w.z);
    const zipDx = zipPos.x - w.x, zipDz = zipPos.z - w.z;
    const nearZip = zipDx * zipDx + zipDz * zipDz < ZIP_WOLF_ZAP_RADIUS * ZIP_WOLF_ZAP_RADIUS;

    if (nearZip) {
      zipZapWolf(w);
      despawnWolf(i);
      continue;
    }

    if (pDist < WOLF_RETREAT_DIST || inEggZone) w.state = 'retreat';

    if (w.state === 'retreat') {
      const a = Math.atan2(w.x, w.z);
      w.x += Math.sin(a) * w.speed * 1.8 * dt;
      w.z += Math.cos(a) * w.speed * 1.8 * dt;
      if (wDist > 42) { despawnWolf(i); continue; }
    } else if (toDist > 3.5) {
      w.x += (dx / toDist) * w.speed * 0.55 * dt;
      w.z += (dz / toDist) * w.speed * 0.55 * dt;
      w.state = 'stalk';
    } else {
      w.state = 'circle';
      w.circleAngle += dt * 0.7;
      const tx = player.pos.x - Math.sin(w.circleAngle) * 3.0;
      const tz = player.pos.z - Math.cos(w.circleAngle) * 3.0;
      w.x += (tx - w.x) * Math.min(1, dt * 2);
      w.z += (tz - w.z) * Math.min(1, dt * 2);
      if (toDist < 1.6) {
        const push = Math.atan2(-player.pos.x, -player.pos.z);
        player.vel.x += Math.sin(push) * 5;
        player.vel.z += Math.cos(push) * 5;
        spawnDust(player.pos.x, initialY + player.pos.y, player.pos.z, 5, 0.9);
        w.state = 'stalk';
      }
    }

    // He's "being chased" while a wolf is closing in (stalk) or circling close.
    if ((w.state === 'stalk' || w.state === 'circle') && toDist < 8) chasing = true;

    w.group.rotation.y = Math.atan2(dx, dz);
    w.group.position.set(w.x, heightAt(w.x, w.z), w.z);

    // Simulation stays full-rate; cosmetic rig posing can step at a lower
    // cadence on Chromebooks without changing enemy steering or collisions.
    if (!_enemyCosmeticReady) continue;

    // ---- Gait + body animation -------------------------------------------
    // Gallop fleeing, a measured trot while stalking, a slinking sidestep while
    // circling. Legs move in a diagonal pattern (FL+BR, then FR+BL).
    const retreat = w.state === 'retreat', circle = w.state === 'circle';
    const ws = retreat ? 13 : (circle ? 7.5 : 8.5);
    const wp = t * ws + w.phase;
    const swing = retreat ? 0.72 : (circle ? 0.42 : 0.5);
    for (let li = 0; li < 4; li++) {
      const leg = w.legs[li];
      const s = Math.sin(wp + WOLF_LEG_PHASE[li]);
      const lift = Math.max(0, s);                     // foot-recovery (up) phase
      leg.hip.rotation.x = leg.baseHip + s * swing;
      // Knee flexes most as the paw lifts, extends through the power stroke.
      leg.knee.rotation.x = leg.baseKnee + lift * (leg.front ? 0.55 : 0.75);
    }

    // Spine bob (vertical push-off) + a low crouch when hunting, level when fleeing.
    const bob = Math.abs(Math.sin(wp)) * (retreat ? 0.05 : 0.03);
    const crouch = w.state === 'stalk' ? 0.05 : (circle ? 0.03 : 0);
    w.spine.position.y = w.baseSpineY - crouch + bob;
    w.spine.rotation.x = (retreat ? -0.06 : 0) + Math.sin(wp) * 0.02;

    // Head: lowered & thrust forward stalking, scanning while circling, up when fleeing.
    const headLow = w.state === 'stalk' ? 0.32 : (circle ? 0.16 : -0.12);
    w.neck.rotation.x = 0.40 + headLow * 0.55;
    w.head.rotation.x = headLow * 0.4 + Math.sin(t * 1.3 + w.phase) * 0.05;
    w.head.rotation.y = Math.sin(t * 0.8 + w.phase) * (circle ? 0.38 : 0.16);

    // Ears: pinned flat when fleeing, swivelling alertly otherwise.
    const earPin = retreat ? -0.7 : Math.sin(t * 2.3 + w.phase) * 0.14;
    w.earL.rotation.x = earPin;
    w.earR.rotation.x = earPin + Math.sin(t * 2.7 + w.phase) * 0.05;

    // Tail: loose & low hunting, sways while circling, tucked hard when fleeing.
    const tailDroop = retreat ? 1.75 : (circle ? 0.72 : 0.88);
    w.tailBase.rotation.x = tailDroop;
    const wag = retreat ? 0.05 : 0.18;
    w.tailBase.rotation.y = Math.sin(t * 2.5 + w.phase) * wag;
    w.tailMid.rotation.y = Math.sin(t * 2.5 + w.phase + 0.5) * wag * 1.3;
    w.tailTip.rotation.y = Math.sin(t * 2.5 + w.phase + 1.0) * wag * 1.6;

    // Eyeshine: a slow amber pulse, flickering toward gold.
    w.eyeGlowMat.opacity = 0.3 + Math.sin(t * 3.5 + w.phase) * 0.22;
    w.eyeMat.color.setHex(Math.sin(t * 2 + w.phase) > 0.3 ? 0xffd24a : 0xffa520);
  }

  // Worried face while a wolf is on him — refreshed each frame so it holds
  // through the chase and fades once they give up. Face only; never freezes him.
  wolfChasing = chasing;
  if (chasing) triggerFace('sad', 0.3);
}


// ============= SKULLFACE — random night ambush =============
// A rare town-setting scare: after dark, if Chocky wanders outside the safe
// central plaza, SKULLFACE can step out of the gloom. He pops close for a quick
// jumpscare, then immediately sprints outward until the fog swallows him.
const SKULLFACE_NIGHT = 0.58;
const SKULLFACE_SAFE_RADIUS = 14;
const SKULLFACE_MAX_TERRITORY = 29;       // keep this encounter near town, before far-north egg territory
const SKULLFACE_APPROACH_SPEED = 2.2;
const SKULLFACE_FLEE_SPEED = 8.5;
let skullface = null;
let skullfaceCooldown = 8;
const skullfaceJumpEl = document.getElementById('skullfaceJump');

function buildSkullface() {
  const root = new THREE.Group();
  const body = new THREE.Group();
  root.add(body);
  const mats = [];
  const mat = (color, basic = false) => {
    const m = basic
      ? new THREE.MeshBasicMaterial({ color, fog: false })
      : new THREE.MeshLambertMaterial({ color });
    m.transparent = true;
    m.opacity = 1;
    mats.push(m);
    return m;
  };
  const skullMat = mat(0xf2eee2);
  const boneMat = mat(0xd8c4a6);
  const blackMat = mat(0x08090c);
  const shirtMat = mat(0x12506a);
  const shirtDark = mat(0x0b3548);
  const pantsMat = mat(0x173f52);
  const bootMat = mat(0x221612);
  const eyeMat = mat(0xe00000, true);
  const featureMat = mat(0x14110f, true);

  function box(parent, x, y, z, w, h, d, material) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
    mesh.position.set(x, y, z);
    mesh.castShadow = !ULTRA_LOW_SHADOWS;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  }

  box(body, 0, 0.92, 0, 0.74, 0.72, 0.38, shirtMat);
  box(body, 0, 0.98, 0.205, 0.56, 0.20, 0.035, shirtDark);  // front jacket fold
  const head = new THREE.Group();
  head.position.set(0, 1.52, 0.03);
  body.add(head);
  box(head, 0, 0, 0, 0.76, 0.56, 0.58, skullMat);
  box(head, 0, 0.31, -0.10, 0.74, 0.13, 0.46, blackMat);     // dark cap/hair top
  box(head, -0.23, -0.02, 0.305, 0.18, 0.085, 0.025, eyeMat);
  box(head,  0.23, -0.02, 0.305, 0.18, 0.085, 0.025, eyeMat);
  const browL = box(head, -0.23, 0.08, 0.32, 0.24, 0.045, 0.025, skullMat);
  browL.rotation.z = 0.18;
  const browR = box(head, 0.23, 0.08, 0.32, 0.24, 0.045, 0.025, skullMat);
  browR.rotation.z = -0.18;
  box(head, 0, -0.10, 0.322, 0.09, 0.13, 0.025, featureMat); // nose hole
  box(head, 0, -0.26, 0.325, 0.30, 0.06, 0.025, featureMat); // skull mouth
  for (let i = -2; i <= 2; i++) box(head, i * 0.055, -0.30, 0.34, 0.022, 0.08, 0.018, skullMat);
  box(head, -0.43, -0.08, 0.02, 0.10, 0.24, 0.34, skullMat);
  box(head,  0.43, -0.08, 0.02, 0.10, 0.24, 0.34, skullMat);

  function arm(side) {
    const g = new THREE.Group();
    g.position.set(side * 0.47, 1.12, 0);
    body.add(g);
    box(g, 0, -0.23, 0, 0.18, 0.50, 0.20, blackMat);
    box(g, 0, -0.56, 0.02, 0.18, 0.22, 0.18, boneMat);
    return g;
  }
  const armL = arm(-1), armR = arm(1);
  function leg(side) {
    const g = new THREE.Group();
    g.position.set(side * 0.20, 0.55, 0);
    body.add(g);
    box(g, 0, -0.28, 0, 0.26, 0.58, 0.25, pantsMat);
    box(g, 0, -0.63, 0.03, 0.28, 0.15, 0.32, bootMat);
    return g;
  }
  const legL = leg(-1), legR = leg(1);

  root.scale.setScalar(1.05);
  return { root, body, head, armL, armR, legL, legR, mats, phase: Math.random() * Math.PI * 2 };
}

function setSkullfaceOpacity(k) {
  if (!skullface) return;
  for (const m of skullface.mats) m.opacity = k;
}

function spawnSkullface() {
  if (skullface || !gameMode || player.dead) return;
  const s = buildSkullface();
  const ahead = player.facing + (Math.random() - 0.5) * 1.5;
  let x = player.pos.x + Math.sin(ahead) * (7 + Math.random() * 3.5);
  let z = player.pos.z + Math.cos(ahead) * (7 + Math.random() * 3.5);
  const fromTown = Math.atan2(x, z);
  const r = Math.max(SKULLFACE_SAFE_RADIUS + 2, Math.hypot(x, z));
  x = Math.sin(fromTown) * r;
  z = Math.cos(fromTown) * r;
  s.x = x; s.z = z;
  s.state = 'stalk';
  s.timer = 4.2;
  s.opacity = 0.25;
  s.root.position.set(x, initialY + heightAt(x, z), z);
  propsGroup.add(s.root);
  s.actor = spawnActor('skullface', s);
  skullface = s;
  setSkullfaceOpacity(s.opacity);
}

function triggerSkullfaceJumpscare() {
  if (!skullface || skullface.state === 'scare' || skullface.state === 'flee') return;
  skullface.state = 'scare';
  skullface.timer = 0.48;
  skullface.opacity = 1;
  const a = player.facing;
  skullface.x = player.pos.x + Math.sin(a) * 1.15;
  skullface.z = player.pos.z + Math.cos(a) * 1.15;
  skullface.fleeAngle = Math.atan2(skullface.x - player.pos.x, skullface.z - player.pos.z);
  skullface.root.position.set(skullface.x, initialY + heightAt(skullface.x, skullface.z), skullface.z);
  skullface.root.rotation.y = Math.atan2(player.pos.x - skullface.x, player.pos.z - skullface.z);
  setSkullfaceOpacity(1);
  if (skullfaceJumpEl) skullfaceJumpEl.style.display = 'grid';
  addCamShake(0.75);
  triggerFace('surprised', 0.8);
  showBumpMsg('SKULLFACE!', 'He vanishes into the dark...', 1.4, 'skullface');
}

function removeSkullface() {
  if (!skullface) return;
  if (skullfaceJumpEl) skullfaceJumpEl.style.display = 'none';
  propsGroup.remove(skullface.root);
  skullface.root.traverse(c => {
    if (c.geometry) c.geometry.dispose();
    if (c.material) {
      if (Array.isArray(c.material)) c.material.forEach(m => m.dispose());
      else c.material.dispose();
    }
  });
  despawnActor(skullface.actor);
  skullface = null;
}
function clearSkullface() { removeSkullface(); skullfaceCooldown = 8; }

function updateSkullface(dt, t) {
  if (!SPOOKY) return;   // cozy profile: no jumpscares, ever
  const pDist = Math.hypot(player.pos.x, player.pos.z);
  const eligible = gameMode && !player.dead && skyNightFactor > SKULLFACE_NIGHT &&
                   pDist > SKULLFACE_SAFE_RADIUS && pDist < SKULLFACE_MAX_TERRITORY;
  skullfaceCooldown = Math.max(0, skullfaceCooldown - dt);
  if (!skullface && eligible && skullfaceCooldown <= 0) {
    const chance = dt * (LOW ? 0.025 : 0.04) * (0.7 + skyNightFactor);
    if (Math.random() < chance) spawnSkullface();
  }
  if (!skullface) return;

  const dx = player.pos.x - skullface.x;
  const dz = player.pos.z - skullface.z;
  const dist = Math.max(0.001, Math.hypot(dx, dz));
  skullface.timer -= dt;

  if (skullface.state === 'stalk') {
    if (!eligible) { skullface.state = 'flee'; skullface.timer = 2.2; skullface.fleeAngle = Math.atan2(-dx, -dz); }
    else {
      skullface.x += (dx / dist) * SKULLFACE_APPROACH_SPEED * dt;
      skullface.z += (dz / dist) * SKULLFACE_APPROACH_SPEED * dt;
      skullface.opacity = Math.min(1, skullface.opacity + dt * 0.45);
      if (dist < 3.0 || skullface.timer <= 0) triggerSkullfaceJumpscare();
    }
  } else if (skullface.state === 'scare') {
    if (skullfaceJumpEl && skullface.timer < 0.12) skullfaceJumpEl.style.display = 'none';
    if (skullface.timer <= 0) {
      skullface.state = 'flee';
      skullface.timer = 3.0;
      skullface.fleeAngle = Math.atan2(skullface.x - player.pos.x, skullface.z - player.pos.z);
      spawnDust(skullface.x, initialY + heightAt(skullface.x, skullface.z) + 0.05, skullface.z, 12, 1.2);
    }
  } else if (skullface.state === 'flee') {
    skullface.x += Math.sin(skullface.fleeAngle) * SKULLFACE_FLEE_SPEED * dt;
    skullface.z += Math.cos(skullface.fleeAngle) * SKULLFACE_FLEE_SPEED * dt;
    skullface.opacity = Math.max(0, skullface.opacity - dt * 0.38);
    if (skullface.timer <= 0 || Math.hypot(skullface.x - player.pos.x, skullface.z - player.pos.z) > 24) {
      removeSkullface();
      skullfaceCooldown = 32 + Math.random() * 28;
      return;
    }
  }

  const run = skullface.state === 'flee';
  const gait = t * (run ? 14 : 7) + skullface.phase;
  skullface.body.position.y = Math.abs(Math.sin(gait)) * (run ? 0.08 : 0.035);
  skullface.armL.rotation.x =  Math.sin(gait) * (run ? 1.0 : 0.28);
  skullface.armR.rotation.x = -Math.sin(gait) * (run ? 1.0 : 0.28);
  skullface.legL.rotation.x = -Math.sin(gait) * (run ? 0.9 : 0.35);
  skullface.legR.rotation.x =  Math.sin(gait) * (run ? 0.9 : 0.35);
  skullface.head.rotation.y = Math.sin(t * 6 + skullface.phase) * (skullface.state === 'scare' ? 0.18 : 0.07);
  const faceYaw = skullface.state === 'flee' ? skullface.fleeAngle : Math.atan2(dx, dz);
  skullface.root.rotation.y = faceYaw;
  skullface.root.position.set(skullface.x, initialY + heightAt(skullface.x, skullface.z), skullface.z);
  setSkullfaceOpacity(skullface.opacity);
}

// ============= SHELL SHOCKER EGGS — north easter egg =============
// Stray TOO FAR NORTH (well past the wolf-haunted plain) and the wolves give
// way to a squad of "Shell Shockers": floating, man-sized eggs with cartoon
// white-glove hands toting little laser guns. They lock onto Chocky and open
// fire on sight — three hits and he disintegrates into a pile of ash, then
// regenerates back at the town square three seconds later. North is +Z here
// (the big tree / north fences live at z≈+9), so the trigger is a large +Z.
const EGG_NORTH_DIST = 34;        // player.pos.z beyond this = Shell Shocker territory
const EGG_MAX = TUNING.eggMax ?? (LOW ? 2 : 3);
const EGG_SPEED = 1.9;
const EGG_HOVER_DIST = 7.5;       // standoff distance the eggs like to keep
const EGG_FIRE_RANGE = 16;
const EGG_FIRE_COOLDOWN = 1.7;    // base seconds between an egg's shots
const EGG_HITS_TO_KILL = 3;
const EGG_LASER_SPEED = 16;
const eggs = [];

// Shared laser-bolt geometry/material (additive green tracer, never disposed).
const laserBoltGeo = new THREE.BoxGeometry(0.07, 0.07, 0.75);
const laserBoltMat = new THREE.MeshBasicMaterial({
  color: 0x8dff9e, transparent: true, opacity: 0.95,
  blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
});
const LASER_POOL = poolScale(LOW ? 8 : 16);
const lasers = [];
for (let i = 0; i < LASER_POOL; i++) {
  const mesh = new THREE.Mesh(laserBoltGeo, laserBoltMat);
  mesh.visible = false;
  scene.add(mesh);
  lasers.push({ mesh, vx: 0, vy: 0, vz: 0, life: 0 });
}

// A black bandana with a white skull, drawn once and wrapped around each egg's
// "face". The skull sits at the centre (u=0.5) so it lands on the front of the
// arc; the cloth runs out to either side.
function makeBandanaTexture() {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 128;
  const ctx = c.getContext('2d');
  // Cloth base + a couple of darker folds for a bit of texture.
  ctx.fillStyle = '#16161a'; ctx.fillRect(0, 0, 256, 128);
  ctx.fillStyle = 'rgba(0,0,0,0.25)';
  ctx.fillRect(0, 0, 30, 128); ctx.fillRect(226, 0, 30, 128);
  // ---- White skull, centred.
  const cx = 128, cy = 60;
  ctx.fillStyle = '#f1ede2';
  // Cranium (rounded) + cheeks.
  ctx.beginPath(); ctx.arc(cx, cy - 6, 30, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx - 26, cy + 6);
  ctx.quadraticCurveTo(cx - 22, cy + 34, cx, cy + 34);
  ctx.quadraticCurveTo(cx + 22, cy + 34, cx + 26, cy + 6);
  ctx.fill();
  // Jaw block.
  ctx.fillRect(cx - 15, cy + 24, 30, 16);
  // Eye sockets (angled inward for a mean look).
  ctx.fillStyle = '#111';
  function socket(sx) {
    ctx.save(); ctx.translate(cx + sx, cy - 4); ctx.rotate(sx > 0 ? -0.3 : 0.3);
    ctx.beginPath(); ctx.ellipse(0, 0, 10, 12, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  socket(-13); socket(13);
  // Nose.
  ctx.beginPath(); ctx.moveTo(cx, cy + 6); ctx.lineTo(cx - 6, cy + 18);
  ctx.lineTo(cx + 6, cy + 18); ctx.closePath(); ctx.fill();
  // Teeth gaps.
  for (let i = -2; i <= 2; i++) ctx.fillRect(cx + i * 6 - 1.5, cy + 24, 3, 16);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const bandanaTex = cachedTexture('bandana', makeBandanaTexture);

function makeEggShellGeometry() {
  // A sphere tapered toward the top into a proper egg profile. Build it once so
  // low-tier Shell Shockers can be hidden/reused without geometry churn.
  const shellGeo = new THREE.SphereGeometry(0.46, 20, 22);
  const pos = shellGeo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    const k = y > 0 ? 1 - y * 0.9 : 1 + y * 0.1;   // pinch in above the equator
    pos.setX(i, pos.getX(i) * k);
    pos.setZ(i, pos.getZ(i) * k);
  }
  shellGeo.scale(1, 1.32, 1);
  shellGeo.computeVertexNormals();
  return shellGeo;
}

const shellShockerShared = {
  shellGeo: makeEggShellGeometry(),
  bandanaGeo: new THREE.CylinderGeometry(0.47, 0.5, 0.5, 28, 1, true, -Math.PI * 0.55, Math.PI * 1.1),
  knotGeo: new THREE.BoxGeometry(0.12, 0.12, 0.1),
  clothTailGeo: new THREE.BoxGeometry(0.05, 0.24, 0.04),
  mittGeo: new THREE.SphereGeometry(0.135, 12, 12),
  thumbGeo: new THREE.SphereGeometry(0.06, 8, 8),
  cuffGeo: new THREE.BoxGeometry(0.16, 0.07, 0.16),
  barrelGeo: new THREE.BoxGeometry(0.08, 0.08, 0.36),
  gripGeo: new THREE.BoxGeometry(0.07, 0.16, 0.08),
  muzzleGeo: new THREE.SphereGeometry(0.05, 8, 8),
  lowShellMat: new THREE.MeshLambertMaterial({ color: 0xf4ead8 }),
  whiteMat: new THREE.MeshLambertMaterial({ color: 0xffffff }),
  bandanaMat: new THREE.MeshLambertMaterial({ map: bandanaTex, side: THREE.DoubleSide }),
  clothMat: new THREE.MeshLambertMaterial({ color: 0x16161a }),
  cuffMat: new THREE.MeshLambertMaterial({ color: 0xd8d2c4 }),
  gunMetalMat: new THREE.MeshLambertMaterial({ color: 0x33373d }),
  gunDarkMat: new THREE.MeshLambertMaterial({ color: 0x202327 }),
  muzzleMat: new THREE.MeshBasicMaterial({ color: 0x8dff9e }),
};
const shellShockerSharedGeos = new Set([
  shellShockerShared.shellGeo, shellShockerShared.bandanaGeo, shellShockerShared.knotGeo,
  shellShockerShared.clothTailGeo, shellShockerShared.mittGeo, shellShockerShared.thumbGeo,
  shellShockerShared.cuffGeo, shellShockerShared.barrelGeo, shellShockerShared.gripGeo,
  shellShockerShared.muzzleGeo,
]);
const shellShockerSharedMats = new Set([
  shellShockerShared.lowShellMat, shellShockerShared.whiteMat, shellShockerShared.bandanaMat,
  shellShockerShared.clothMat, shellShockerShared.cuffMat, shellShockerShared.gunMetalMat,
  shellShockerShared.gunDarkMat, shellShockerShared.muzzleMat,
]);
const lowEggPool = [];

// Build one Shell Shocker: a squashed-sphere egg shell wearing a skull bandana,
// two floating cartoon gloves, and a laser pistol. Front faces local +Z (toward
// the player when it turns to aim, like the wolves). Low-tier eggs use stable
// shared geometry/materials and are pooled; high-tier eggs keep their random
// shell tint variation while still reusing stable meshes/materials.
function buildShellShocker(pooled = false) {
  const root = new THREE.Group();
  root.visible = !pooled;
  const body = new THREE.Group();   // bob/hover pivot for the whole egg
  root.add(body);

  const shellMat = pooled ? shellShockerShared.lowShellMat : new THREE.MeshLambertMaterial({
    color: new THREE.Color(0xf4ead8).multiplyScalar(0.92 + Math.random() * 0.12),
  });
  const shell = new THREE.Mesh(shellShockerShared.shellGeo, shellMat);
  shell.castShadow = true;
  body.add(shell);

  // ---- Skull bandana wrapped across the front of the egg's face. The arc is
  // centred on +Z so the skull shows when the egg turns to face the player.
  const bandana = new THREE.Mesh(shellShockerShared.bandanaGeo, shellShockerShared.bandanaMat);
  bandana.position.y = 0.12;
  body.add(bandana);
  // Knot + two trailing tails at the back of the head.
  const knot = new THREE.Mesh(shellShockerShared.knotGeo, shellShockerShared.clothMat);
  knot.position.set(0, 0.12, -0.44);
  body.add(knot);
  for (const s of [-1, 1]) {
    const tail = new THREE.Mesh(shellShockerShared.clothTailGeo, shellShockerShared.clothMat);
    tail.position.set(s * 0.06, -0.02, -0.46);
    tail.rotation.set(0.35, 0, s * 0.35);
    body.add(tail);
  }

  // ---- Two floating cartoon gloves (white mitten + thumb nub).
  function glove(side) {
    const g = new THREE.Group();
    const mitt = new THREE.Mesh(shellShockerShared.mittGeo, shellShockerShared.whiteMat);
    g.add(mitt);
    const thumb = new THREE.Mesh(shellShockerShared.thumbGeo, shellShockerShared.whiteMat);
    thumb.position.set(side * 0.1, 0.06, 0.06);
    g.add(thumb);
    const cuff = new THREE.Mesh(shellShockerShared.cuffGeo, shellShockerShared.cuffMat);
    cuff.position.y = -0.13;
    g.add(cuff);
    g.position.set(side * 0.52, -0.06, 0.16);
    body.add(g);
    return g;
  }
  const gloveL = glove(-1);
  const gloveR = glove(1);

  // ---- Laser pistol clutched by the right glove, barrel pointing forward (+Z).
  const barrel = new THREE.Mesh(shellShockerShared.barrelGeo, shellShockerShared.gunMetalMat);
  barrel.position.z = 0.15;
  gloveR.add(barrel);
  const grip = new THREE.Mesh(shellShockerShared.gripGeo, shellShockerShared.gunDarkMat);
  grip.position.set(0, -0.09, 0.02);
  gloveR.add(grip);
  const muzzle = new THREE.Mesh(shellShockerShared.muzzleGeo, shellShockerShared.muzzleMat);
  muzzle.position.z = 0.34;
  gloveR.add(muzzle);
  const muzzleGlow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTex, color: 0x8dff9e, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, fog: true,
  }));
  muzzleGlow.scale.set(0.5, 0.5, 1);
  muzzleGlow.position.z = 0.36;
  gloveR.add(muzzleGlow);

  return { root, body, gloveL, gloveR, muzzleGlow, shellMat, pooled, active: false };
}

function acquireLowEgg() {
  while (lowEggPool.length < EGG_MAX) {
    const egg = buildShellShocker(true);
    propsGroup.add(egg.root);
    lowEggPool.push(egg);
  }
  const egg = lowEggPool.find(e => !e.active);
  if (!egg) return null;
  egg.active = true;
  egg.root.visible = true;
  return egg;
}

function spawnEgg() {
  const egg = LOW ? acquireLowEgg() : buildShellShocker();
  if (!egg) return;
  if (!egg.pooled) propsGroup.add(egg.root);
  // Fan out ahead of the player, biased further north so they emerge from the
  // fog he's walking into.
  const ang = (Math.random() - 0.5) * Math.PI * 0.9;
  const dist = EGG_HOVER_DIST + 3 + Math.random() * 6;
  egg.x = player.pos.x + Math.sin(ang) * dist;
  egg.z = player.pos.z + Math.cos(ang) * dist + 2;
  egg.hover = 1.05 + Math.random() * 0.4;
  egg.bobPhase = Math.random() * Math.PI * 2;
  egg.fireTimer = 0.7 + Math.random() * EGG_FIRE_COOLDOWN;
  egg.muzzleFlash = 0;
  egg.muzzleGlow.material.opacity = 0;
  egg.fireY = initialY + heightAt(egg.x, egg.z) + egg.hover;
  // Start with its back to Chocky, then turn around shortly after appearing so
  // its skull bandana is revealed.
  const aim0 = Math.atan2(player.pos.x - egg.x, player.pos.z - egg.z);
  egg.yaw = aim0 + Math.PI;
  egg.faceAway = true;
  egg.turnTimer = 0.5 + Math.random() * 0.7;
  egg.root.rotation.y = egg.yaw;
  egg.actor = spawnActor('shellshocker', egg);
  eggs.push(egg);
}

function disposeShellShockerPart(c) {
  if (c.geometry && !shellShockerSharedGeos.has(c.geometry)) c.geometry.dispose();
  if (!c.material) return;
  const mats = Array.isArray(c.material) ? c.material : [c.material];
  for (const mat of mats) {
    if (!shellShockerSharedMats.has(mat)) mat.dispose();
  }
}

function removeEgg(i) {
  const egg = eggs[i];
  if (egg.pooled) {
    egg.active = false;
    egg.root.visible = false;
    egg.muzzleFlash = 0;
    egg.muzzleGlow.material.opacity = 0;
  } else {
    propsGroup.remove(egg.root);
    egg.root.traverse(disposeShellShockerPart);
  }
  despawnActor(egg.actor);
  eggs.splice(i, 1);
}
function clearAllEggs() { while (eggs.length) removeEgg(0); }

function fireLaser(egg) {
  const dx = player.pos.x - egg.x, dz = player.pos.z - egg.z;
  const d = Math.hypot(dx, dz) || 1;
  // Muzzle a little out in front of the egg, toward Chocky.
  const ox = egg.x + (dx / d) * 0.6;
  const oz = egg.z + (dz / d) * 0.6;
  const oy = egg.fireY - 0.05;
  // Aim at Chocky's torso (center ~0.7 above his feet).
  const tx = player.pos.x, ty = initialY + player.pos.y + 0.7, tz = player.pos.z;
  const vx = tx - ox, vy = ty - oy, vz = tz - oz;
  const len = Math.hypot(vx, vy, vz) || 1;
  const shot = lasers.find(L => !L.mesh.visible);
  if (!shot) return;
  shot.mesh.position.set(ox, oy, oz);
  shot.mesh.lookAt(tx, ty, tz);   // bolt's +Z lines up with the shot
  shot.vx = (vx / len) * EGG_LASER_SPEED;
  shot.vy = (vy / len) * EGG_LASER_SPEED;
  shot.vz = (vz / len) * EGG_LASER_SPEED;
  shot.life = 2.5;
  shot.mesh.visible = true;
  egg.muzzleFlash = 0.12;
  spawnDust(ox, oy, oz, 2, 0.3);
}

function deactivateLaser(L) {
  L.life = 0;
  L.mesh.visible = false;
}
function clearAllLasers() {
  for (const L of lasers) deactivateLaser(L);
}

function updateLasers(dt) {
  for (let i = lasers.length - 1; i >= 0; i--) {
    const L = lasers[i];
    if (!L.mesh.visible) continue;
    L.life -= dt;
    L.mesh.position.x += L.vx * dt;
    L.mesh.position.y += L.vy * dt;
    L.mesh.position.z += L.vz * dt;
    if (!player.dead) {
      const px = player.pos.x, py = initialY + player.pos.y + 0.7, pz = player.pos.z;
      const hit = Math.hypot(
        L.mesh.position.x - px, L.mesh.position.y - py, L.mesh.position.z - pz);
      if (hit < 0.6) { deactivateLaser(L); eggHitPlayer(); continue; }
    }
    if (L.life <= 0) deactivateLaser(L);
  }
}

// ---- Ash pile: where Chocky stood when he disintegrated. A flattened mound of
// grey embers that vanishes when he regenerates.
let ashGroup = null;
function spawnAshPile(x, z) {
  removeAshPile();
  ashGroup = new THREE.Group();
  const cols = [0x3c3833, 0x524d46, 0x6b655b];
  for (let i = 0; i < 16; i++) {
    const r = 0.05 + Math.random() * 0.11;
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(r, 6, 5),
      new THREE.MeshLambertMaterial({ color: cols[(Math.random() * cols.length) | 0] }));
    const a = Math.random() * Math.PI * 2, rad = Math.random() * 0.42;
    m.position.set(x + Math.cos(a) * rad, initialY + heightAt(x, z) + 0.02 + Math.random() * 0.06, z + Math.sin(a) * rad);
    m.scale.y = 0.4 + Math.random() * 0.3;   // flattened
    ashGroup.add(m);
  }
  scene.add(ashGroup);
}
function removeAshPile() {
  if (!ashGroup) return;
  scene.remove(ashGroup);
  ashGroup.traverse(c => {
    if (c.geometry) c.geometry.dispose();
    if (c.material) c.material.dispose();
  });
  ashGroup = null;
}

// ---- Chocky takes a hit. Brief i-frames keep one bolt from counting twice.
function eggHitPlayer() {
  if (player.dead || player.invuln > 0) return;
  player.hits++;
  player.invuln = 0.6;
  addCamShake(0.22);
  triggerFace('dizzy', 0.5);
  spawnDust(player.pos.x, initialY + player.pos.y + 0.6, player.pos.z, 8, 1.0);
  updateEggHud();
  if (player.hits >= EGG_HITS_TO_KILL) disintegratePlayer();
}

function disintegratePlayer() {
  player.dead = true;
  player.deadTimer = 3.0;
  player.vel.set(0, 0, 0);
  if (chocky) chocky.visible = false;
  spawnAshPile(player.pos.x, player.pos.z);
  spawnDust(player.pos.x, initialY + player.pos.y + 0.5, player.pos.z, 30, 2.0);
  addCamShake(0.4);
  // The Shell Shockers stand down once their target is ash.
  clearAllEggs();
  clearAllLasers();
  updateEggHud();
}

function regeneratePlayer() {
  const spawn = getVillageRespawnCheckpoint();
  removeAshPile();
  clearShellShockers();
  clearSkullface();
  clearAllWolves();
  player.dead = false;
  player.hits = 0;
  player.invuln = 1.2;          // a moment of grace at the checkpoint
  resetPlayerMovementState({ position: spawn.pos, facing: spawn.facing });
  if (chocky) {
    chocky.visible = true;
    chocky.position.set(spawn.pos.x, initialY + spawn.pos.y, spawn.pos.z);
    chocky.rotation.y = spawn.facing;
  }
  spawnDust(spawn.pos.x, initialY + spawn.pos.y, spawn.pos.z, 24, 1.8);
  triggerFace('surprised', 0.6);
  updateEggHud();
}

let eggHudShown = '';
function updateEggHud() {
  const hud = document.getElementById('eggHud');
  if (!hud) return;
  const inZone = SHELL_SHOCKERS_ENABLED && gameMode && player.pos.z > EGG_NORTH_DIST;
  // Called from the frame loop — skip the DOM writes (and the emoji string
  // builds) unless the visible state actually changed. When Shell Shockers are
  // toggled off, hide their whole warning/HP HUD even if the player is still in
  // the north zone or has leftover hit state from before the toggle.
  const state = !SHELL_SHOCKERS_ENABLED ? 'hidden'
    : player.dead ? 'dead'
    : (inZone || (gameMode && player.hits > 0)) ? 'shock' + player.hits
    : 'hidden';
  if (state === eggHudShown) return;
  eggHudShown = state;
  if (state === 'dead') {
    hud.style.display = 'block';
    setDomText('eggWarn', '💀 DISINTEGRATED');
    setDomText('eggHp', 'regenerating…');
  } else if (state.startsWith('shock')) {
    hud.style.display = 'block';
    setDomText('eggWarn', '⚠ SHELL SHOCKERS!');
    const left = Math.max(0, EGG_HITS_TO_KILL - player.hits);
    setDomText('eggHp', '🥚'.repeat(left) + '💥'.repeat(player.hits));
  } else {
    hud.style.display = 'none';
  }
}

function clearShellShockers() {
  clearAllEggs();
  clearAllLasers();
  removeAshPile();
}

function updateShellShockers(dt, t) {
  if (!SHELL_SHOCKERS_ENABLED) return;   // cozy default off; ESC-menu / ?spooky opt-in
  const inZone = player.pos.z > EGG_NORTH_DIST && !player.dead;
  if (inZone && eggs.length < EGG_MAX) {
    if (Math.random() < dt * 0.6) spawnEgg();
  }

  for (let i = eggs.length - 1; i >= 0; i--) {
    const egg = eggs[i];
    const dx = player.pos.x - egg.x, dz = player.pos.z - egg.z;
    const dist = Math.hypot(dx, dz) || 0.0001;
    const leaving = !inZone || dist > 30;

    egg.bobPhase += dt;
    const hoverY = initialY + heightAt(egg.x, egg.z) + egg.hover + Math.sin(t * 2 + egg.bobPhase) * 0.12;
    egg.fireY = hoverY;   // muzzle height for any shot fired this frame

    const aim = Math.atan2(dx, dz);   // yaw that points the skull face at Chocky

    if (leaving) {
      // Retreat north into the fog (back turned) and despawn once well away.
      egg.z += EGG_SPEED * 1.6 * dt;
      egg.fireTimer = 1.0;
      egg.faceAway = true;
      if (dist > 36) { removeEgg(i); continue; }
    } else {
      // Hold a standoff distance; drift in or out to keep range, plus a slow strafe.
      if (dist > EGG_HOVER_DIST + 1) {
        egg.x += (dx / dist) * EGG_SPEED * dt;
        egg.z += (dz / dist) * EGG_SPEED * dt;
      } else if (dist < EGG_HOVER_DIST - 1) {
        egg.x -= (dx / dist) * EGG_SPEED * dt;
        egg.z -= (dz / dist) * EGG_SPEED * dt;
      }
      egg.x += Math.cos(t * 0.6 + egg.bobPhase) * EGG_SPEED * 0.35 * dt;
      // Occasionally turn its back, then swivel around again to reveal the
      // skull. Faces the player most of the time; only fires while it does.
      egg.turnTimer -= dt;
      if (egg.turnTimer <= 0) {
        egg.faceAway = !egg.faceAway;
        egg.turnTimer = egg.faceAway ? (1.0 + Math.random() * 1.2)
                                     : (3.5 + Math.random() * 3.5);
      }
    }

    // Smoothly swivel toward the desired yaw (front to the player, or away).
    const targetYaw = aim + (egg.faceAway ? Math.PI : 0);
    let dyaw = targetYaw - egg.yaw;
    while (dyaw > Math.PI) dyaw -= Math.PI * 2;
    while (dyaw < -Math.PI) dyaw += Math.PI * 2;
    egg.yaw += dyaw * Math.min(1, dt * 5);

    // Fire only when it has turned far enough to actually be facing Chocky.
    const facingPlayer = !egg.faceAway && Math.cos(egg.yaw - aim) > 0.55;
    if (!leaving) {
      egg.fireTimer -= dt;
      if (facingPlayer && dist < EGG_FIRE_RANGE && egg.fireTimer <= 0) {
        fireLaser(egg);
        egg.fireTimer = EGG_FIRE_COOLDOWN + Math.random() * 0.9;
      }
    }

    egg.root.position.set(egg.x, hoverY, egg.z);
    egg.root.rotation.y = egg.yaw;

    // Cosmetic: gloves bob out of sync, muzzle flash decays. Throttle this
    // on low-tier hardware; firing, hit checks and root movement stay full-rate.
    if (_enemyCosmeticReady) {
      egg.muzzleFlash = Math.max(0, egg.muzzleFlash - _enemyCosmeticDt);
      egg.muzzleGlow.material.opacity = Math.min(1, egg.muzzleFlash * 7);
      egg.gloveL.position.y = -0.06 + Math.sin(t * 4 + egg.bobPhase) * 0.03;
      egg.gloveR.position.y = -0.06 + Math.sin(t * 4 + egg.bobPhase + 1) * 0.03;
    }
  }

  updateLasers(dt);
}

// ============= DAY / NIGHT CYCLE =============
// A continuous ~10-minute loop that drives the sun's arc, light colours and
// intensities, the sky/fog colour and the "nightness" the ambience reads.
const DAY_LENGTH = 600;        // seconds for one full day (~10 minutes)
let dayTime = 0.42;            // start mid-morning so the scene opens bright
let dayNightEnabled = true;

// Keyframes around the day. t = fraction of the day; n = nightness (0..1).
const dayStops = [
  // Night is kept moonlit (not pitch black) so the village stays navigable.
  { t: 0.00, sky: 0x222d4f, sun: 0xb5bfdf, sunI: 0.40, hSky: 0x40537f, hGnd: 0x34333b, hI: 0.46, fillI: 0.19, rimI: 0.18, n: 1.00 },
  { t: 0.22, sky: PALETTE.nightBlue, sun: 0xaeb8dc, sunI: 0.50, hSky: 0x526991, hGnd: 0x3a3b3f, hI: 0.45, fillI: 0.20, rimI: 0.20, n: 0.78 },
  { t: 0.27, sky: PALETTE.skyPeach, sun: 0xf0bd88, sunI: 1.32, hSky: 0xd6c2c4, hGnd: 0x7f6b58, hI: 0.46, fillI: 0.30, rimI: 0.42, n: 0.25 },
  { t: 0.34, sky: PALETTE.skyMorning, sun: 0xf4dca8, sunI: 1.85, hSky: PALETTE.skyBlueSoft, hGnd: PALETTE.sand, hI: 0.50, fillI: 0.32, rimI: 0.42, n: 0.05 },
  { t: 0.50, sky: PALETTE.skyCream, sun: PALETTE.panel, sunI: 2.12, hSky: PALETTE.skyBlueSoft, hGnd: PALETTE.sand, hI: 0.55, fillI: 0.35, rimI: 0.42, n: 0.00 },
  { t: 0.66, sky: 0xf2ead8, sun: 0xf3e0b8, sunI: 2.00, hSky: PALETTE.skyBlueSoft, hGnd: PALETTE.sand, hI: 0.50, fillI: 0.34, rimI: 0.45, n: 0.02 },
  { t: 0.75, sky: 0xefc59d, sun: 0xeeb07f, sunI: 1.72, hSky: 0xe3c4b5, hGnd: 0x8b715d, hI: 0.42, fillI: 0.43, rimI: 0.58, n: 0.18 },
  { t: 0.81, sky: PALETTE.skyRose, sun: 0xe99a82, sunI: 1.10, hSky: 0xa78ba2, hGnd: 0x5a4a4c, hI: 0.40, fillI: 0.33, rimI: 0.42, n: 0.48 },
  { t: 0.88, sky: 0x4d5178, sun: 0xa2acd4, sunI: 0.58, hSky: 0x636a94, hGnd: 0x34343a, hI: 0.42, fillI: 0.23, rimI: 0.23, n: 0.80 },
  { t: 1.00, sky: 0x222d4f, sun: 0xb5bfdf, sunI: 0.40, hSky: 0x40537f, hGnd: 0x34333b, hI: 0.46, fillI: 0.19, rimI: 0.18, n: 1.00 },
];
const _c1 = new THREE.Color(), _c2 = new THREE.Color(), _sunTmp = new THREE.Vector3();
const zoneVisual = { fog: new THREE.Color(0xffffff), sky: new THREE.Color(0xffffff), warm: 0 };
let zoneVisualTarget = 'village';
function updateZoneVisualTarget() {
  if (!gameMode) { zoneVisualTarget = 'village'; return; }
  const { x, z } = player.pos;
  if (x > 26) zoneVisualTarget = 'wordville';
  else if (x < -26) zoneVisualTarget = 'grove';
  else if (z > 26) zoneVisualTarget = 'obby';
  else if (z < -34) zoneVisualTarget = 'numberton';
  else if (z < -26) zoneVisualTarget = 'green';
  else zoneVisualTarget = 'village';
}
// The sun→target offset (length 12, direction = current sun) and the world point
// the shadow frustum is centred on. In play mode updateShadowFollow() slides the
// target onto Chocky so his shadow stays inside the ortho box out on the hills;
// otherwise it sits at the origin (turntable Chocky / village). applyDayNight
// recomputes the offset and re-pins the light to whatever the target currently is.
const keySunOffset = new THREE.Vector3(6, 10, 5);
const shadowFollowTarget = new THREE.Vector3(0, 0, 0);
// Declared here (not with the animation system below) because applyDayNight's
// window-glow flicker reads it and applyDayNight is seeded at module top level
// — a later declaration would be a temporal-dead-zone crash at boot.
let animTime = 0;
function applyDayNight(t) {
  // Find the surrounding keyframes and the blend factor between them.
  let a = dayStops[0], b = dayStops[dayStops.length - 1];
  for (let i = 0; i < dayStops.length - 1; i++) {
    if (t >= dayStops[i].t && t <= dayStops[i+1].t) { a = dayStops[i]; b = dayStops[i+1]; break; }
  }
  const f = Math.min(1, Math.max(0, (t - a.t) / ((b.t - a.t) || 1)));
  const mix = (x, y) => x + (y - x) * f;

  scene.background.copy(_c1.setHex(a.sky)).lerp(_c2.setHex(b.sky), f);
  scene.fog.color.copy(scene.background);
  const zv = ZONE_VISUALS[zoneVisualTarget] || ZONE_VISUALS.village;
  zoneVisual.fog.setHex(zv.fog);
  zoneVisual.sky.setHex(zv.sky);
  zoneVisual.warm = zv.warm;
  scene.background.lerp(zoneVisual.sky, zv.skyMix);
  scene.fog.color.lerp(zoneVisual.fog, zv.fogMix);
  skyWashMat.color.copy(scene.background);
  // Haze rolls in thicker overnight and at the low-angle dawn/dusk light.
  scene.fog.density = FOG_BASE_DENSITY * (1 + mix(a.n, b.n) * 0.5);
  key.color.copy(_c1.setHex(a.sun)).lerp(_c2.setHex(b.sun), f);
  hemi.color.copy(_c1.setHex(a.hSky)).lerp(_c2.setHex(b.hSky), f);
  hemi.groundColor.copy(_c1.setHex(a.hGnd)).lerp(_c2.setHex(b.hGnd), f);
  key.intensity  = mix(a.sunI, b.sunI);
  hemi.intensity = mix(a.hI,   b.hI);
  fill.intensity = mix(a.fillI, b.fillI);
  rim.intensity  = mix(a.rimI, b.rimI);
  skyNightFactor = mix(a.n, b.n);
  if (windowGlowMats.length) {
    const glow = Math.min(1, Math.max(0, (skyNightFactor - 0.25) / 0.75));
    _windowGlow.lerpColors(_windowDay, _windowNight, glow);
    for (let i = 0; i < windowGlowMats.length; i++) {
      const flicker = glow * (0.92 + 0.08 * Math.sin(animTime * 2.1 + i * 1.73));
      windowGlowMats[i].color.copy(_windowFlicker.copy(_windowGlow).multiplyScalar(0.9 + flicker * 0.16));
    }
  }
  updateHorizonRidge(skyNightFactor);

  // Sun travels an east → overhead → west arc; below the horizon at night.
  const theta = (t - 0.25) * Math.PI * 2;
  sunDirWorld.set(Math.cos(theta), Math.sin(theta), 0.25).normalize();
  // Keep the key light above the horizon (flip to the moon side at night) so
  // Chocky is never lit from underneath — the dim cool tone reads as moonlight.
  _sunTmp.copy(sunDirWorld);
  if (_sunTmp.y < 0.08) { _sunTmp.multiplyScalar(-1); if (_sunTmp.y < 0.08) _sunTmp.y = 0.08; }
  keySunOffset.copy(_sunTmp).multiplyScalar(12);
  // Re-pin the light to the current follow target (origin off play mode) so the
  // sun direction and the frustum centre stay consistent as the day turns.
  key.position.copy(keySunOffset).add(shadowFollowTarget);
  key.target.position.copy(shadowFollowTarget);
}

// Slide the sun's shadow frustum to stay centred on Chocky in play mode. The
// ortho box is only ±16 (it was sized for the village); without this, walking
// out onto the surrounding hills carried Chocky past its edge and his shadow —
// plus the slope he stood on — simply stopped rendering into the depth map.
// Cheap (a few vector ops) and free in shadow cost: the map already refreshes
// every SHADOW_INTERVAL frames regardless, so re-aiming it costs nothing extra.
function updateShadowFollow() {
  if (gameMode && chocky) {
    shadowFollowTarget.set(chocky.position.x, initialY + player.standY, chocky.position.z);
    key.position.copy(keySunOffset).add(shadowFollowTarget);
    key.target.position.copy(shadowFollowTarget);
  } else if (shadowFollowTarget.lengthSq() !== 0) {
    // Left play mode — recentre on the origin so the turntable/editor view (and
    // the editor sun panel, which drives key.position directly) behave as before.
    shadowFollowTarget.set(0, 0, 0);
    key.position.copy(keySunOffset).add(shadowFollowTarget);
    key.target.position.copy(shadowFollowTarget);
  }
}
// A full day is ~10 minutes, so the sky shifts imperceptibly between frames.
// We still advance the clock every frame (cheap) but only re-derive the light
// colours/intensities ~12×/sec, saving a half-dozen Color lerps every frame.
let _dnAccum = 1;          // >interval so the first frame applies immediately
const DN_INTERVAL = 1 / 12;
function updateDayNight(dt) {
  if (!dayNightEnabled) return;
  dayTime = (dayTime + dt / DAY_LENGTH) % 1;
  _dnAccum += dt;
  if (_dnAccum >= DN_INTERVAL) {
    _dnAccum = 0;
    applyDayNight(dayTime);
    editorHooks.onDayTimeChange();   // sun-panel clock readout, when present
  }
}
applyDayNight(dayTime);        // seed the lights at the starting time

// Tree sway lives in the chunk system now, as a vertex-shader offset driven by
// one shared time uniform (updateSwayTime) — every tier gets it, including LOW.

// Dust particles. A fixed pool reused as a ring buffer: spawning never
// allocates a Mesh/Material/Vector3 and dying never disposes one, so heavy
// bursts (footsteps, landings, coin pickups) cause zero GC churn — the kind of
// allocation storm that shows up as periodic frame hitches on a Chromebook.
const dustGeo = new THREE.SphereGeometry(0.08, 6, 4);
const DUST_POOL = poolScale(LOW ? 24 : 72);
const dustPool = [];
let _dustIdx = 0;
for (let i = 0; i < DUST_POOL; i++) {
  const mat = new THREE.MeshBasicMaterial({ color: 0xfff8e8, transparent: true, opacity: 0, fog: true });
  const p = new THREE.Mesh(dustGeo, mat);
  p.visible = false;
  p.userData = { vx: 0, vy: 0, vz: 0, life: 0, max: 0.55, scale0: 1 };
  scene.add(p);
  dustPool.push(p);
}
function spawnDust(x, y, z, count = 6, strength = 1) {
  for (let i = 0; i < count; i++) {
    const p = dustPool[_dustIdx];
    _dustIdx = (_dustIdx + 1) % DUST_POOL;
    const d = p.userData;
    p.position.set(x + (Math.random() - 0.5) * 0.2, y + 0.05, z + (Math.random() - 0.5) * 0.2);
    d.vx = (Math.random() - 0.5) * 1.4 * strength;
    d.vy = Math.random() * 1.0 * strength + 0.3;
    d.vz = (Math.random() - 0.5) * 1.4 * strength;
    d.life = 0.55; d.max = 0.55;
    d.scale0 = 0.5 + Math.random() * 0.5;
    p.scale.setScalar(d.scale0);
    p.material.opacity = 0.85;
    p.visible = true;
  }
}
function updateDust(dt) {
  for (let i = 0; i < dustPool.length; i++) {
    const p = dustPool[i];
    if (!p.visible) continue;
    const d = p.userData;
    d.life -= dt;
    if (d.life <= 0) { p.visible = false; p.material.opacity = 0; continue; }
    p.position.x += d.vx * dt; p.position.y += d.vy * dt; p.position.z += d.vz * dt;
    d.vy -= 4 * dt;            // light gravity on dust
    d.vx *= 0.96; d.vy *= 0.96; d.vz *= 0.96;
    const t = d.life / d.max;
    p.material.opacity = 0.85 * t;
    p.scale.setScalar(d.scale0 * (1 + (1 - t) * 1.5));   // grow as it fades
  }
}

// ---- Footprints: soft decals Chocky leaves as he walks, fading slowly away.
// A fixed pool reused as a ring buffer — zero per-step allocation, so it stays
// cheap enough to run every frame on a Chromebook.
function makeFootprintTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 64, 64);
  ctx.fillStyle = '#3a2a18';
  // Sole (ball of the foot) — a soft, nearly round pad near the middle…
  ctx.beginPath(); ctx.ellipse(32, 30, 12, 13, 0, 0, Math.PI * 2); ctx.fill();
  // …and a smaller heel pad close below it (short stubby print, not a long shoe).
  ctx.beginPath(); ctx.ellipse(32, 47, 8.5, 8, 0, 0, Math.PI * 2); ctx.fill();
  // A few toe dabs across the top for a little character.
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath(); ctx.ellipse(32 + i * 7.5, 16, 2.6, 3, 0, 0, Math.PI * 2); ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const footprintTex = cachedTexture('footprint', makeFootprintTexture);
// Lay the geometry flat (normal up) once so each print only needs a Y spin.
const footprintGeo = new THREE.PlaneGeometry(0.22, 0.26);
footprintGeo.rotateX(-Math.PI / 2);
const FOOTPRINT_POOL = poolScale(LOW ? 8 : 22);
const FOOTPRINT_LIFE = LOW ? 5 : 6;   // seconds to fully fade
const footprints = [];
let _footIdx = 0;
for (let i = 0; i < FOOTPRINT_POOL; i++) {
  const mat = new THREE.MeshBasicMaterial({
    map: footprintTex, transparent: true, opacity: 0,
    depthWrite: false, fog: true,
  });
  const m = new THREE.Mesh(footprintGeo, mat);
  m.rotation.order = 'YXZ';
  m.renderOrder = 1;               // draw over the ground without z-fighting
  m.visible = false;
  m.userData = { life: 0 };
  scene.add(m);
  footprints.push(m);
}
// Drop a print at a planted foot. `side` is -1 (left) / +1 (right).
function spawnFootprint(x, z, facing, side) {
  const m = footprints[_footIdx];
  _footIdx = (_footIdx + 1) % FOOTPRINT_POOL;
  // Offset sideways from Chocky's centre line to the planted foot (kept narrow
  // so the trail doesn't look bow-legged).
  const rx = Math.cos(facing), rz = -Math.sin(facing);   // right vector
  m.position.set(x + rx * side * 0.09, initialY + heightAt(x, z) + 0.012, z + rz * side * 0.09);
  m.rotation.y = facing + Math.PI;   // toes point the way he's walking
  m.userData.life = FOOTPRINT_LIFE;
  m.material.opacity = 0.5;
  m.visible = true;
}
function updateFootprints(dt) {
  for (let i = 0; i < footprints.length; i++) {
    const m = footprints[i];
    if (!m.visible) continue;
    m.userData.life -= dt;
    if (m.userData.life <= 0) { m.visible = false; m.material.opacity = 0; continue; }
    // Hold near full for the first second, then ease out over the rest.
    m.material.opacity = 0.5 * Math.min(1, m.userData.life / (FOOTPRINT_LIFE - 1));
  }
}
function clearFootprints() {
  for (const m of footprints) { m.visible = false; m.material.opacity = 0; m.userData.life = 0; }
}

// Camera shake (decays each frame)
let camShake = 0;
function addCamShake(amount) { camShake = Math.max(camShake, amount); }

const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(0, 0.9, 0);
controls.enablePan = true;          // pan with right-mouse / two-finger drag
controls.enableZoom = true;
controls.zoomSpeed = 1.8;            // snappier scroll-wheel zoom
controls.minDistance = 0.4;          // zoom in close to inspect face
controls.maxDistance = 30;           // pull way back
controls.maxPolarAngle = Math.PI * 0.85;  // allow nearly side-on view
controls.update();

// ---------- load chocky ----------
const bones = {};        // name -> bone
const restQ = new Map(); // bone -> THREE.Quaternion clone (rest pose)
let chocky = null;
let chockyRoot = null;
let chockyMat = null;    // Chocky's single material — its map IS his face
let initialY = 0;
const _tmpQ = new THREE.Quaternion();
const _tmpE = new THREE.Euler();

// Baked-animation playback (uses Three.js AnimationMixer)
let mixer = null;
const bakedClips = {};       // name -> THREE.AnimationClip
let activeAction = null;     // currently playing baked action (null = procedural mode)
let baseScale = 1;           // recorded after grounding; used for squash/stretch

// chocky.glb's download is kicked off early (the hoisted block just above
// `await loadKit()`) so it streams in parallel with kit.glb + the baked world
// instead of queued behind them — it's the asset the kid watches the loading
// screen for. Consume it here, where all the model-setup code it needs exists.
// All retry logic lives in loadGLBWithRetry (core/net.js).
chockyGltfPromise.then((gltf) => {
  chocky = gltf.scene;
  // First pass: mesh setup, AND find the SkinnedMesh (canonical source of bones)
  let skinnedMesh = null;
  chocky.traverse((o) => {
    if (o.isMesh) {
      o.castShadow = !ULTRA_LOW_SHADOWS;
      o.receiveShadow = false;
      if (o.material && o.material.map) {
        o.material.map.colorSpace = THREE.SRGBColorSpace;
        // Chocky's body is a flat cream colour, so any part that faces away
        // from the sun (e.g. the underside of the arms when they hang down)
        // reads as a muddy dark smudge. Add a gentle emissive floor that
        // follows the albedo map: it lifts those shaded areas while keeping
        // the dark face features (eyes/mouth) dark. Tune CHOCKY_EMISSIVE.
        const CHOCKY_EMISSIVE = 0.18;
        o.material.emissive = new THREE.Color(0xffffff);
        o.material.emissiveMap = o.material.map;
        o.material.emissiveIntensity = CHOCKY_EMISSIVE;
        o.material.needsUpdate = true;
        chockyMat = o.material;   // remember it so we can swap expressions
      }
    }
    if (o.isSkinnedMesh) skinnedMesh = o;
  });

  // Debug: dump EVERY named node in the scene graph
  if (DEBUG_LOAD) {
    console.log('=== ALL named nodes in scene ===');
    chocky.traverse((o) => {
      if (o.name) console.log(`  type=${o.type}  name="${o.name}"`);
    });
  }

  // Pull bones from the skeleton — works regardless of scene graph quirks
  if (skinnedMesh && skinnedMesh.skeleton) {
    if (DEBUG_LOAD) console.log(`=== SkinnedMesh has ${skinnedMesh.skeleton.bones.length} bones ===`);
    for (const b of skinnedMesh.skeleton.bones) {
      bones[b.name] = b;
      restQ.set(b, b.quaternion.clone());
    }
  } else {
    console.warn('No SkinnedMesh found in chocky.glb — rig is broken');
  }

  // Bone aliases: map semantic names to whatever the rig actually calls them.
  // We match by SUFFIX so we don't care about Maya namespaces, prefixes, etc.
  // (glTF loaders mangle the ':' separator in different ways.)
  const ALIASES = {
    'L_Upperarm': ['shoulder_l_Jnt', 'L_Upperarm'],
    'L_Forearm':  ['elbow_l_Jnt',    'L_Forearm'],
    'L_Hand':     ['wrist_l_Jnt',    'L_Hand'],
    'R_Upperarm': ['shoulder_r_Jnt', 'R_Upperarm'],
    'R_Forearm':  ['elbow_r_Jnt',    'R_Forearm'],
    'R_Hand':     ['wrist_r_Jnt',    'R_Hand'],
    'L_Thigh':    ['hip_l_Jnt',      'L_Thigh'],
    'L_Calf':     ['knee_l_Jnt',     'L_Calf'],
    'L_Foot':     ['foot_l_Jnt',     'L_Foot'],
    'R_Thigh':    ['hip_r_Jnt',      'R_Thigh'],
    'R_Calf':     ['knee_r_Jnt',     'R_Calf'],
    'R_Foot':     ['foot_r_Jnt',     'R_Foot'],
    'Head':       ['head_Jnt',       'Head'],
    'Spine01':    ['spine1_Jnt',     'Spine01'],
    'Spine02':    ['spine2_Jnt',     'Spine02'],
    'Waist':      ['waist_Jnt',      'Waist'],
    'Pelvis':     ['pelvis_Jnt',     'Pelvis'],
  };
  const allBoneNames = Object.keys(bones);
  for (const [logical, candidates] of Object.entries(ALIASES)) {
    if (bones[logical]) continue;
    for (const c of candidates) {
      // suffix-match (handles "chocky_rig_v07hand_l_Jnt" → "hand_l_Jnt")
      const match = allBoneNames.find(n => n.toLowerCase().endsWith(c.toLowerCase()));
      if (match) { bones[logical] = bones[match]; if (DEBUG_LOAD) console.log(`  alias: ${logical} → ${match}`); break; }
    }
  }

  // === BAKED ANIMATIONS via AnimationMixer ===
  if (gltf.animations && gltf.animations.length) {
    mixer = new THREE.AnimationMixer(chocky);
    for (const clip of gltf.animations) {
      bakedClips[clip.name] = clip;
      if (DEBUG_LOAD) console.log(`  baked clip: "${clip.name}" (${clip.duration.toFixed(2)}s)`);
    }
    validateBakedClips();
  }

  // === SCALE & GROUND ===
  // Use ONLY the mesh's geometry for the bounding box — bones can extend
  // beyond the visible mesh and cause the "floating shadow" problem.
  function meshOnlyBox(obj) {
    obj.updateMatrixWorld(true);
    const b = new THREE.Box3();
    obj.traverse(o => {
      if (o.isMesh && o.geometry) {
        if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
        const local = o.geometry.boundingBox.clone();
        local.applyMatrix4(o.matrixWorld);
        b.union(local);
      }
    });
    return b;
  }

  const box = meshOnlyBox(chocky);
  const size = box.getSize(new THREE.Vector3());
  if (DEBUG_LOAD) console.log(`raw mesh box: min=(${box.min.x.toFixed(2)},${box.min.y.toFixed(2)},${box.min.z.toFixed(2)}) max=(${box.max.x.toFixed(2)},${box.max.y.toFixed(2)},${box.max.z.toFixed(2)}) size=(${size.x.toFixed(2)},${size.y.toFixed(2)},${size.z.toFixed(2)})`);

  const targetH = 1.8;
  // Guard against degenerate or zero bounding box
  const longestAxis = Math.max(size.x, size.y, size.z);
  const refH = (size.y > 0.01) ? size.y : longestAxis;
  const scale = (refH > 0.01) ? (targetH / refH) : 1;
  if (DEBUG_LOAD) console.log(`applying scale: ${scale.toFixed(3)}`);
  chocky.scale.setScalar(scale);

  // Recompute (mesh-only) and ground so feet sit ON the floor
  const box2 = meshOnlyBox(chocky);
  if (DEBUG_LOAD) console.log(`after scale: min=(${box2.min.x.toFixed(2)},${box2.min.y.toFixed(2)},${box2.min.z.toFixed(2)}) max=(${box2.max.x.toFixed(2)},${box2.max.y.toFixed(2)},${box2.max.z.toFixed(2)})`);
  chocky.position.x -= (box2.min.x + box2.max.x) / 2;
  chocky.position.z -= (box2.min.z + box2.max.z) / 2;
  chocky.position.y -= box2.min.y;
  initialY = chocky.position.y;
  if (DEBUG_LOAD) console.log(`final chocky position: (${chocky.position.x.toFixed(2)}, ${chocky.position.y.toFixed(2)}, ${chocky.position.z.toFixed(2)})`);

  chockyRoot = chocky;
  baseScale = chocky.scale.x;   // remember scale for squash/stretch
  scene.add(chocky);

  // Build the swappable expression set from the GLB's own face texture.
  buildFaces();
  // Re-apply the saved outfit (body tint + hat) now that the rig exists.
  outfitsOnChockyReady();

  setDomDisplay('loading', 'none');
  // The world + Chocky are fully in the scene: warm every shader program and
  // upload the expression textures now, in the idle post-spawn frames, so the
  // first spell cast / nightfall glow / blink can't stall a play-mode frame.
  schedulePrewarm({ renderer, scene, camera, getTextures: () => Object.values(faceTex) });
  if (DEBUG_LOAD) console.log('Bones found:', Object.keys(bones));
  // Editor (when loaded) builds its bone dropdown + baked-anim buttons now. The
  // latch lets an editor that loads AFTER this fires still catch up (see below).
  editorHooks.modelReady = true;
  editorHooks.onModelReady();
  // Auto-enter Play mode so users land in the village right away
  setTimeout(() => { if (!gameMode) setPlayMode(true); }, 50);
  // Print rest rotations so we can see which bones have non-trivial rest poses
  if (DEBUG_LOAD) {
    for (const name of ['L_Upperarm','R_Upperarm','L_Thigh','R_Thigh','Head']) {
      const b = bones[name];
      if (b) {
        const e = new THREE.Euler().setFromQuaternion(b.quaternion);
        console.log(`  ${name} rest euler: x=${e.x.toFixed(2)} y=${e.y.toFixed(2)} z=${e.z.toFixed(2)}`);
      } else console.log(`  ${name} NOT FOUND`);
    }
  }
}).catch((err) => {
  console.error(err);
  const loading = document.getElementById('loading');
  if (loading) {
    loading.innerHTML = `
      <div style="max-width:420px; text-align:center; line-height:1.35;">
        <div style="font-size:22px; font-weight:800; margin-bottom:8px;">Chocky needs a quick reload</div>
        <div style="font-size:15px; opacity:0.78; margin-bottom:14px;">The character file did not finish loading. Please check the connection and try again.</div>
        <button id="retryLoadBtn" style="font:inherit; font-weight:800; padding:10px 18px; border:0; border-radius:12px; background:#ff6b6b; color:white; cursor:pointer;">Try again</button>
      </div>`;
    const retry = document.getElementById('retryLoadBtn');
    if (retry) retry.addEventListener('click', () => location.reload());
  }
});

// ---------- facial expressions ----------
// Chocky's whole albedo texture IS his face (cream skin with eyes, cheeks and an
// open mouth — everything outside the centre wraps around the back of his body).
// So "animating his face" is just swapping that one texture. We bake a handful
// of simple expressions onto canvases — each starts from his real face texture
// (so the skin, cheeks and feature placement always match exactly) and only the
// eyes and/or mouth are redrawn. A tiny state machine then picks an expression
// from what he's doing: blinking when calm, eyes wide mid-jump, a grin at a run,
// dizzy spirals after a hard landing.
const faceTex = {};     // name -> CanvasTexture
const FACE_BG = '#f9e8d4';   // exact cream of the texture background
const FACE_INK = '#1c2436';  // the dark navy of his eyes & mouth
let faceImage = null;        // his original face, drawn to a base canvas
// The UV transform (offset/repeat/rotation) that GLTFLoader put on the GLB face
// texture from its KHR_texture_transform. gltfpack (`npm run pack:glb -cc`)
// quantizes UVs: chocky.glb's face UVs now live in a 0..1/16 corner and the
// texture's repeat (~15.5) + offset expand them back to the full image. When we
// swap chockyMat.map to an expression CANVAS (default repeat 1,1), those tiny
// UVs would sample an ~8px blank corner — Chocky loses his face. So every face
// texture must inherit the same transform as the map it replaces. Captured once
// in buildFaces() while chockyMat.map is still the original GLB texture.
let faceUV = null;
function applyFaceUV(tex) {
  if (!faceUV || !tex) return;
  tex.wrapS = faceUV.wrapS; tex.wrapT = faceUV.wrapT;
  tex.offset.copy(faceUV.offset);
  tex.repeat.copy(faceUV.repeat);
  tex.center.copy(faceUV.center);
  tex.rotation = faceUV.rotation;
  tex.matrixAutoUpdate = true;   // recomputes tex.matrix (the uv transform) from the above
  tex.needsUpdate = true;
}

function buildFaces() {
  if (!chockyMat || !chockyMat.map || !chockyMat.map.image) return;
  const img = chockyMat.map.image;
  // Snapshot the original texture's UV transform BEFORE any setFace() swaps the
  // map out from under us (see faceUV note above). On an untransformed export
  // this is a harmless identity (repeat 1,1), so the no-build/unpacked path is
  // unaffected.
  const src = chockyMat.map;
  faceUV = {
    offset: src.offset.clone(),
    repeat: src.repeat.clone(),
    center: src.center.clone(),
    rotation: src.rotation,
    wrapS: src.wrapS, wrapT: src.wrapT,
  };
  // Base canvas holding the untouched face — every expression starts from this.
  const base = document.createElement('canvas');
  base.width = img.width; base.height = img.height;
  base.getContext('2d').drawImage(img, 0, 0);
  faceImage = base;
  for (const name of ['happy', 'blink', 'grin', 'surprised', 'sad', 'dizzy']) {
    faceTex[name] = makeFace(name);
  }
  // 'happy' is a pristine copy of the original, so swapping it in is a no-op
  // visually — the perfect neutral to fall back to.
  setFace('happy');
  loadFaceOverrides();
}

// Hand-painted expression overrides: if faces/<name>.png exists next to this
// file, it replaces the code-drawn version of that expression. Any missing
// file silently keeps the canvas-drawn fallback, so the artist can override
// one face or all six. The PNGs use the same orientation as the GLB texture
// (NOT flipped — paint over the exports in the artist kit and it just works).
function loadFaceOverrides() {
  const ldr = new THREE.TextureLoader();
  for (const name of ['happy', 'blink', 'grin', 'surprised', 'sad', 'dizzy']) {
    ldr.load(`faces/${name}.png`, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = chockyMat.map.flipY;
      tex.anisotropy = chockyMat.map.anisotropy || 1;
      applyFaceUV(tex);              // inherit the GLB's UV transform (see faceUV note)
      faceTex[name] = tex;
      prewarmTexture(renderer, tex);   // upload now — not on his next blink
      // If this expression is currently on his face, refresh it in place.
      if (face.current === name) { face.current = null; setFace(name); }
      if (DEBUG_LOAD) console.log(`face override loaded: faces/${name}.png`);
    }, undefined, () => { /* no override for this face — fallback stays */ });
  }
}

// Draw one expression onto a fresh canvas cloned from the base face. Coordinates
// are authored in the texture's native 128-space and scaled to whatever the real
// texture is. Eyes sit at (24,56)/(104,56), the mouth around (64,79).
function makeFace(name) {
  const c = document.createElement('canvas');
  c.width = faceImage.width; c.height = faceImage.height;
  const x = c.getContext('2d');
  x.drawImage(faceImage, 0, 0);
  const S = c.width / 128;
  x.lineCap = 'round'; x.lineJoin = 'round';
  const eyeXs = [24, 104], eyeY = 56, mouthX = 64, mouthY = 79;
  // Paint cream over the original eyes / mouth so we can draw our own.
  const wipeEyes = () => {
    // Kept clear of the cheeks (which begin ~y73) so they survive untouched.
    x.fillStyle = FACE_BG;
    for (const ex of eyeXs) x.fillRect((ex - 17) * S, (eyeY - 18) * S, 34 * S, 34 * S);
  };
  const wipeMouth = () => {
    // Top kept below the eyes (~y66) so frowns etc. don't nibble an eye corner.
    x.fillStyle = FACE_BG;
    x.fillRect((mouthX - 30) * S, (mouthY - 12) * S, 60 * S, 36 * S);
  };
  const ellipse = (cx, cy, rx, ry, col) => {
    x.fillStyle = col; x.beginPath();
    x.ellipse(cx * S, cy * S, rx * S, ry * S, 0, 0, Math.PI * 2); x.fill();
  };
  const arc = (cx, cy, w, dip, lw) => {
    x.strokeStyle = FACE_INK; x.lineWidth = lw * S; x.beginPath();
    x.moveTo((cx - w) * S, cy * S);
    x.quadraticCurveTo(cx * S, (cy + dip) * S, (cx + w) * S, cy * S);
    x.stroke();
  };
  switch (name) {
    case 'happy':      // pristine original — the neutral, smiley open mouth
      break;
    case 'blink':      // eyes shut (gentle downward lashes), mouth unchanged
      wipeEyes();
      for (const ex of eyeXs) arc(ex, eyeY, 10, 7, 4.5);
      break;
    case 'grin':       // happy squint (^ ^) + a wide closed smile
      wipeEyes();
      for (const ex of eyeXs) arc(ex, eyeY + 4, 10, -9, 4.5);
      wipeMouth();
      arc(mouthX, mouthY - 4, 22, 18, 6);
      break;
    case 'surprised':  // round wide eyes + a small "o"
      wipeEyes();
      for (const ex of eyeXs) ellipse(ex, eyeY - 1, 11, 13, FACE_INK);
      wipeMouth();
      ellipse(mouthX, mouthY + 1, 9, 11, FACE_INK);
      break;
    case 'sad':        // original eyes, downturned frown
      wipeMouth();
      arc(mouthX, mouthY + 8, 17, -14, 5);
      break;
    case 'dizzy':      // X-ed out eyes + a wavy daze of a mouth
      wipeEyes();
      x.strokeStyle = FACE_INK; x.lineWidth = 4 * S;
      for (const ex of eyeXs) {
        x.beginPath();
        x.moveTo((ex - 8) * S, (eyeY - 8) * S); x.lineTo((ex + 8) * S, (eyeY + 8) * S);
        x.moveTo((ex + 8) * S, (eyeY - 8) * S); x.lineTo((ex - 8) * S, (eyeY + 8) * S);
        x.stroke();
      }
      wipeMouth();
      x.strokeStyle = FACE_INK; x.lineWidth = 4 * S; x.beginPath();
      x.moveTo((mouthX - 14) * S, mouthY * S);
      x.quadraticCurveTo((mouthX - 7) * S, (mouthY - 6) * S, mouthX * S, mouthY * S);
      x.quadraticCurveTo((mouthX + 7) * S, (mouthY + 6) * S, (mouthX + 14) * S, mouthY * S);
      x.stroke();
      break;
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = chockyMat.map.flipY;   // match the glTF texture's orientation
  tex.anisotropy = chockyMat.map.anisotropy || 1;
  applyFaceUV(tex);                  // inherit the GLB's UV transform (see faceUV note)
  return tex;
}

// Expression state: a movement-driven base plus short-lived "holds" for one-off
// reactions (a hard landing, a respawn) that briefly override the base.
const face = {
  current: null,
  holdName: null, holdTime: 0,
  blinkCooldown: 2 + Math.random() * 3,
  blinking: 0,
};
function setFace(name) {
  if (name === face.current || !chockyMat || !faceTex[name]) return;
  face.current = name;
  chockyMat.map = faceTex[name];
  if (chockyMat.emissiveMap) chockyMat.emissiveMap = faceTex[name];
}
// Briefly force an expression (overrides the movement base for `dur` seconds).
function triggerFace(name, dur) {
  if (!faceTex[name]) return;
  face.holdName = name; face.holdTime = dur;
}
function updateFace(dt) {
  if (!chockyMat || !faceImage) return;
  // Base expression from what Chocky's doing right now.
  let base = 'happy';
  if (gameMode) {
    if (player.state === 'run') base = 'grin';
    else if (player.state === 'jumpUp' || player.state === 'jumpDown') base = 'surprised';
  }
  // A transient reaction wins while it's still holding.
  if (face.holdTime > 0) {
    face.holdTime -= dt;
    setFace(face.holdName);
    return;
  }
  // Otherwise blink on a lazy timer — but only from a calm, open-eyed base.
  if (face.blinking > 0) {
    face.blinking -= dt;
    setFace('blink');
    return;
  }
  if (base === 'happy') {
    face.blinkCooldown -= dt;
    if (face.blinkCooldown <= 0) {
      face.blinking = 0.12;
      face.blinkCooldown = 2.4 + Math.random() * 3.6;
      setFace('blink');
      return;
    }
  }
  setFace(base);
}

// ---------- animation system ----------
let currentAnim = 'idle';
// animTime is declared up by applyDayNight (its earliest top-level user —
// the window-glow flicker); it advances in tick() below.

function resetPose() {
  for (const [bone, q] of restQ) {
    bone.quaternion.copy(q);
  }
  if (chockyRoot) chockyRoot.position.y = initialY;
  resetSquash();
}

// === WORLD-SPACE rotation helpers ===
// The rig's local bone frames are bizarre (e.g. L_Thigh has a -151° rest
// rotation), so we apply rotations around predictable WORLD axes instead.
// World axes from Chocky's perspective: +X = his left, +Y = up, +Z = forward
// (toward camera, since the camera faces -Z and Chocky faces +Z).
const _worldX = new THREE.Vector3(1, 0, 0);
const _worldY = new THREE.Vector3(0, 1, 0);
const _worldZ = new THREE.Vector3(0, 0, 1);
const _wasdCamDir = new THREE.Vector3();

// Rotate a bone around a world-space axis by `angle` radians, ADDITIVELY
// on top of the rest pose (which resetPose() already restored).
function rotW(name, axis, angle) {
  const b = bones[name];
  if (!b) return;
  b.rotateOnWorldAxis(axis, angle);
}

// Convenience: "swing" a limb forward/back, "raise" lifts it up/down,
// "twist" rotates around the limb's length. All in world axes.
// Arms in T-pose extend along ±X. Legs extend along -Y.
// Arm: swing(+) = forward, raise(+) = up overhead, twist around its length
function poseArm(side /* 'L' | 'R' */, swing = 0, raise = 0, twist = 0) {
  const name = side + '_Upperarm';
  // Order matters: raise first, then swing, then twist
  if (raise) rotW(name, _worldZ, side === 'L' ? raise : -raise);
  if (swing) rotW(name, _worldY, side === 'L' ? -swing : swing);
  if (twist) rotW(name, _worldX, twist);
}

function poseForearm(side, bend = 0) {
  // Bend the elbow. In T-pose forearm continues along ±X, so bending it
  // forward = rotate around world Y (sign depends on side).
  const name = side + '_Forearm';
  if (bend) rotW(name, _worldY, side === 'L' ? -bend : bend);
}

// Relaxed arm hanging at the side. The T-pose has the arm sticking straight
// out along ±X, which looks robotic when standing or walking. This first drops
// the upper arm DOWN to the side (rotate around world Z), then swings it
// fore/aft like a pendulum (around world X — the axis that actually moves a
// hanging arm forward/back), and finally bends the elbow in the same sagittal
// plane. Use this instead of poseArm whenever the arm should rest by the body.
//   down  : how far to drop from horizontal. -PI/2 ≈ straight down; ~-1.3 hangs
//           with a touch of natural outward splay.
//   swing : pendulum fore/aft (negative = forward, toward camera).
//   elbow : elbow bend, negative brings the hand forward.
// Default hanging-arm rest, shared by idle and the arm fidgets so they can
// blend smoothly out of it.
const ARM_REST_DOWN = -1.48;
const ARM_REST_ELBOW = -0.10;
function relaxArm(side, down = -1.48, swing = 0, elbow = 0) {
  const up = side + '_Upperarm';
  const fa = side + '_Forearm';
  rotW(up, _worldZ, side === 'L' ? down : -down);
  if (swing) rotW(up, _worldX, swing);
  if (elbow) rotW(fa, _worldX, elbow);
}

// Leg: swing(+) = forward, splay(+) = outward
function poseLeg(side, swing = 0, splay = 0) {
  const name = side + '_Thigh';
  // Leg points in -Y. Forward swing = rotate around world X.
  if (swing) rotW(name, _worldX, swing);
  if (splay) rotW(name, _worldZ, side === 'L' ? splay : -splay);
}

function poseCalf(side, bend = 0) {
  // Bend knee — heel comes UP and BACKWARD (behind Chocky), like a walk cycle
  const name = side + '_Calf';
  if (bend) rotW(name, _worldX, bend);
}

function poseHead(pitch = 0, yaw = 0, roll = 0) {
  if (pitch) rotW('Head', _worldX, pitch);
  if (yaw)   rotW('Head', _worldY, yaw);
  if (roll)  rotW('Head', _worldZ, roll);
}

function poseSpine(pitch = 0) {
  if (pitch) rotW('Spine01', _worldX, pitch);
}

// kept for backward compat (a few old anims still call it)
function setBone(name, dx = 0, dy = 0, dz = 0) {
  const b = bones[name];
  if (!b) return;
  const r = restQ.get(b);
  _tmpE.set(dx, dy, dz, 'XYZ');
  _tmpQ.setFromEuler(_tmpE);
  b.quaternion.copy(r).multiply(_tmpQ);
}

// ===== Easing + squash helpers (cartoon timing) =====
// Sine waves are boring. Use these to make motion punchy.
const ease = {
  outCubic:  t => 1 - Math.pow(1 - t, 3),
  outBack:   (t, s=1.7) => 1 + (s+1)*Math.pow(t-1,3) + s*Math.pow(t-1,2),
  outElastic:t => { if (t===0||t===1) return t; const p=0.3; return Math.pow(2,-10*t)*Math.sin((t-p/4)*(2*Math.PI)/p)+1; },
  inOutQuad: t => t<0.5 ? 2*t*t : 1-Math.pow(-2*t+2,2)/2,
  bounce:    t => { const n=7.5625,d=2.75; if (t<1/d) return n*t*t; if (t<2/d) return n*(t-=1.5/d)*t+0.75; if (t<2.5/d) return n*(t-=2.25/d)*t+0.9375; return n*(t-=2.625/d)*t+0.984375; },
};
// Squash/stretch the body via root scale. amount=1 is neutral.
// >1 stretches vertical (skinnier), <1 squashes (wider). Volume preserved.
function setSquash(amount) {
  if (!chocky) return;
  chocky.scale.y = baseScale * amount;
  const horiz = baseScale / Math.sqrt(amount);
  chocky.scale.x = horiz;
  chocky.scale.z = horiz;
}
function resetSquash() { if (chocky) chocky.scale.setScalar(baseScale); }

// animation library — all rotations around WORLD axes for predictability
const ANIMS = {
  idle(t, skipArms = false) {
    // Breathing: subtle squash on inhale/exhale (the body fills out, not just bobs)
    const breath = Math.sin(t * 1.6);
    setSquash(1 + breath * 0.04);
    // Head: small slow nod + occasional look-around (offset sines = organic)
    poseHead(Math.sin(t * 1.6) * 0.06 + 0.02, Math.sin(t * 0.7) * 0.18, Math.sin(t * 0.5) * 0.05);
    poseSpine(breath * 0.025);
    // Arms hang relaxed at the sides, drifting fore/aft at slightly different
    // phases (so they're not symmetric) and rising a hair on the breath.
    // Skipped when an arm-driving fidget owns the arms this frame.
    if (!skipArms) {
      const armDrift = Math.sin(t * 1.1) * 0.05;
      relaxArm('L', ARM_REST_DOWN + breath * 0.02,  armDrift,        ARM_REST_ELBOW + armDrift * 0.4);
      relaxArm('R', ARM_REST_DOWN + breath * 0.02, -armDrift * 0.85, ARM_REST_ELBOW - armDrift * 0.4);
    }
    if (chockyRoot) chockyRoot.position.y = initialY + breath * 0.02;
  },

  wave(t) {
    poseHead(-0.05, Math.sin(t * 2) * 0.08, 0);
    // Right arm raised overhead (90° + 15° lean)
    poseArm('R', 0, Math.PI / 2 + 0.25, 0);
    // Once the forearm is pointing UP, "wave" = rotate it around world X
    // (front-back axis) so the hand swings left/right above the head
    rotW('R_Forearm', _worldX, Math.sin(t * 7) * 0.55);
    // Left arm gentle sway
    poseArm('L', Math.sin(t * 1.3) * 0.08);
    if (chockyRoot) chockyRoot.position.y = initialY + Math.sin(t * 2.2) * 0.015;
  },

  walk(t) {
    // Cycle: 0.8s per full cycle (left+right step)
    const speed = 2 * Math.PI / 0.8;
    const s = Math.sin(t * speed);
    const c = Math.cos(t * speed);
    const stepPhase = Math.abs(c);     // 0 at mid-stride, 1 at footfall
    // Legs: sharper than sine — use sin^3 so the foot hangs in air longer between steps
    const swing = Math.sign(s) * Math.pow(Math.abs(s), 0.7);
    poseLeg('L',  swing * 0.7);
    poseLeg('R', -swing * 0.7);
    // Knees bend in air (when leg is swinging forward)
    poseCalf('L', Math.max(0, -s) * 1.0);
    poseCalf('R', Math.max(0,  s) * 1.0);
    // Arms hang at the sides and swing fore/aft like pendulums, opposite to
    // the legs on the same side. Elbows keep a soft bend that tightens a touch
    // as the arm swings forward — reads as a loose, natural walk.
    const armSwing = swing * 0.5;
    relaxArm('L', -1.42, -armSwing, -0.28 - Math.max(0, -armSwing) * 0.3);
    relaxArm('R', -1.42,  armSwing, -0.28 - Math.max(0,  armSwing) * 0.3);
    // Body: bob UP at mid-stride (when both legs are passing each other), squash on footfall
    const bobHeight = (1 - stepPhase) * 0.08;        // peaks mid-stride
    const squash = 1 - stepPhase * 0.06;             // squash on footfall
    setSquash(squash);
    if (chockyRoot) chockyRoot.position.y = initialY + bobHeight;
    // Spine: slight forward lean + side roll with each step
    poseSpine(0.04 + (1 - stepPhase) * 0.04);
    rotW(bones['Spine01']?.name || 'spine1_Jnt', _worldZ, s * 0.06);
    // Head: counter-rotate slightly (head wants to stay level)
    poseHead(-0.05, s * 0.08, -s * 0.04);
  },

  jump(t) {
    // Phases (total 1.8s loop): anticipation, wind-up, launch, air, land, recover
    const T = 1.8;
    const loop = (t % T) / T;
    const MAX_ARM = Math.PI / 2 + 0.25;
    let crouch = 0, lift = 0, armUp = 0, squash = 1, leanBack = 0;

    if (loop < 0.10) {                  // anticipation: tiny rise, arms back
      const k = loop / 0.10;
      crouch = -k * 0.15;               // body straightens, arms pulled back
      armUp = -k * 0.3;                 // arms swing BEHIND (negative raise)
      squash = 1 + k * 0.06;
    } else if (loop < 0.22) {           // wind-up: deep crouch, big squash
      const k = (loop - 0.10) / 0.12;
      const e = ease.outCubic(k);
      crouch = -0.15 + e * 0.95;        // crouch all the way down
      armUp = -0.3 + e * 0.3;           // arms come back through neutral
      squash = 1.06 - e * 0.28;         // SQUASH HARD (0.78 = wide+short)
      leanBack = e * 0.1;
    } else if (loop < 0.32) {           // launch: explosive extension, big stretch
      const k = (loop - 0.22) / 0.10;
      const e = ease.outCubic(k);
      crouch = 0.8 * (1 - e);
      lift = e * 1.1;
      armUp = e * MAX_ARM;
      squash = 0.78 + e * 0.45;         // STRETCH UP (1.23 = tall+skinny)
    } else if (loop < 0.55) {           // air: held pose, arms up, slight stretch
      lift = 1.1;
      armUp = MAX_ARM;
      squash = 1.18;
    } else if (loop < 0.70) {           // descent
      const k = (loop - 0.55) / 0.15;
      lift = 1.1 * (1 - k);
      armUp = MAX_ARM;
      squash = 1.18 - k * 0.10;
    } else if (loop < 0.78) {           // land: hard squash
      const k = (loop - 0.70) / 0.08;
      const e = ease.outCubic(k);
      lift = 0;
      crouch = e * 0.9;
      armUp = MAX_ARM * (1 - e);
      squash = 1.08 - e * 0.32;          // BIG IMPACT SQUASH (0.76)
    } else {                             // recovery: bounce back with overshoot
      const k = (loop - 0.78) / 0.22;
      const e = ease.outElastic(k);
      crouch = 0.9 * (1 - e);
      squash = 0.76 + e * 0.24;          // springs back, settles
    }

    poseLeg('L', -crouch * 0.5);
    poseLeg('R', -crouch * 0.5);
    poseCalf('L', Math.max(0, crouch) * 1.1);
    poseCalf('R', Math.max(0, crouch) * 1.1);
    poseSpine(-crouch * 0.15 + leanBack);
    poseArm('L', 0, armUp);
    poseArm('R', 0, armUp);
    poseHead(leanBack * 0.5, 0, 0);
    setSquash(squash);
    if (chockyRoot) chockyRoot.position.y = initialY + lift;
  },

  cheer(t) {
    // Hopping cheer: 0.5s per hop with anticipation+squash on each landing
    const T = 0.55;
    const loop = (t % T) / T;
    let lift = 0, squash = 1;
    if (loop < 0.3) {                            // ground squash → launch
      const k = loop / 0.3;
      squash = 1 - ease.outCubic(k) * 0.18 + ease.outCubic(k) * 0.3;
      lift = ease.outCubic(k) * 0.2;
    } else if (loop < 0.65) {                    // airborne stretch
      const k = (loop - 0.3) / 0.35;
      lift = 0.2 + Math.sin(k * Math.PI) * 0.25;
      squash = 1.15;
    } else {                                     // landing squash
      const k = (loop - 0.65) / 0.35;
      const e = ease.outElastic(k);
      lift = (1 - e) * 0.15;
      squash = 0.82 + e * 0.18;
    }
    const raise = Math.PI / 2 + 0.2;
    poseArm('L', 0, raise + Math.sin(t * 12) * 0.15);
    poseArm('R', 0, raise + Math.sin(t * 12 + 0.3) * 0.15);
    poseHead(-0.18, Math.sin(t * 3) * 0.18, 0);
    poseSpine(-0.08);
    setSquash(squash);
    if (chockyRoot) chockyRoot.position.y = initialY + lift;
  },

  sad(t) {
    // Slow, sighing rhythm. Body squashes slightly on each sigh.
    const sigh = Math.sin(t * 1.2);
    poseSpine(0.28 + sigh * 0.04);
    poseHead(0.40 + sigh * 0.03, Math.sin(t * 0.6) * 0.06, 0);
    poseArm('L', 0.3, -0.4);
    poseArm('R', 0.3, -0.4);
    poseForearm('L', 0.2);
    poseForearm('R', 0.2);
    // Subtle downward squash on the exhale
    setSquash(1 + sigh * 0.03 - 0.02);
    if (chockyRoot) chockyRoot.position.y = initialY + sigh * 0.015 - 0.01;
  },

  // Diagnostic: rotates each limb around each world axis individually
  wiggleTest(t) {
    const phase = Math.floor(t * 0.5) % 12;
    const k = Math.sin(t * Math.PI) * 1.0;
    const axes = [_worldX, _worldY, _worldZ];
    const axisNames = ['worldX', 'worldY', 'worldZ'];
    const bones4 = ['L_Upperarm', 'R_Upperarm', 'L_Thigh', 'R_Thigh'];
    const boneIdx = Math.floor(phase / 3);
    const axisIdx = phase % 3;
    rotW(bones4[boneIdx], axes[axisIdx], k);
    if (!ANIMS._lastPhase || ANIMS._lastPhase !== phase) {
      console.log(`[wiggle] ${bones4[boneIdx]} around ${axisNames[axisIdx]}`);
      ANIMS._lastPhase = phase;
    }
  },
};

// Idle fidgets — short procedural "bored" gestures layered ON TOP of the base
// idle when Chocky stands around. Each fn takes p in [0,1] over its duration;
// an env = sin(p·π) window ramps the gesture in and out so it never pops.
const IDLE_FIDGETS = {
  // Glance around the village
  lookAround: { dur: 3.6, fn(p) {
    const env = Math.sin(p * Math.PI);
    const yaw = Math.sin(p * Math.PI * 2) * 0.55;
    poseHead(Math.sin(p * Math.PI * 4) * 0.04 * env, yaw, -yaw * 0.15);
    rotW('Spine01', _worldY, yaw * 0.2);
  }},
  // Look up at the sky / passing birds, one hand shading the eyes
  lookUp: { dur: 3.8, arms: true, fn(p) {
    const env = Math.sin(p * Math.PI);
    poseHead(-0.5 * env, Math.sin(p * Math.PI * 2) * 0.1 * env, 0);
    poseSpine(-0.06 * env);
    // Hand rises to shade the eyes, peaking with the head tilt (back to 0 at the ends).
    const h = Math.pow(env, 1.5);
    // Left arm stays relaxed at the side; right blends up out of the rest.
    relaxArm('L', ARM_REST_DOWN, 0, ARM_REST_ELBOW);
    relaxArm('R', ARM_REST_DOWN * (1 - h), 0, ARM_REST_ELBOW * (1 - h));
    poseArm('R', 0.1 * h, 0.5 * h);
    poseForearm('R', 1.3 * h);
  }},
  // Big stretch — arms overhead, body extends tall
  stretch: { dur: 3.2, arms: true, fn(p) {
    const env = Math.sin(p * Math.PI);
    const raise = (Math.PI / 2 + 0.15) * env;
    // Blend from hanging rest (env=0) up to overhead (env=1).
    relaxArm('L', ARM_REST_DOWN * (1 - env), 0, ARM_REST_ELBOW * (1 - env));
    relaxArm('R', ARM_REST_DOWN * (1 - env), 0, ARM_REST_ELBOW * (1 - env));
    poseArm('L', 0, raise, 0);
    poseArm('R', 0, raise, 0);
    poseSpine(-0.14 * env);
    poseHead(-0.18 * env, 0, 0);
    setSquash(1 + env * 0.13);
  }},
  // Yawn — head tilts back, arms rise a touch, a slow swell
  yawn: { dur: 3.2, arms: true, fn(p) {
    const env = Math.sin(p * Math.PI);
    poseHead(-0.32 * env, 0, 0);
    relaxArm('L', ARM_REST_DOWN * (1 - env), 0, ARM_REST_ELBOW * (1 - env));
    relaxArm('R', ARM_REST_DOWN * (1 - env), 0, ARM_REST_ELBOW * (1 - env));
    poseArm('L', 0.2 * env, 0.4 * env);
    poseArm('R', 0.2 * env, 0.4 * env);
    poseForearm('L', 0.6 * env);
    poseForearm('R', 0.6 * env);
    setSquash(1 + env * 0.06);
  }},
  // Tap a foot while humming
  tapFoot: { dur: 3.6, fn(p) {
    const env = Math.sin(p * Math.PI);
    poseLeg('R', -0.05 * env, 0.04 * env);
    const tap = Math.max(0, Math.sin(p * Math.PI * 10));
    poseCalf('R', tap * 0.5 * env);
    poseHead(Math.sin(p * Math.PI * 10) * 0.05 * env, 0, 0);
    poseForearm('L', 0.3 * env);
    poseForearm('R', 0.3 * env);
  }},
  // Shift weight side to side
  shiftWeight: { dur: 3.0, fn(p) {
    const env = Math.sin(p * Math.PI);
    const s = Math.sin(p * Math.PI * 2);
    rotW('Spine01', _worldZ, s * 0.12 * env);
    poseHead(0, 0, -s * 0.06 * env);
    poseLeg('L', 0, Math.max(0, s) * 0.05 * env);
    poseLeg('R', 0, Math.max(0, -s) * 0.05 * env);
  }},
  // Ponder — a hand drifts up toward the chin, head tilts
  ponder: { dur: 3.4, arms: true, fn(p) {
    const env = Math.sin(p * Math.PI);
    // Left arm stays relaxed; right blends up to bring the hand to the chin.
    relaxArm('L', ARM_REST_DOWN, 0, ARM_REST_ELBOW);
    relaxArm('R', ARM_REST_DOWN * (1 - env), 0, ARM_REST_ELBOW * (1 - env));
    poseArm('R', 0.18 * env, 0.16 * env);
    poseForearm('R', 1.35 * env);
    poseHead(0.06 * env, 0.12 * env, 0.14 * env);
    rotW('Spine01', _worldY, 0.06 * env);
  }},
};



// ---------- baked animation playback ----------
// The buttons that trigger baked clips live in the editor top bar, but the
// functions are core: setPlayMode() stops baked playback when the game takes
// over the skeleton.
function playBaked(name, opts = {}) {
  if (!mixer || !bakedClips[name]) return false;
  const { loop = THREE.LoopRepeat, timeScale = 1, clamp = false } = opts;
  if (activeAction) {
    activeAction.stop();
    activeAction.enabled = false;
    activeAction.setEffectiveWeight(0);
  }
  const action = mixer.clipAction(bakedClips[name]);
  action.enabled = true;
  action.setEffectiveWeight(1);
  action.reset();
  action.setLoop(loop, loop === THREE.LoopOnce ? 1 : Infinity);
  action.clampWhenFinished = clamp;
  action.timeScale = timeScale;
  action.fadeIn(0.2).play();
  activeAction = action;
  currentAnim = null;     // disable procedural while baked is playing
  resetSquash();          // baked anims don't use squash; clear any leftover
  return true;
}

function stopBaked() {
  if (!activeAction) return;

  // Procedural animations stop driving the AnimationMixer, so a fade scheduled
  // here would never be advanced and its non-zero weight could linger. Disable
  // the baked action immediately before handing the skeleton back to code.
  activeAction.stop();
  activeAction.enabled = false;
  activeAction.setEffectiveWeight(0);
  if (mixer) mixer.stopAllAction();
  activeAction = null;
}

// ====================================================================
// PLAY MODE: keyboard-controlled character with physics
// ====================================================================
// Double jump: one extra leap is allowed in mid-air (a deliberate second press).
// The second jump does a quick full-turn twirl so it reads clearly to a kid.
const MAX_AIR_JUMPS = 1;        // extra jumps allowed while airborne
const DOUBLE_JUMP_SPIN = 0.5;   // seconds for the full-turn twirl flourish
// A normal flat-ground jump lands around jumpVel. Keep that landing cozy: no
// camera shake or hard butt-slam pose unless Chocky drops from meaningfully higher.
const HARD_LANDING_VEL = 9.0;
const player = {
  pos: new THREE.Vector3(0, 0, 0),
  vel: new THREE.Vector3(0, 0, 0),
  facing: 0,
  facingTarget: 0,
  onGround: true,
  // Height of the surface Chocky is standing on (terrain or a platform top),
  // terrain-local. Updated only while grounded so the camera can frame him atop
  // raised platforms without bobbing during a jump arc.
  standY: 0,
  state: 'idle',
  stateTime: 0,
  walkSpeed: 2.4,
  runSpeed: 5.0,
  jumpVel: 7.6,
  gravityUp: -19,
  gravityDown: -42,
  isJumping: false,
  coyoteTimer: 0,
  jumpBuffer: 0,
  airJumps: 0,             // mid-air jumps used since leaving the ground (double jump)
  spin: 0,                 // remaining seconds of the double-jump twirl flourish
  // Idle fidgets: when he stands around he glances about, stretches, etc.,
  // and occasionally does a friendly baked emote (wave). Coin pickups fire a
  // baked Cheer through the same emote channel.
  idleTime: 0,
  emote: null,             // active baked one-shot { name, time, dur, cancelOnMove }
  bakedState: null,        // which locomotion clip the active baked action represents
  fidget: null,            // { name, time, dur } of the active procedural fidget
  fidgetCooldown: 4,       // seconds until the next fidget
  // Shell Shocker easter egg: damage taken, brief i-frames, death/respawn.
  hits: 0,
  invuln: 0,
  dead: false,
  deadTimer: 0,
};

const VILLAGE_SAFE_RADIUS = 14;
const VILLAGE_PATH_SAFE_DIST = 1.4;
const lastSafeVillagePos = new THREE.Vector3(0, 0, 0);
let lastSafeVillageFacing = 0;
let hasLastSafeVillagePos = false;

function distToVillagePathSegment(x, z, seg) {
  const dx = seg.x2 - seg.x1, dz = seg.z2 - seg.z1;
  const lenSq = dx * dx + dz * dz;
  const t = lenSq ? Math.max(0, Math.min(1, ((x - seg.x1) * dx + (z - seg.z1) * dz) / lenSq)) : 0;
  const px = seg.x1 + dx * t, pz = seg.z1 + dz * t;
  return Math.hypot(x - px, z - pz);
}

function isSafeVillageCheckpoint(x, z) {
  // Runs every grounded frame (updateLastSafeVillageCheckpoint) — indexed loops,
  // not .some(), so no per-frame closure allocation on the hot path.
  if (Math.hypot(x, z) <= VILLAGE_SAFE_RADIUS) return true;
  for (let i = 0; i < villagePathSegments.length; i++) {
    if (distToVillagePathSegment(x, z, villagePathSegments[i]) <= VILLAGE_PATH_SAFE_DIST) return true;
  }
  for (let i = 0; i < villageLandmarks.length; i++) {
    const l = villageLandmarks[i];
    if (Math.hypot(x - l.x, z - l.z) <= l.r) return true;
  }
  return false;
}

function updateLastSafeVillageCheckpoint() {
  if (!gameMode || player.dead || !player.onGround || !isSafeVillageCheckpoint(player.pos.x, player.pos.z)) return;
  lastSafeVillagePos.copy(player.pos);
  lastSafeVillageFacing = player.facing;
  hasLastSafeVillagePos = true;
}

function getVillageRespawnCheckpoint() {
  return {
    pos: hasLastSafeVillagePos ? lastSafeVillagePos : new THREE.Vector3(0, 0, 0),
    facing: hasLastSafeVillagePos ? lastSafeVillageFacing : 0,
  };
}

function resetPlayerMovementState({ position = player.pos, facing = player.facing } = {}) {
  stopBaked();
  clearMoveTarget();   // teleports/respawns cancel any tap-to-move goal
  player.pos.copy(position);
  player.vel.set(0, 0, 0);
  player.facing = player.facingTarget = facing;
  player.onGround = true;
  player.standY = heightAt(player.pos.x, player.pos.z);
  _camGroundY = null;  // snap the camera height anchor instead of sliding on teleport
  _camAnchorX = _camAnchorZ = null;  // and snap the diorama soft-follow anchor
  player.state = 'idle';
  player.stateTime = 0;
  player.isJumping = false;
  player.coyoteTimer = 0;
  player.jumpBuffer = 0;
  player.airJumps = 0;
  player.spin = 0;
  player.idleTime = 0;
  player.emote = null;
  player.bakedState = null;
  player.fidget = null;
  player.fidgetCooldown = 4;
}

// Fast-travel home (minimap "🏠 Home" button, src/game/minimap.js). Unlike the
// respawn checkpoint, this always lands on the open spawn plaza at the origin —
// a predictable, forgiving "take me back" for a young player who wandered off.
// Cozy: it clears any threat pressure (like the manual respawn) and never fails.
function goHome() {
  if (!gameMode) return;
  clearShellShockers();
  clearSkullface();
  clearAllWolves();
  player.hits = 0;
  player.invuln = 1.2;
  resetPlayerMovementState({ position: new THREE.Vector3(0, 0, 0), facing: 0 });
  if (chocky) {
    chocky.visible = true;
    chocky.position.set(0, initialY, 0);
    chocky.rotation.y = 0;
  }
  spawnDust(0, initialY, 0, 14, 1.6);
  triggerFace('grin', 0.6);
  updateEggHud();
}

function movePlayerToVillageCheckpoint(dustCount = 12, dustSize = 1.4) {
  const spawn = getVillageRespawnCheckpoint();
  player.hits = 0;
  player.invuln = 0;
  resetPlayerMovementState({ position: spawn.pos, facing: spawn.facing });
  if (chocky) {
    chocky.visible = true;
    chocky.position.set(spawn.pos.x, initialY + spawn.pos.y, spawn.pos.z);
    chocky.rotation.y = spawn.facing;
  }
  spawnDust(spawn.pos.x, initialY + spawn.pos.y, spawn.pos.z, dustCount, dustSize);
}

// Baked one-shot "emotes" (Wave, Cheer, …) briefly take over the skeleton on
// top of gameplay. They only start while grounded; the lifecycle in updatePlayer
// ends them on completion, on a jump, or — for idle gestures — once he moves.
function triggerEmote(name, { cancelOnMove = false } = {}) {
  if (player.emote || !player.onGround || !bakedClips[name]) return false;
  if (!playBaked(name, { loop: THREE.LoopOnce, clamp: true })) {
    player.emote = null;
    return false;
  }
  player.fidget = null;
  player.emote = { name, time: 0, dur: bakedClips[name].duration, cancelOnMove };
  return true;
}

// Which baked clip drives each play-mode state (only when that clip exists;
// otherwise the procedural poses below run). Dispatch keys on the clip NAME, so
// states that share a clip never restart it: Run reuses Walk sped up (crossing
// the walk/run threshold just retunes timeScale — the cycle keeps looping), and
// the whole jump arc (up→down→land) shares the one-shot Jump clip so it isn't
// restarted mid-air. Idle/Walk loop; the jump one-shot clamps on its last frame.
const STATE_CLIP = {
  idle:     { name: 'Idle', loop: THREE.LoopRepeat },
  walk:     { name: 'Walk', loop: THREE.LoopRepeat },
  run:      { name: 'Walk', loop: THREE.LoopRepeat, timeScale: 1.5 },
  jumpUp:   { name: 'Jump', loop: THREE.LoopOnce, clamp: true },
  jumpDown: { name: 'Jump', loop: THREE.LoopOnce, clamp: true },
  land:     { name: 'Jump', loop: THREE.LoopOnce, clamp: true },
};
function wantBakedClip(state) {
  const c = STATE_CLIP[state];
  return (c && bakedClips[c.name]) ? c : null;
}

const REQUIRED_BAKED_CLIPS = ['Idle', 'Walk', 'Jump', 'Cheer', 'IdleFidget', 'Wave'];
function validateBakedClips() {
  const missing = REQUIRED_BAKED_CLIPS.filter((name) => !bakedClips[name]);
  if (!missing.length) return;
  console.warn(`[anim] chocky.glb is missing baked clip(s): ${missing.join(', ')}. Available clips: ${Object.keys(bakedClips).join(', ') || '(none)'}`);
}

function syncBakedJumpClip() {
  if (!activeAction || !activeAction.getClip || activeAction.getClip().name !== 'Jump') return;
  const dur = activeAction.getClip().duration || 0;
  if (dur <= 0) return;
  let phase = 0;
  if (player.state === 'jumpUp') {
    const k = THREE.MathUtils.clamp(1 - Math.max(0, player.vel.y) / player.jumpVel, 0, 1);
    phase = 0.08 + k * 0.30;
  } else if (player.state === 'jumpDown') {
    const k = THREE.MathUtils.clamp(Math.max(0, -player.vel.y) / player.jumpVel, 0, 1);
    phase = 0.40 + k * 0.28;
  } else if (player.state === 'land') {
    const k = THREE.MathUtils.clamp(player.stateTime / 0.18, 0, 1);
    phase = 0.70 + k * 0.28;
  } else {
    return;
  }
  activeAction.time = THREE.MathUtils.clamp(phase * dur, 0, Math.max(0, dur - 0.0001));
}

function applyPlayerModelTransform() {
  if (!chocky) return;
  chocky.position.set(player.pos.x, initialY + player.pos.y, player.pos.z);
  const twirl = player.spin > 0 ? (1 - player.spin / DOUBLE_JUMP_SPIN) * Math.PI * 2 : 0;
  chocky.rotation.y = player.facing + twirl;
}

// Pick and start the next idle gesture. Small glances are always available;
// the bigger stretch/yawn (and the baked wave) only once he's been idle a while.
function triggerIdleFidget() {
  const longIdle = player.idleTime > 16;
  // Prefer Alex's baked gestures when present (they replace the procedural
  // glances, which can't layer over the baked Idle clip): a friendly Wave once
  // he's been idle a while, otherwise the baked idle-fidget.
  if (longIdle && Math.random() < 0.4 && triggerEmote('Wave', { cancelOnMove: true })) return;
  if (triggerEmote('IdleFidget', { cancelOnMove: true })) return;
  // Procedural fallback (used only when no baked clips are loaded).
  const small = ['lookAround', 'shiftWeight', 'tapFoot', 'lookUp', 'ponder'];
  const big   = ['stretch', 'yawn'];
  const pool  = longIdle ? small.concat(big) : small;
  const name  = pool[(Math.random() * pool.length) | 0];
  player.fidget = { name, time: 0, dur: IDLE_FIDGETS[name].dur };
}

const keys = {};
let prevSpaceDown = false;   // for edge detection inside updatePlayer
const _nearObs = [];         // reused spatial-hash query result (no per-frame alloc)
// The player is an actor like everything else (world/actors.js) — the
// registry contract that keeps the multiplayer door open.
spawnActor('player', player);

// Dev/no-build only: a debug handle for the headless test scripts (and
// devtools poking). Compiled out of kid builds.
if (typeof __CHOCKY_BUILD__ === 'undefined' || !__CHOCKY_BUILD__) {
  window.__chockyDebug = {
    player,
    heightAt,
    chunkStats,
    actors,
    scene,            // for headless editor/world tests (project + pick objects)
    get gameMode() { return gameMode; },
    get nearbyHut() { return nearbyHut; },
    camera,
    get cameraMode() { return cameraMode; },
    setCameraMode(m) { setPlayCameraMode(m); },
    get camPullDist() { return _camPullDist; },
    // Camera azimuth (atan2 of eye->Chocky in XZ): a swing shows up as this
    // value changing a lot; the diorama keeps it ~constant as Chocky turns.
    get cameraAzimuth() {
      return Math.atan2(camera.position.x - chocky.position.x,
                        camera.position.z - chocky.position.z);
    },
    get camAnchor() { return { x: _camAnchorX, z: _camAnchorZ }; },
    // Occlusion-fade introspection: instance ghosting (trees) via chunks.js, and
    // the deepest structure (hut) ghost. lastUploads === 0 when nothing changed
    // proves the per-frame update does no wasted GPU work while idle.
    getOcclusionFadeState() {
      const inst = readOcclusionFadeState();
      let minStructureOpacity = 1, fadedStructures = 0;
      for (const s of fadeStructures) {
        const o = s.mats[0].opacity;
        if (o < minStructureOpacity) minStructureOpacity = o;
        if (o < 0.95) fadedStructures++;
      }
      return { ...inst, structures: fadeStructures.length, minStructureOpacity, fadedStructures };
    },
    // GPU sway introspection: shared clock value + loaded meshes carrying aSway.
    getSwayState: readSwayState,
    // Enemy toggles (ESC-menu Wolves / Shell Shockers), for the options test.
    get wolvesEnabled() { return WOLVES_ENABLED; },
    get shellShockersEnabled() { return SHELL_SHOCKERS_ENABLED; },
    enumerateProps,   // {type, d:{x,z,gy,scale,...}} — lets tests find a tree to hide behind
    // Collision probe: how many static/movable obstacles sit in the r-box around
    // (x,z). Used by editorshelltest to confirm an added structure is solid.
    obstaclesNear(x, z, r = 3) { const out = []; collectObstaclesNear(x - r, z - r, x + r, z + r, out); return out.length; },
    // World-space centres + radii of the fadeable structures, for tests that
    // need to stand Chocky behind a known hut.
    getFadeStructureCenters() {
      return fadeStructures.map(s => ({ x: s.cx, y: s.cy, z: s.cz, r: s.r }));
    },
    // Test teleport: place Chocky on the ground at (x, z), optionally facing
    // a given yaw (0 = +Z).
    setPlayerPos(x, z, facing = player.facing) {
      resetPlayerMovementState({ position: new THREE.Vector3(x, heightAt(x, z), z), facing });
    },
    // Outfit equip/inspect for headless verification (this whole debug surface
    // is gated out of the kid build; the closet UI drives outfits.js directly).
    equipOutfit, getEquippedOutfit,
    get bones() { return bones; },
  };
  // Dev-only handmade-model preview: ?model=foo.glb drops a .glb into the world.
  // Lazy-loaded so it stays out of the kid bundle entirely.
  if (new URLSearchParams(location.search).has('model')) {
    import('./dev/modelPreview.js').then((m) => m.mountModelPreview());
  }
}
function isTypingTarget(target) {
  return !!target && (
    target.isContentEditable ||
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)
  );
}
function clearInputState() {
  for (const k of Object.keys(keys)) keys[k] = false;
  for (const k of Object.keys(keyHold)) keyHold[k] = 0;
  prevSpaceDown = false;
}
const keyHold = Object.create(null);
addEventListener('keydown', e => {
  if (isTypingTarget(e.target)) return;
  const k = e.key.toLowerCase();
  if (!keys[k]) keyHold[k] = 0;
  keys[k] = true;
  if (gameMode && [' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
    e.preventDefault();
  }
});
addEventListener('keyup', e => {
  const k = e.key.toLowerCase();
  keys[k] = false;
  keyHold[k] = 0;
});
addEventListener('blur', clearInputState);
document.addEventListener('visibilitychange', () => { if (document.hidden) clearInputState(); });

// Touch + tap-to-move input: writes into touchInput, consumed by
// updatePlayer() alongside the keyboard. The joystick/jump overlay only
// appears on touch devices; tap/click-to-move works for everyone.
initTouchControls({
  scene, camera,
  canvas: renderer.domElement,
  terrainMesh: playFloor,    // taps raycast the real hills, not a y=0 plane
  raycastObstacleTops,       // taps on platforms/roofs choose standable tops
  heightAt,
  getPlayerPos: () => player.pos,
  // Tap-to-move is off while an editor is open, so a click that selects a
  // building doesn't also send Chocky walking toward it.
  isActive: () => gameMode && !player.dead && !editorHooks.editing,
});

let gameMode = false;
const playHelp = document.getElementById('playHelp');
const playHelpChip = document.getElementById('playHelpChip');
let playHelpCollapseTimer = null;

function clearPlayHelpTimer() {
  if (playHelpCollapseTimer) {
    clearTimeout(playHelpCollapseTimer);
    playHelpCollapseTimer = null;
  }
}
function showPlayHelpFull() {
  clearPlayHelpTimer();
  playHelp.classList.add('visible');
  playHelp.classList.remove('collapsed');
  playHelpChip.setAttribute('aria-expanded', 'true');
}
function collapsePlayHelp() {
  if (!gameMode) return;
  playHelp.classList.add('visible', 'collapsed');
  playHelpChip.setAttribute('aria-expanded', 'false');
}
function schedulePlayHelpCollapse() {
  clearPlayHelpTimer();
  playHelpCollapseTimer = setTimeout(collapsePlayHelp, 5000);
}
function hidePlayHelp() {
  clearPlayHelpTimer();
  playHelp.classList.remove('visible', 'collapsed');
  playHelpChip.setAttribute('aria-expanded', 'false');
}
playHelpChip.addEventListener('click', () => {
  // Expand the controls again, then re-collapse to the capsule after ~5s.
  showPlayHelpFull();
  schedulePlayHelpCollapse();
});

// Editor world-preview flag (see setEditorWorldView below). Declared above
// setPlayMode because setPlayMode clears it on every mode toggle.
let editorWorldView = false;

// Despawn every enemy and clear their FX pools. Both setPlayMode branches reset
// the same set on entering and leaving play mode; one helper keeps them in step.
function clearAllEnemies() {
  clearAllWolves();
  wizardLightningFx.clear();
  clearWolfAshPiles();
  clearSkullface();
  clearShellShockers();
}

// Enter/exit play mode. The kid build calls this exactly once at boot (it has
// no UI to leave play mode); the editor's ▶ Play button toggles it freely.
function setPlayMode(on) {
  gameMode = on;
  // setPlayMode owns scenery visibility directly, so the editor-preview override
  // below no longer reflects reality — clear it. (If an editor stays open across
  // the toggle, editor.js re-asserts the preview in its onPlayModeChange hook.)
  editorWorldView = false;

  // Toggle scenery
  floor.visible = !gameMode;
  playFloor.visible = gameMode;
  townGround.visible = gameMode;
  playShadowOverlay.visible = gameMode;
  skyWash.visible = gameMode;
  grid.visible = false;   // textured ground reads better than the reference grid
  propsGroup.visible = gameMode;
  // The set of visible shadow casters just changed — force a shadow refresh on
  // the next frame (we otherwise refresh at a reduced cadence).
  renderer.shadowMap.needsUpdate = true;

  if (gameMode) {
    editorHooks.autoSpin = false;
    clearInputState();
    resetPlayerMovementState({ position: new THREE.Vector3(0, 0, 0), facing: 0 });
    hasLastSafeVillagePos = false;
    lastSafeVillagePos.set(0, 0, 0);
    lastSafeVillageFacing = 0;
    player.hits = 0;
    player.invuln = 0;
    player.dead = false;
    player.deadTimer = 0;
    if (chocky) chocky.visible = true;
    stopBaked();
    currentAnim = null;
    showPlayHelpFull();
    schedulePlayHelpCollapse();
    setDomDisplay('playCamUI', 'block');
    setDomDisplay('coinHud', 'block');
    const cbHud = document.getElementById('chromebookHud');
    if (cbHud) cbHud.classList.toggle('visible', LOW);
    minimapApi.setVisible(true);
    cameraMode = 'fixed';
    playZoom = 1;
    clearFootprints();
    syncCamButtons();
    clearMoveTarget();
    setTouchUIVisible(true);
    controls.enabled = false;
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    window.focus();
    prevSpaceDown = !!keys[' '];
    clearAllEnemies();
    updateEggHud();
    // Reset coin pickups (the wallet itself persists — see game/save.js).
    // One-shot quest coins are exempt: dug-up treasure stays dug up.
    updateCoinUI();
    for (const c of coins) {
      if (c.once) continue;
      c.collected = false;
      c.respawnTimer = 0;
      c.group.visible = true;
    }
  } else {
    clearInputState();
    clearAllEnemies();
    player.dead = false;
    player.hits = 0;
    if (chocky) chocky.visible = true;
    setDomDisplay('eggHud', 'none');
    eggHudShown = '';     // direct hide above bypasses updateEggHud's change guard
    if (chockyRoot) { chockyRoot.position.set(0, initialY, 0); chockyRoot.rotation.y = 0; }
    currentAnim = 'idle';
    nearbyHut = null;
    nearWell = false;
    _wellSplash = 0;
    _wellState = null;
    storeApi.close();
    hideBumpMsg();
    setDomDisplay('gameDialog', 'none');
    shownHutCard = undefined;   // direct hide bypasses updateGameHutDialog's change guard
    hidePlayHelp();
    setDomDisplay('playCamUI', 'none');
    setDomDisplay('coinHud', 'none');
    const cbHud = document.getElementById('chromebookHud');
    if (cbHud) cbHud.classList.remove('visible');
    minimapApi.setVisible(false);
    setTouchUIVisible(false);   // also clears any tap-to-move target
    controls.enabled = true;
    camera.position.set(0, 1.4, 4.2);
    controls.target.set(0, 0.9, 0);
  }
  // Editor UI follows along (▶/■ button label, gizmo detach, panel hiding…)
  editorHooks.onPlayModeChange(gameMode);
}

// ====================================================================
// EDITOR WORLD PREVIEW
// ====================================================================
// The object editor (! key) and terrain sculptor let authors move/rotate/scale
// the village and reshape the hills — but the village (propsGroup) and the
// terrain (playFloor) are only shown in ▶ play mode. The default Chocky-turntable
// viewer hid them, so pressing ! there left nothing on screen to click and the
// click-picker (which skips invisible objects) silently selected nothing. This
// shows the whole village WITHOUT entering play mode (no player reset, free
// orbit camera) so editing is reliable straight from the landing view. It is a
// no-op in play mode, where everything is already visible.
// (editorWorldView is declared above setPlayMode, which clears it on toggle.)
const _editorCamSave = { pos: new THREE.Vector3(), target: new THREE.Vector3() };
function setEditorWorldView(on) {
  on = !!on;
  if (on === editorWorldView) return;
  editorWorldView = on;
  if (gameMode) return;            // play mode already shows the whole village
  propsGroup.visible = on;
  playFloor.visible = on;
  townGround.visible = on;
  playShadowOverlay.visible = on;
  floor.visible = !on;             // hide the turntable disc while editing the village
  controls.enabled = true;         // always orbitable while framing an edit
  if (on) {
    // Frame the village from a 3/4 angle so the buildings are visible & clickable.
    _editorCamSave.pos.copy(camera.position);
    _editorCamSave.target.copy(controls.target);
    camera.position.set(16, 13, 20);
    controls.target.set(0, 1, 0);
  } else {
    camera.position.copy(_editorCamSave.pos);
    controls.target.copy(_editorCamSave.target);
  }
  controls.update();
  renderer.shadowMap.needsUpdate = true;
}

// Chocky's collision box: horizontal radius and feet-to-top height.
const PLAYER_COLLIDE_R = 0.35;
const PLAYER_COLLIDE_H = 1.6;
// Do we vertically overlap this obstacle (so its sides can block us)? Runs once
// per obstacle per axis inside the per-frame collision sweep — a module-scope
// function reading the module player, NOT a closure re-created every frame.
function blocksHorizontally(ob) {
  const ourBot = player.pos.y;
  const ourTop = player.pos.y + PLAYER_COLLIDE_H;
  // Need clearance: 0.05 below the top so stepping ON it doesn't trigger side-block.
  return ourTop > ob.minY + 0.05 && ourBot < ob.maxY - 0.05;
}

// Re-apply the editor's live bone rotations onto the rig (the map is empty in
// play/kid builds, so this is a no-op there). Extracted so every pose path
// re-asserts overrides identically — a new path can't forget to.
function applyBoneOverrides() {
  for (const [bone, q] of editorHooks.boneOverrides) bone.quaternion.copy(q);
}

function updatePlayer(dt) {
  // ---- Disintegrated by Shell Shockers: freeze input/physics while the ash
  // settles, then regenerate back at the town square (see eggHitPlayer).
  if (player.dead) {
    player.deadTimer -= dt;
    if (player.invuln > 0) player.invuln -= dt;
    if (player.deadTimer <= 0) regeneratePlayer();
    updateGameCamera(dt);
    return;
  }
  if (player.invuln > 0) player.invuln -= dt;

  // ---- Input
  for (const k of Object.keys(keys)) {
    if (keys[k]) keyHold[k] = (keyHold[k] || 0) + dt;
  }
  // ARROW keys: tank-style (forward/back in facing direction, left/right rotate)
  let arrowFwd = 0, arrowTurn = 0;
  if (keys['arrowup'])    arrowFwd  += 1;
  if (keys['arrowdown'])  arrowFwd  -= 1;
  if (keys['arrowleft'])  arrowTurn += 1;   // CCW (turn left)
  if (keys['arrowright']) arrowTurn -= 1;

  // WASD keys: camera-relative strafe (auto-face direction). During Obby
  // Peak's chase camera, A/D become tank turns too, matching the arrow keys
  // so narrow-platform steering ramps up instead of snapping sideways.
  const obbyChaseControls = obbyCameraAssist && cameraMode === OBBY_CAMERA_MODE;
  if (obbyChaseControls) {
    if (keys['a']) arrowTurn += 1;
    if (keys['d']) arrowTurn -= 1;
  }
  let ix = 0, iz = 0;
  if (keys['w']) iz -= 1;
  if (keys['s']) iz += 1;
  if (!obbyChaseControls && keys['a']) ix -= 1;
  if (!obbyChaseControls && keys['d']) ix += 1;
  // Virtual joystick: same screen semantics as WASD (stick up = screen up),
  // so it rides the same camera-relative transform below.
  ix += touchInput.stickX;
  iz += touchInput.stickY;
  const wasdLen = Math.hypot(ix, iz);
  if (wasdLen > 0) {
    ix /= wasdLen;
    iz /= wasdLen;

    // Project the active play camera's yaw onto the ground plane. `camera.getWorldDirection()`
    // points toward the top of the screen once flattened, while its perpendicular is the
    // matching screen-right strafe direction. This keeps WASD camera-relative without
    // changing arrow-key tank controls below.
    camera.getWorldDirection(_wasdCamDir);
    let camFwdX = _wasdCamDir.x;
    let camFwdZ = _wasdCamDir.z;
    let camLen = Math.hypot(camFwdX, camFwdZ);
    if (camLen < 0.0001) {
      camFwdX = Math.sin(player.facing);
      camFwdZ = Math.cos(player.facing);
      camLen = Math.hypot(camFwdX, camFwdZ) || 1;
    }
    camFwdX /= camLen;
    camFwdZ /= camLen;
    const camRightX = -camFwdZ;
    const camRightZ = camFwdX;
    const worldIx = camRightX * ix + camFwdX * -iz;
    const worldIz = camRightZ * ix + camFwdZ * -iz;
    ix = worldIx;
    iz = worldIz;
  }

  // ---- Tap/click-to-move: steer toward the tapped goal until direct input
  // takes over, we arrive, or we've been pushing against a prop for half a
  // second (no pathfinding — collision stops us, then the target cancels).
  let tapSteer = false;
  let tapRun = false;
  if (touchInput.moveTarget) {
    if (wasdLen > 0 || arrowFwd !== 0 || arrowTurn !== 0) {
      clearMoveTarget();   // hands on the controls beat the tap goal
    } else {
      const tdx = touchInput.moveTarget.x - player.pos.x;
      const tdz = touchInput.moveTarget.z - player.pos.z;
      const tdist = Math.hypot(tdx, tdz);
      touchInput.targetAge += dt;
      const sp = Math.hypot(player.vel.x, player.vel.z);
      // Grace period lets acceleration spin up before "blocked" can trigger.
      if (touchInput.targetAge > 0.4 && sp < 0.4) touchInput.blockedTime += dt;
      else touchInput.blockedTime = 0;
      if (tdist < 0.3 || touchInput.blockedTime > 0.5) {
        clearMoveTarget();
      } else {
        ix = tdx / tdist;        // world-space already — skips the camera math
        iz = tdz / tdist;
        tapSteer = true;
        tapRun = !!touchInput.targetRun;  // latched from the original click distance
      }
    }
  }

  const running = !!keys['shift'] || touchInput.stickMag > RUN_DEFLECT || tapRun;
  const speed = running ? player.runSpeed : player.walkSpeed;
  let turnSpeed = 3.0;   // radians per second when rotating with arrows
  const chaseTurnRamp = cameraMode === 'first' || obbyChaseControls;
  if (chaseTurnRamp && (arrowTurn !== 0 || wasdLen > 0)) {
    // In close chase-style cameras, full-speed snap-turns feel twitchy on a
    // narrow platform. Arrow turns already ease up from a gentle start; give
    // WASD's auto-facing the same held-key ramp during the Obby assist camera.
    const held = Math.max(
      keyHold['arrowleft'] || 0,
      keyHold['arrowright'] || 0,
      keyHold['w'] || 0,
      keyHold['a'] || 0,
      keyHold['s'] || 0,
      keyHold['d'] || 0,
    );
    const ramp = Math.min(1, held / 0.85);
    turnSpeed = 0.85 + (3.0 - 0.85) * ramp * ramp;
  }

  // ---- Apply arrow turning directly to facing
  if (arrowTurn !== 0) {
    player.facing += arrowTurn * turnSpeed * dt;
    player.facingTarget = player.facing;   // keep target in sync so smoothing below doesn't fight us
  }

  // ---- Horizontal movement with acceleration (less robotic than instant velocity)
  const fwdX = Math.sin(player.facing);
  const fwdZ = Math.cos(player.facing);
  let tgtVx = fwdX * arrowFwd * speed + ix * speed;
  let tgtVz = fwdZ * arrowFwd * speed + iz * speed;
  // Snappier on ground, decent air control
  const accel = player.onGround ? 22 : 8;
  player.vel.x += (tgtVx - player.vel.x) * Math.min(1, accel * dt);
  player.vel.z += (tgtVz - player.vel.z) * Math.min(1, accel * dt);
  // Snap tiny residual velocity to zero so the idle anim engages cleanly
  if (Math.abs(player.vel.x) < 0.02 && tgtVx === 0) player.vel.x = 0;
  if (Math.abs(player.vel.z) < 0.02 && tgtVz === 0) player.vel.z = 0;

  // ---- Mario-style jump
  // Edge-detect space inside updatePlayer (not in the event listener) — robust
  const spaceDown = !!keys[' '] || touchInput.jumpHeld;
  const spaceJustPressed = spaceDown && !prevSpaceDown;
  prevSpaceDown = spaceDown;

  if (spaceJustPressed) player.jumpBuffer = 0.12;
  player.jumpBuffer -= dt;
  player.coyoteTimer -= dt;
  // A grounded (or coyote-grace) press is the first jump; a genuine mid-air press
  // spends an air jump and twirls.
  const groundedJump = player.onGround || player.coyoteTimer > 0;
  const canJump = groundedJump || player.airJumps < MAX_AIR_JUMPS;
  if (player.jumpBuffer > 0 && canJump) {
    const airJump = !groundedJump;
    player.vel.y = player.jumpVel;   // full reset (overrides any downward fall velocity)
    player.onGround = false;
    player.isJumping = true;
    player.jumpBuffer = 0;
    player.coyoteTimer = 0;
    if (airJump) {
      player.airJumps++;
      player.spin = DOUBLE_JUMP_SPIN;   // kick off the twirl flourish
    }
    // Puff of dust under his feet at launch (use actual feet level)
    spawnDust(player.pos.x, initialY + player.pos.y, player.pos.z, 8, 1.0);
  }
  // Variable height: release space early → short hop (~50% height)
  if (player.isJumping && !spaceDown && player.vel.y > 0) {
    player.vel.y *= 0.55;        // 0.55² ≈ 30% of full apex
    player.isJumping = false;
  }
  if (player.vel.y <= 0) player.isJumping = false;

  // ---- Gravity + integration (asymmetric: heavier when falling)
  if (!player.onGround) {
    const g = player.vel.y > 0 ? player.gravityUp : player.gravityDown;
    player.vel.y += g * dt;
  }

  // ===== Per-axis AABB collision against props =====
  const R = PLAYER_COLLIDE_R;   // Chocky's collision box; blocksHorizontally() is
  const H = PLAYER_COLLIDE_H;   // hoisted to module scope, R/H alias the consts.

  // One spatial-hash query covers the whole frame's swept volume (the old
  // code scanned every obstacle in the world three times per frame).
  const sweep = R + 0.6 + Math.max(Math.abs(player.vel.x), Math.abs(player.vel.z)) * dt;
  const obCount = collectObstaclesNear(
    player.pos.x - sweep, player.pos.z - sweep,
    player.pos.x + sweep, player.pos.z + sweep,
    _nearObs
  );

  // ---- X axis
  player.pos.x += player.vel.x * dt;
  for (let _oi = 0; _oi < obCount; _oi++) {
    const ob = _nearObs[_oi];
    if (!blocksHorizontally(ob)) continue;
    if (player.pos.x + R > ob.minX && player.pos.x - R < ob.maxX &&
        player.pos.z + R > ob.minZ && player.pos.z - R < ob.maxZ) {
      if (player.vel.x > 0) player.pos.x = ob.minX - R;
      else if (player.vel.x < 0) player.pos.x = ob.maxX + R;
      player.vel.x = 0;
    }
  }
  // ---- Z axis
  player.pos.z += player.vel.z * dt;
  for (let _oi = 0; _oi < obCount; _oi++) {
    const ob = _nearObs[_oi];
    if (!blocksHorizontally(ob)) continue;
    if (player.pos.x + R > ob.minX && player.pos.x - R < ob.maxX &&
        player.pos.z + R > ob.minZ && player.pos.z - R < ob.maxZ) {
      if (player.vel.z > 0) player.pos.z = ob.minZ - R;
      else if (player.vel.z < 0) player.pos.z = ob.maxZ + R;
      player.vel.z = 0;
    }
  }
  // ---- Y axis (swept + sticky-ground for stable standing on blocks)
  const prevY = player.pos.y;
  player.pos.y += player.vel.y * dt;

  // Compute "ground level beneath us right now": the terrain heightfield,
  // raised by any prop top we were standing on. We count a block's top as
  // ground if we were AT OR ABOVE it last frame (so we can step onto it /
  // stay on it) — but NOT if we walked into its side from a lower height.
  let groundY = heightAt(player.pos.x, player.pos.z);
  for (let _oi = 0; _oi < obCount; _oi++) {
    const ob = _nearObs[_oi];
    const inXZ = player.pos.x + R > ob.minX && player.pos.x - R < ob.maxX &&
                 player.pos.z + R > ob.minZ && player.pos.z - R < ob.maxZ;
    if (!inXZ) continue;
    // "We were standing on or above this top" within a generous epsilon
    if (prevY >= ob.maxY - 0.02) {
      groundY = Math.max(groundY, ob.maxY);
    }
    // Head bonk: rising and hitting the bottom of a block above
    if (player.vel.y > 0 && prevY + H <= ob.minY + 0.02 && player.pos.y + H > ob.minY) {
      player.pos.y = ob.minY - H;
      player.vel.y = -0.5;
    }
  }

  const wasInAir = !player.onGround;
  if (player.pos.y <= groundY) {
    const landingVel = -player.vel.y;
    player.pos.y = groundY;
    player.vel.y = 0;
    if (!player.onGround) {
      player.onGround = true;
      // Only count it as a "real" landing if we were genuinely airborne (not
      // a sub-frame micro-bounce)
      if (wasInAir && landingVel > 0.5 && player.stateTime > 0.05) {
        const hardLanding = landingVel >= HARD_LANDING_VEL;
        if (hardLanding) {
          player.state = 'land';
          player.stateTime = 0;
        }
        const impactK = Math.min(1, landingVel / HARD_LANDING_VEL);
        spawnDust(player.pos.x, initialY + groundY, player.pos.z, Math.round(4 + impactK * 10), 0.65 + impactK * 0.6);
        questApi.onPlayerLand(player.pos.x, player.pos.z);
        if (hardLanding) {
          addCamShake(0.04 + impactK * 0.12);
          // A real thump leaves him briefly dazed (cross-eyed); a softer drop just startles.
          if (impactK > 0.85) triggerFace('dizzy', 0.6);
          else triggerFace('surprised', 0.25);
        }
      }
    }
  } else if (player.onGround) {
    if (player.vel.y <= 0 && player.pos.y - groundY < 0.35) {
      // Walking down a gentle slope: glue feet to the ground instead of
      // micro-falling every frame (which would spam the land state and break
      // the walk animation on every downhill step).
      player.pos.y = groundY;
    } else {
      // Genuinely stepped off something — start falling
      player.onGround = false;
      player.coyoteTimer = 0.1;
    }
  }

  // Record the surface height while grounded so the camera frames Chocky on top
  // of platforms. Held during jumps (only set when grounded) so a jump arc never
  // bobs the camera; it eases up once he lands on a higher platform.
  if (player.onGround) { player.standY = groundY; player.airJumps = 0; }

  // ---- Facing direction (WASD strafe and tap-to-move want auto-face; arrow
  // turn sets it directly)
  if (arrowTurn === 0 && (wasdLen > 0 || tapSteer)) {
    player.facingTarget = Math.atan2(ix, iz);
    let diff = player.facingTarget - player.facing;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    if (chaseTurnRamp && wasdLen > 0 && !tapSteer) {
      const step = Math.min(Math.abs(diff), turnSpeed * dt);
      player.facing += Math.sign(diff) * step;
    } else {
      player.facing += diff * Math.min(1, dt * 12);
    }
  }

  // ---- State machine
  player.stateTime += dt;
  const horizSpeed = Math.hypot(player.vel.x, player.vel.z);
  const isMoving = horizSpeed > 0.1;
  let newState = player.state;
  if (!player.onGround) {
    newState = player.vel.y > 0 ? 'jumpUp' : 'jumpDown';
  } else if (player.state === 'land' && player.stateTime < 0.18) {
    newState = 'land';
  } else if (isMoving) {
    newState = (running && horizSpeed > player.walkSpeed + 0.5) ? 'run' : 'walk';
  } else {
    newState = 'idle';
  }
  if (newState !== player.state) {
    player.state = newState;
    player.stateTime = 0;
  }

  // ---- Baked emote lifecycle (Wave/IdleFidget on idle, Cheer on coin pickup).
  // An emote owns the skeleton until it finishes, he jumps, or — for idle
  // gestures — he starts moving; then we hand it back to the state dispatch.
  if (player.emote) {
    player.emote.time += dt;
    const moving = player.state === 'walk' || player.state === 'run';
    const airborne = player.state === 'jumpUp' || player.state === 'jumpDown';
    if (player.emote.time >= player.emote.dur || airborne ||
        (player.emote.cancelOnMove && moving)) {
      stopBaked();
      player.emote = null;
      player.bakedState = null;            // let the dispatch restart locomotion
      player.idleTime = 0;
      player.fidgetCooldown = 4 + Math.random() * 4;
    }
  }

  // ---- Idle fidgets: glance around / stretch / tap a foot / wave the longer
  // he stands still. The cooldown shrinks the more he's been idle.
  if (player.state === 'idle' && !player.emote) {
    player.idleTime += dt;
    if (player.fidget) {
      player.fidget.time += dt;
      if (player.fidget.time >= player.fidget.dur) {
        player.fidget = null;
        player.fidgetCooldown = 3.5 + Math.random() * 4.5;
      }
    } else {
      player.fidgetCooldown -= dt;
      if (player.idleTime > 3 && player.fidgetCooldown <= 0) triggerIdleFidget();
    }
  } else if (player.state !== 'idle') {
    player.idleTime = 0;
    player.fidget = null;
  }

  // ---- Footstep dust during walk/run (small puff every half-cycle)
  if ((player.state === 'walk' || player.state === 'run') && player.onGround) {
    const cycleSpeed = player.state === 'run' ? 2 * Math.PI / 0.45 : 2 * Math.PI / 0.8;
    const stepCount = Math.floor(player.stateTime * cycleSpeed / Math.PI);
    if (stepCount !== player._lastStep) {
      player._lastStep = stepCount;
      // tiny puff behind/under
      spawnDust(
        player.pos.x - Math.sin(player.facing) * 0.15,
        initialY + player.pos.y,
        player.pos.z - Math.cos(player.facing) * 0.15,
        2, 0.5
      );
    }
  } else { player._lastStep = -1; }

  // ---- Footprints: their own, finer cadence so the prints sit close together
  // (a continuous trail) rather than one long stride apart.
  if ((player.state === 'walk' || player.state === 'run') && player.onGround) {
    const fpInterval = player.state === 'run' ? 0.14 : 0.22;
    const fpStep = Math.floor(player.stateTime / fpInterval);
    if (fpStep !== player._lastFpStep) {
      player._lastFpStep = fpStep;
      spawnFootprint(player.pos.x, player.pos.z, player.facing, (fpStep & 1) ? 1 : -1);
    }
  } else { player._lastFpStep = -1; }

  // ---- Pose FIRST (resetPose touches chockyRoot.position.y; we override below).
  // A baked emote, or the current state's baked clip, drives the skeleton via
  // mixer.update (called in the main tick after this). Only fall back to the
  // procedural poses when no baked clip covers the situation.
  resetPose();
  const wantClip = player.emote ? null : wantBakedClip(player.state);
  if (player.emote) {
    // one-shot emote mid-play — mixer.update writes its keyframes
  } else if (wantClip) {
    // (Re)start a locomotion clip only when the underlying CLIP changes — never
    // when only the gait does. Walk and Run are the SAME baked clip at different
    // speeds, so crossing the walk/run threshold (a tap-to-move long walk easing
    // down to a walk within 6 units, or a joystick wobbling around full
    // deflection) must NOT reset the cycle to frame 0 — that was the per-step
    // hitch. When the clip is already playing, just retune its timeScale so the
    // loop runs continuously as authored in the file.
    if (player.bakedState !== wantClip.name) {
      playBaked(wantClip.name, wantClip);
      player.bakedState = wantClip.name;
    } else if (activeAction) {
      activeAction.timeScale = wantClip.timeScale || 1;
    }
    syncBakedJumpClip();
    // mixer.update writes its keyframes after this
  } else {
    // No baked clip for this state (e.g. the original single-clip model) —
    // procedural poses; release any baked action first.
    if (activeAction) stopBaked();
    player.bakedState = null;
    const phase = player.stateTime;
    if (player.state === 'idle') {
      const fidget = player.fidget ? IDLE_FIDGETS[player.fidget.name] : null;
      ANIMS.idle(phase, !!(fidget && fidget.arms));
      if (fidget) {
        const fidgetPhase = THREE.MathUtils.clamp(player.fidget.time / player.fidget.dur, 0, 1);
        fidget.fn(fidgetPhase);
      }
    } else if (player.state === 'walk') {
      ANIMS.walk(phase);
    } else if (player.state === 'run') {
      runPose(phase);
    } else if (player.state === 'jumpUp') {
      jumpUpPose(player.vel.y);
    } else if (player.state === 'jumpDown') {
      jumpDownPose(-player.vel.y);
    } else if (player.state === 'land') {
      landPose(phase);
    }
  }
  applyBoneOverrides();

  // ---- Double-jump twirl: a cosmetic full turn over DOUBLE_JUMP_SPIN seconds,
  // layered on the model group (composes over the jump pose / baked clip below).
  if (player.spin > 0) player.spin = Math.max(0, player.spin - dt);

  // ---- THEN apply physics position (overrides any y-bob the pose tried to set).
  // This is repeated after AnimationMixer updates so baked root tracks cannot
  // erase facing or the double-jump twirl.
  applyPlayerModelTransform();

  updateLastSafeVillageCheckpoint();
  updateObbyCameraAssist();

  // ---- Camera follow (mode-dependent)
  updateGameCamera(dt);

  // (No per-frame shadow camera tracking — light is now static with a wide
  // frustum. Saved a shadow-matrix recompute per frame.)

  // ---- Game-hut proximity check
  updateGameHutDialog();
}

// Track which hut the player is near. Hysteresis (trigger vs release) keeps
// the dialog from flickering when you stand right at the edge.
let nearbyHut = null;
const HUT_TRIGGER_DIST = 1.4;
const HUT_RELEASE_DIST = 1.9;

let shownHutCard;   // last hut rendered into #gameDialog (undefined = force re-render)

function updateGameHutDialog() {
  let closest = null, closestD = Infinity;
  for (const h of gameHuts) {
    const dx = player.pos.x - h.entranceX, dz = player.pos.z - h.entranceZ;
    const d = Math.hypot(dx, dz);
    if (d < closestD) { closestD = d; closest = h; }
  }
  if (closest && closestD < HUT_TRIGGER_DIST) {
    nearbyHut = closest;
  } else if (nearbyHut && (!closest || closestD > HUT_RELEASE_DIST)) {
    nearbyHut = null;
  }
  // Runs every frame, but the card's content is static per hut — only touch
  // the DOM (innerHTML parses, string builds) when the nearby hut changes.
  if (nearbyHut === shownHutCard) return;
  shownHutCard = nearbyHut;
  const dialog = document.getElementById('gameDialog');
  if (!dialog) return;
  if (nearbyHut) {
    const enterKbd = '<kbd style="background:#ff6b6b; color:white; padding:2px 8px; border-radius:4px; font-family:ui-monospace,monospace;">ENTER</kbd>';
    if (nearbyHut.gateway) {
      setDomText('gameDialogLabel', 'Travel To');
      setDomText('gameDialogName', nearbyHut.name);
      setDomHtml('gameDialogHint', `Press ${enterKbd} to visit &nbsp;·&nbsp; walk away to cancel`);
    } else {
      setDomText('gameDialogLabel', nearbyHut.playable ? 'Game Hut' : 'Coming Soon');
      setDomText('gameDialogName', nearbyHut.playable ? `PLAY ${nearbyHut.name}?` : `${nearbyHut.name}`);
      setDomHtml('gameDialogHint', nearbyHut.playable
        ? `Press ${enterKbd} to play &nbsp;·&nbsp; walk away to cancel`
        : 'This game hut is still being built. Walk away to close this note.');
    }
    dialog.style.display = 'block';
  } else {
    dialog.style.display = 'none';
  }
}

function launchGame(hut) {
  if (!hut || !hut.playable) return;
  const query = new URLSearchParams({
    game: hut.slug,
    name: hut.name,
  });
  const requestedGrade = (new URLSearchParams(location.search).get('grade') || '').trim();
  const safeGrade = /^[A-Za-z0-9-]{1,12}$/.test(requestedGrade) ? requestedGrade : null;
  if (safeGrade) query.set('grade', safeGrade);
  if (SPOOKY) query.set('spooky', '1');   // content profile survives the round-trip
  window.location.href = `game.html?${query.toString()}`;
}

// Camera modes used in play mode. The default is the swing-free Diorama view
// ('fixed'): a constant world-direction 3/4 angle that slides to follow Chocky
// without rotating with his facing, so reversing direction never whips the
// camera around him. The over-the-shoulder modes stay available via C / buttons.
let cameraMode = 'fixed';
// Obby Peak is a vertical precision-jumping course, so it temporarily opts into
// the over-the-shoulder follow camera once Chocky starts climbing. When he falls
// back to the ground at the base, the helper restores the camera he was using
// before the assist. Scalars only; called from updatePlayer's hot path.
const OBBY_CAMERA_RADIUS = 6.8;
const OBBY_CAMERA_EXIT_RADIUS = 10.5;
const OBBY_CAMERA_MIN_CLIMB = 0.22;
// Obby assist uses the visible over-the-shoulder chase view, not the
// first-person mode (which hides Chocky and makes the tower read poorly).
const OBBY_CAMERA_MODE = 'follow';
const OBBY_CAMERA_ZOOM = 0.62;
let obbyCameraAssist = false;
let obbyCameraRestoreMode = 'fixed';
let obbyCameraRestoreZoom = 1;
function setPlayCameraMode(mode, { zoom = playZoom, stabilize = false } = {}) {
  const changed = cameraMode !== mode;
  cameraMode = mode;
  playZoom = zoom;
  if (stabilize || changed) {
    // Camera-mode swaps happen at Obby Peak's high platforms and summit. Reset
    // mode-local smoothing so the new normal/chase view starts from Chocky's
    // current platform height instead of carrying stale pull-in or diorama
    // anchor state from the previous mode.
    _camPullDist = -1;
    _camAnchorX = _camAnchorZ = null;
    if (player) _camGroundY = initialY + player.standY;
  }
  syncCamButtons();
}
function updateObbyCameraAssist() {
  if (!gameMode || player.dead) {
    obbyCameraAssist = false;
    return;
  }
  const dx = player.pos.x - OBBY_CENTER.x;
  const dz = player.pos.z - OBBY_CENTER.z;
  const distSq = dx * dx + dz * dz;
  const terrainY = heightAt(player.pos.x, player.pos.z);
  const onCourse = distSq <= OBBY_CAMERA_RADIUS * OBBY_CAMERA_RADIUS &&
                   player.pos.z >= OBBY_CENTER.z - 6.5 &&
                   player.pos.y > terrainY + OBBY_CAMERA_MIN_CLIMB;
  if (!obbyCameraAssist && onCourse) {
    obbyCameraAssist = true;
    obbyCameraRestoreMode = cameraMode === 'free' ? 'fixed' : cameraMode;
    obbyCameraRestoreZoom = playZoom;
    setPlayCameraMode(OBBY_CAMERA_MODE, { zoom: OBBY_CAMERA_ZOOM, stabilize: true });
    return;
  }
  if (!obbyCameraAssist) return;
  const onSummit = distSq < OBBY_SUMMIT_R * OBBY_SUMMIT_R && player.pos.y > OBBY_SUMMIT_TOP - 0.6;
  const backOnGround = player.onGround && player.pos.y <= terrainY + 0.12;
  const leftCourse = distSq > OBBY_CAMERA_EXIT_RADIUS * OBBY_CAMERA_EXIT_RADIUS ||
                     player.pos.z < OBBY_CENTER.z - 8.5;
  if (onSummit || backOnGround || (leftCourse && player.onGround)) {
    obbyCameraAssist = false;
    // If the player manually picked another camera during the course, keep
    // their choice. Otherwise restore the pre-Obby view once they are grounded.
    if (cameraMode === OBBY_CAMERA_MODE) {
      setPlayCameraMode(obbyCameraRestoreMode, { zoom: obbyCameraRestoreZoom, stabilize: true });
    }
  }
}
let optionsMenuOpen = false;

function prettyCameraMode(mode) {
  const labels = {
    fixed: 'Diorama',
    follow: 'Follow',
    first: 'First person',
    side: 'Side',
    top: 'Top-down',
    far: 'Far',
    free: 'Free orbit',
  };
  return labels[mode] || mode || 'Follow';
}

function syncOptionsMenuLabels() {
  const camLabel = document.getElementById('optionsCameraMode');
  if (camLabel) camLabel.textContent = prettyCameraMode(cameraMode);

  const profileLabel = document.getElementById('optionsProfileStatus');
  if (profileLabel) profileLabel.textContent = SPOOKY ? 'Spooky' : 'Cozy';

  const audioLabel = document.getElementById('optionsAudioStatus');
  if (audioLabel) {
    const media = document.querySelector('audio, video');
    audioLabel.textContent = media ? (media.muted ? 'Muted' : 'On') : 'Not available';
  }

  const versionLabel = document.getElementById('optionsVersion');
  if (versionLabel) versionLabel.textContent = APP_VERSION;

  syncEnemyToggle('optionsWolvesToggle', WOLVES_ENABLED);
  syncEnemyToggle('optionsShellShockersToggle', SHELL_SHOCKERS_ENABLED);
}

// Reflect an enemy toggle's on/off state onto its pill button (label + the
// aria-pressed flag the CSS keys the accent fill off).
function syncEnemyToggle(id, on) {
  const btn = document.getElementById(id);
  if (!btn) return;
  btn.textContent = on ? 'On' : 'Off';
  btn.setAttribute('aria-pressed', on ? 'true' : 'false');
}

// Flip wolves on/off at runtime: persist the choice, and when turning OFF clear
// any wolves already in the world so the change is immediate (the update gate
// stops new spawns on its own). syncOptionsMenuLabels refreshes the button.
function setWolvesEnabled(on) {
  WOLVES_ENABLED = !!on;
  saveEnemyPref('chocky.wolves', WOLVES_ENABLED);
  if (!WOLVES_ENABLED) clearAllWolves();
  syncOptionsMenuLabels();
}
function setShellShockersEnabled(on) {
  SHELL_SHOCKERS_ENABLED = !!on;
  saveEnemyPref('chocky.shellShockers', SHELL_SHOCKERS_ENABLED);
  if (!SHELL_SHOCKERS_ENABLED) clearShellShockers();
  updateEggHud();
  syncOptionsMenuLabels();
}

function setOptionsMenuOpen(open) {
  optionsMenuOpen = !!open;
  const menu = document.getElementById('optionsMenu');
  if (!menu) return;
  if (optionsMenuOpen) syncOptionsMenuLabels();
  menu.style.display = optionsMenuOpen ? 'grid' : 'none';
}

{
  const closeBtn = document.getElementById('optionsCloseBtn');
  if (closeBtn) closeBtn.addEventListener('click', () => setOptionsMenuOpen(false));
  const resumeBtn = document.getElementById('optionsResumeBtn');
  if (resumeBtn) resumeBtn.addEventListener('click', () => setOptionsMenuOpen(false));
  const wolvesBtn = document.getElementById('optionsWolvesToggle');
  if (wolvesBtn) wolvesBtn.addEventListener('click', () => setWolvesEnabled(!WOLVES_ENABLED));
  const shellBtn = document.getElementById('optionsShellShockersToggle');
  if (shellBtn) shellBtn.addEventListener('click', () => setShellShockersEnabled(!SHELL_SHOCKERS_ENABLED));
  // Editor-only row (revealed by html.editor-available): open the Build-tuning
  // panel through the editorHooks contract — core never touches editor DOM.
  const buildBtn = document.getElementById('optionsBuildBtn');
  if (buildBtn) buildBtn.addEventListener('click', () => {
    setOptionsMenuOpen(false);
    document.documentElement.classList.remove('kiosk');
    editorHooks.openBuildPanel();
  });
}

// Scroll-wheel zoom for the village. The play-mode camera is driven directly
// (OrbitControls is disabled except in 'free'), so we scale the camera's
// offset-from-Chocky by this factor. <1 pulls in close, >1 backs out.
let playZoom = 1;
const PLAY_ZOOM_MIN = 0.45, PLAY_ZOOM_MAX = 2.4;
const FIRST_PERSON_ZOOM_MIN = 0.82, FIRST_PERSON_ZOOM_MAX = 1.22;
renderer.domElement.addEventListener('wheel', (e) => {
  // In 'free' orbit, OrbitControls owns the wheel (dolly). Otherwise we drive
  // our own zoom and swallow the event so the page doesn't scroll.
  if (!gameMode || cameraMode === 'free') return;
  e.preventDefault();
  // Exponential step feels natural and is framerate-independent.
  playZoom *= Math.exp(e.deltaY * 0.0012);
  const minZoom = cameraMode === 'first' ? FIRST_PERSON_ZOOM_MIN : PLAY_ZOOM_MIN;
  const maxZoom = cameraMode === 'first' ? FIRST_PERSON_ZOOM_MAX : PLAY_ZOOM_MAX;
  playZoom = Math.max(minZoom, Math.min(maxZoom, playZoom));
}, { passive: false });

// Reused across frames so the camera update allocates nothing (no GC hitches).
const _camLook = new THREE.Vector3();
const _camTgt = new THREE.Vector3();
// Occlusion pull-in: when a structure blocks the view of Chocky, dolly the
// camera straight in to just before it. _camPullDist is the smoothed eye
// distance (scalar so the in-fast/out-slow blend stays direction-stable);
// -1 = uninitialized / not in an occludable mode.
const _camDir = new THREE.Vector3();
let _camPullDist = -1;
// Smoothed camera height anchor (world Y). Eases toward the surface Chocky
// stands on so the view rises to frame him on platforms; null = snap next frame
// (set on teleport/respawn).
let _camGroundY = null;
const CAM_PULL_MARGIN = 0.3;   // stop this far in front of the blocking face
const CAM_PULL_MIN = 1.6;      // never dolly closer than this to the look point
const CAM_PULL_IN_K = 24;      // fast pull-in rate (dt-based) — near instant
const CAM_PULL_OUT_K = 4;      // slow ease-out rate so clearing doesn't pop
// Diorama soft-follow: the camera tracks an XZ "anchor" rather than Chocky's
// raw position, so idle fidgets / tiny steps don't nudge the framing. The
// anchor only moves once Chocky drifts past CAM_DEADZONE, then eases after him.
// null = snap to Chocky next frame (teleport/respawn). Scalars, not Vector3, so
// the per-frame update allocates nothing.
let _camAnchorX = null, _camAnchorZ = null;
const CAM_DEADZONE = 1.2;      // Chocky roams this far before the anchor follows
const CAM_ANCHOR_K = 3.5;      // how fast the anchor eases toward him once outside
function updateGameCamera(dt) {
  // We bypass OrbitControls in game mode (except free orbit) and drive the
  // camera directly via camera.lookAt(). OrbitControls.update() was snapping
  // the camera back into an orbit around target.y and hiding jumps.
  // Camera heights ride the SURFACE under Chocky — terrain, or a platform top
  // when he's standing on one (player.standY, held during jumps so the camera
  // doesn't bob on a jump arc). Anchoring to terrain alone buried the look point
  // inside tall props he stood on, filling the view with their interior wall.
  const targetGroundY = initialY + player.standY;
  if (_camGroundY === null) _camGroundY = targetGroundY;  // snap on first frame / after reset
  _camGroundY += (targetGroundY - _camGroundY) * Math.min(1, dt * 6);
  const terrY = _camGroundY;
  const groundLookY = terrY + 0.9;
  _camLook.set(chocky.position.x, groundLookY, chocky.position.z);
  // Tighter follow — keeps Chocky centred more reliably while exploring
  const lerpK = Math.min(1, dt * 9);
  if (chocky) chocky.visible = cameraMode !== 'first';

  if (cameraMode === 'free') {
    controls.enabled = true;
    controls.target.lerp(_camLook, lerpK);
    return;
  }
  controls.enabled = false;

  // Each mode is a base offset from Chocky; scroll-wheel zoom scales it so the
  // camera dollies straight in/out along the view direction. The "behind"
  // offsets rotate about Y by hand (sin/cos) — cheaper than applyAxisAngle,
  // which allocates a Quaternion internally on every call.
  const z = playZoom;
  const sf = Math.sin(player.facing), cf = Math.cos(player.facing);
  // Outside the diorama, drop the soft-follow anchor so re-entering 'fixed'
  // (via C / the buttons) snaps cleanly to where Chocky is now.
  if (cameraMode !== 'fixed') { _camAnchorX = _camAnchorZ = null; }
  if (cameraMode === 'follow') {
    // Wider 3/4 view (like classic 3D platformer): higher up, further back.
    _camTgt.set(chocky.position.x - 8.0 * sf * z, terrY + 3.8 * z, chocky.position.z - 8.0 * cf * z);
  } else if (cameraMode === 'first') {
    _camTgt.set(chocky.position.x + 0.05 * sf, terrY + 1.28 * z, chocky.position.z + 0.05 * cf);
    _camLook.set(chocky.position.x + 5.0 * sf, terrY + 1.2 * z, chocky.position.z + 5.0 * cf);
  } else if (cameraMode === 'side') {
    _camTgt.set(chocky.position.x + 6 * z, terrY + 1.6 * z, chocky.position.z);
  } else if (cameraMode === 'top') {
    _camTgt.set(chocky.position.x, terrY + 9 * z, chocky.position.z + 0.5 * z);
  } else if (cameraMode === 'far') {
    _camTgt.set(chocky.position.x - 11 * sf * z, terrY + 5 * z, chocky.position.z - 11 * cf * z);
  } else if (cameraMode === 'fixed') {
    // Diorama tracking: a CONSTANT world-direction 3/4 angle (deliberately NOT
    // rotated by Chocky's facing — that is the whole anti-swing property). The
    // camera follows a soft XZ anchor that lags inside a small dead-zone, so
    // idle fidgets and tiny steps don't jitter the framing; the anchor glides
    // after him once he walks past CAM_DEADZONE.
    const px = chocky.position.x, pz = chocky.position.z;
    if (_camAnchorX === null) { _camAnchorX = px; _camAnchorZ = pz; }  // snap on first frame / reset
    const ax = px - _camAnchorX, az = pz - _camAnchorZ;
    const ad = Math.hypot(ax, az);
    if (ad > CAM_DEADZONE) {
      // Pull the anchor to the dead-zone edge, then ease the rest (dt-stable).
      const over = (ad - CAM_DEADZONE) / ad;
      const ease = 1 - Math.exp(-CAM_ANCHOR_K * dt);
      _camAnchorX += ax * over * ease;
      _camAnchorZ += az * over * ease;
    }
    // Constant offset, scaled by zoom (slightly tighter/lower than before so
    // Chocky reads larger on a Chromebook). Look height matches groundLookY so
    // the diorama rises with him on platforms, consistent with the other modes.
    _camTgt.set(_camAnchorX + 7 * z, terrY + 5.5 * z, _camAnchorZ + 10 * z);
    _camLook.set(_camAnchorX, groundLookY, _camAnchorZ);
  }

  // Occlusion pull-in: if a structure sits between the look point (Chocky's
  // head) and the desired eye, dolly the eye in to just before it so Chocky —
  // and the area he reads hut signs in — stays visible. Applies to the low
  // over-the-shoulder views only; 'top' looks down and 'free' returned earlier.
  // 'fixed' (Diorama) is deliberately EXCLUDED: its eye rides high above every
  // rooftop, so it is never buried in geometry — and the hard, fast dolly is
  // exactly the "swing" the diorama is meant to avoid. Occluders that cross its
  // ray are faded to translucent instead (see updateOcclusionFade / structure
  // fade), which keeps the camera perfectly still while restoring visibility.
  if (cameraMode === 'follow' || cameraMode === 'far' ||
      cameraMode === 'side') {
    _camDir.subVectors(_camTgt, _camLook);          // look -> desired eye
    const desiredDist = _camDir.length();
    if (desiredDist > 1e-4) {
      _camDir.multiplyScalar(1 / desiredDist);      // normalize in place
      const hit = raycastObstacles(
        _camLook.x, _camLook.y, _camLook.z,
        _camDir.x, _camDir.y, _camDir.z, desiredDist);
      let want = desiredDist;
      // hit ≈ 0 means the look point is *inside* a structure (e.g. mid-jump over
      // a tall prop): dollying toward a buried target just fills the screen with
      // its wall, so keep the wide view and let the height anchor catch up.
      if (hit > 0.05 && hit < desiredDist) want = Math.max(CAM_PULL_MIN, hit - CAM_PULL_MARGIN);
      // Snap in fast, ease out slow; framerate-independent.
      if (_camPullDist < 0) _camPullDist = want;
      const rate = want < _camPullDist ? CAM_PULL_IN_K : CAM_PULL_OUT_K;
      _camPullDist += (want - _camPullDist) * (1 - Math.exp(-rate * dt));
      _camTgt.copy(_camLook).addScaledVector(_camDir, _camPullDist);
    }
  } else {
    _camPullDist = -1;  // reset so re-entering an occludable mode snaps cleanly
  }

  camera.position.lerp(_camTgt, lerpK);
  // Camera shake (decays over time)
  if (camShake > 0.001) {
    camera.position.x += (Math.random() - 0.5) * camShake;
    camera.position.y += (Math.random() - 0.5) * camShake;
    camera.position.z += (Math.random() - 0.5) * camShake;
    camShake *= 0.85;
  }
  camera.lookAt(_camLook);
}

let lastCameraModeForHint = cameraMode;
function syncCamButtons() {
  const enteredFreeOrbit = gameMode && cameraMode === 'free' && lastCameraModeForHint !== 'free';
  document.querySelectorAll('#playCamUI button').forEach(b => {
    b.classList.toggle('active', b.dataset.cam === cameraMode);
  });
  syncOptionsMenuLabels();

  const freeOrbitHint = document.getElementById('freeOrbitHint');
  if (freeOrbitHint) freeOrbitHint.style.display = cameraMode === 'free' ? 'inline' : 'none';

  if (enteredFreeOrbit) {
    showPlayHelpFull();
    schedulePlayHelpCollapse();
  }
  lastCameraModeForHint = cameraMode;
}

document.querySelectorAll('#playCamUI button[data-cam]').forEach(b => {
  b.addEventListener('click', () => {
    setPlayCameraMode(b.dataset.cam);
  });
});



// Play-mode hotkeys: C = cycle camera, R = respawn, ENTER = launch, ! = sign editor
addEventListener('keydown', e => {
  if (isTypingTarget(e.target)) return;
  // Editor hotkeys (` / ~ kiosk toggle, ! sign editor). The kid build ships a
  // no-op hook, so neither key does anything there.
  if (editorHooks.handleHotkey(e)) return;
  if (!gameMode) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    if (e.repeat) return;
    setOptionsMenuOpen(!optionsMenuOpen);
    return;
  }
  const k = e.key.toLowerCase();
  if (k === 'c') {
    if (e.repeat) return;
    // Diorama ('fixed') is the default/rest state, so it leads the cycle; the
    // rest stay reachable by tapping C again.
    const modes = ['fixed', 'follow', 'first', 'side', 'top', 'far', 'free'];
    setPlayCameraMode(modes[(modes.indexOf(cameraMode) + 1) % modes.length]);
  } else if (k === 'r') {
    if (e.repeat) return;
    // Manual respawn doubles as an escape from Shell Shockers, SKULLFACE and wolves.
    clearShellShockers();
    clearSkullface();
    clearAllWolves();
    if (player.dead) { regeneratePlayer(); return; }
    movePlayerToVillageCheckpoint(12, 1.4);
    triggerFace('dizzy', 0.7);   // a little dazed by the teleport
    updateEggHud();
  } else if (k === 'enter' && ((nearbyHut && (nearbyHut.playable || nearbyHut.gateway)) || nearWell)) {
    e.preventDefault();
    if (e.repeat) return;
    if (nearbyHut && (nearbyHut.playable || nearbyHut.gateway)) activateNearbyHut();
    else throwCoinInWell();
  } else if (k === 'y' && nearWell) {
    e.preventDefault();
    if (e.repeat) return;
    throwCoinInWell();
  } else if (k === 'm') {
    // Toggle the wayfinding minimap (src/game/minimap.js).
    if (e.repeat) return;
    minimapApi.toggle();
  } else if (k === 'h') {
    // Fast-travel home (same as the minimap's 🏠 Home button).
    if (e.repeat) return;
    goHome();
  } else if (k === '$') {
    // Testing cheat: top up coins (Shift+4 → e.key === '$').
    if (e.repeat) return;
    coinInventory += 10;
    coinTotalCollected += 10;
    updateCoinUI();
    triggerFace('grin', 0.6);
  }
});

// Tap/click activation for the hut card and the wishing-well prompt — the
// touch path to ENTER interactions (and a nicety for mouse kids too).
function activateNearbyHut() {
  if (!gameMode || !nearbyHut || !(nearbyHut.playable || nearbyHut.gateway)) return;
  if (nearbyHut.gateway) window.location.href = nearbyHut.gateway;
  else launchGame(nearbyHut);
}
{
  const hutCard = document.getElementById('gameDialog');
  hutCard.style.pointerEvents = 'auto';
  hutCard.style.cursor = 'pointer';
  hutCard.addEventListener('click', activateNearbyHut);
  const bumpCard = document.getElementById('bumpMsg');
  bumpCard.style.pointerEvents = 'auto';
  bumpCard.addEventListener('click', () => {
    if (gameMode && nearWell) throwCoinInWell();
  });
}

// ---- Game-mode poses (no chockyRoot.position.y modification; physics handles it)
function runPose(t) {
  const speed = 2 * Math.PI / 0.45;   // faster than walk
  const s = Math.sin(t * speed);
  const c = Math.cos(t * speed);
  const stepPhase = Math.abs(c);
  poseLeg('L',  Math.sign(s) * Math.pow(Math.abs(s), 0.6) * 0.95);
  poseLeg('R', -Math.sign(s) * Math.pow(Math.abs(s), 0.6) * 0.95);
  poseCalf('L', Math.max(0, -s) * 1.4);
  poseCalf('R', Math.max(0,  s) * 1.4);
  // Arms pump fore/aft at the sides with sharply bent elbows (a runner's tuck),
  // opposite to the legs on each side.
  relaxArm('L', -1.28, -s * 0.85, -1.05 - Math.max(0, -s) * 0.3);
  relaxArm('R', -1.28,  s * 0.85, -1.05 - Math.max(0,  s) * 0.3);
  poseSpine(0.12 + (1 - stepPhase) * 0.05);  // forward lean for running
  poseHead(-0.10, s * 0.06, 0);
  const squash = 1 - stepPhase * 0.08;
  setSquash(squash);
}

function jumpUpPose(upVel) {
  // Stretched up, arms up, legs trailing
  const k = Math.min(1, upVel / 5);
  poseArm('L', 0, Math.PI/2 + 0.2);
  poseArm('R', 0, Math.PI/2 + 0.2);
  poseLeg('L', -0.3 * k);
  poseLeg('R', -0.3 * k);
  poseCalf('L', 0.4 * k);
  poseCalf('R', 0.4 * k);
  poseSpine(-0.05);
  poseHead(-0.1, 0, 0);
  setSquash(1.15 + k * 0.08);   // stretched
}

function jumpDownPose(downVel) {
  // Starting to tuck for landing, arms come forward for balance
  const k = Math.min(1, downVel / 5);
  poseArm('L', 0.4 + k * 0.3, 0.3);
  poseArm('R', 0.4 + k * 0.3, 0.3);
  poseLeg('L', -0.5);
  poseLeg('R', -0.5);
  poseCalf('L', 0.8);
  poseCalf('R', 0.8);
  poseSpine(0.05);
  poseHead(0.1, 0, 0);
  setSquash(1.08 - k * 0.1);
}

function landPose(t) {
  // 0.18s squash + spring back
  const k = Math.min(1, t / 0.18);
  const e = ease.outElastic(k);
  poseLeg('L', -0.6 * (1 - e * 0.7));
  poseLeg('R', -0.6 * (1 - e * 0.7));
  poseCalf('L', 1.1 * (1 - e * 0.7));
  poseCalf('R', 1.1 * (1 - e * 0.7));
  poseArm('L', 0.5 * (1 - e), 0);
  poseArm('R', 0.5 * (1 - e), 0);
  poseSpine(0.15 * (1 - e));
  setSquash(0.78 + e * 0.22);   // big impact squash → spring up
}

// ====================================================================
// RUNTIME QUALITY GOVERNOR
// ====================================================================
// TIER is a boot-time guess from GPU strings, and fleet diversity means
// guesses misfire (design plan §3). This is the measured correction: a
// rolling average of real frame time drives the render scale down (up to four
// ×0.85 steps — enough to fall from native DPR back to roughly the old 1.5 cap)
// while the device sustains under ~27 fps, and back up after a
// long comfortable stretch. Conservative on purpose: one step is barely
// visible at 1366×768, and the cooldown + hysteresis keep it from ever
// oscillating. More levers (shadows off, ambience pools) can hang off
// _qgStep later if real fleets need them.
let _qgAccum = 0, _qgFrames = 0, _qgStep = 0, _qgCooldown = 8, _qgGoodTime = 0;
let _qualityStep = 0;
const QG_BASE_RATIO = renderer.getPixelRatio();
function qualityCadence(baseHz, degradedHz) {
  return _qualityStep >= 3 ? degradedHz : baseHz;
}
function applyQgStep() {
  _qualityStep = _qgStep;
  renderer.setPixelRatio(QG_BASE_RATIO * Math.pow(0.85, _qgStep));
  renderer.setSize(innerWidth, innerHeight);
  if (_qualityStep >= 3) {
    console.log('[perf] governor: degraded ambience/enemy cadence enabled');
  }
}
function updateQualityGovernor(dt) {
  _qgAccum += dt;
  _qgFrames++;
  if (_qgCooldown > 0) _qgCooldown -= dt;
  if (_qgAccum < 3) return;                       // judge ~3s windows
  const avgMs = (_qgAccum / _qgFrames) * 1000;
  _qgAccum = 0; _qgFrames = 0;
  if (_qgCooldown > 0) return;
  if (avgMs > 37 && _qgStep < 4) {                // sustained < ~27 fps
    _qgStep++;
    applyQgStep();
    _qgCooldown = 6;
    _qgGoodTime = 0;
    console.log(`[perf] governor: render scale down to step ${_qgStep} (avg ${avgMs.toFixed(1)}ms)`);
  } else if (avgMs < 22 && _qgStep > 0) {         // comfy > ~45 fps
    _qgGoodTime += 3;
    if (_qgGoodTime >= 12) {                      // …for 12s straight
      _qgStep--;
      applyQgStep();
      _qgCooldown = 6;
      _qgGoodTime = 0;
      console.log(`[perf] governor: render scale back up to step ${_qgStep} (avg ${avgMs.toFixed(1)}ms)`);
    }
  } else {
    _qgGoodTime = 0;
  }
}

// ====================================================================
// MAIN LOOP
// ====================================================================
// dt comes from the rAF timestamp (vsync-aligned frame start), not a Clock read
// inside the callback — performance.now() at callback time carries main-thread
// scheduling jitter that reads as judder even at a steady 60fps.
let _prevNow = -1;                      // -1 = resync (first frame, hidden tab, lost context)
let _elapsed = 0;                       // uncapped running time (replaces clock.elapsedTime)
let _frame = 0;                         // frame counter for cadence throttles
// Throttled draw-call diagnostic — logs to console every 3s on low tier
let _perfTimer = 0;
let _fpsHudAccum = 0, _fpsHudFrames = 0;
// The LOW cadence accumulators start at staggered phases so the 24/20/15/12/10
// Hz systems don't all fire on the same rAF (their common multiples would
// otherwise beat into periodic everything-at-once frames). Reset-to-0-on-fire
// preserves the offsets.
let _lowFxAccum = 1 / 40;
let _lowAmbAccum = 1 / 24;
let _lowEnemyAnimAccum = 1 / 48;
let _lowEnemyAccum = 1 / 30;
let _lowZoneAccum = 1 / 20;
let _lowHudAccum = 1 / 25;
let _enemyCosmeticReady = true;
let _enemyCosmeticDt = 0;
// Browser RAF is already paced to the display. Manually skipping near-vsync
// callbacks makes frame intervals uneven on 60Hz Chromebook panels, so we render
// every visible RAF and control cost with cheaper work, not artificial drops.
function tick(now) {
  requestAnimationFrame(tick);
  // Pause entirely while the GL context is lost (awaiting restore → reload)
  if (isContextLost()) { _prevNow = -1; return; }
  // Skip everything when tab is hidden (frees CPU + GPU for the host)
  if (document.hidden) { _prevNow = -1; return; }
  const rawDt = _prevNow >= 0 ? (now - _prevNow) / 1000 : 0;
  _prevNow = now;
  _elapsed += rawDt;
  const dt = Math.min(rawDt, 0.05);   // cap big dt (alt-tab spikes)
  updateQualityGovernor(dt);
  _enemyCosmeticReady = !LOW;
  _enemyCosmeticDt = dt;
  if (LOW) {
    _lowEnemyAnimAccum += dt;
    if (_lowEnemyAnimAccum >= 1 / 24) {
      _enemyCosmeticReady = true;
      _enemyCosmeticDt = Math.min(_lowEnemyAnimAccum, 0.12);
      _lowEnemyAnimAccum = 0;
    }
  }
  if (!editorHooks.paused) animTime += dt;

  // Day-night cycle advances in both modes (it owns the sky + lights).
  updateZoneVisualTarget();
  updateDayNight(dt);
  updateHeroLandmarks(dt);
  updateWater(dt, animTime);

  if (chocky) {
    if (gameMode) {
      // PLAY MODE: input + physics + state-machine pose
      updatePlayer(dt);
      // Mixer drives the baked locomotion/emote clips when active. It runs after
      // updatePlayer, so re-apply any editor bone overrides on top of it here
      // (the in-loop application above is for the procedural path).
      if (mixer && activeAction) {
        mixer.update(dt);
        applyPlayerModelTransform();
        applyBoneOverrides();
      }
      updateDust(dt);
      updateFootprints(dt);
      updateBumpZones();
      updateWell(dt);
      storeApi.update(dt);
      minimapApi.update(dt);
      updateBumpMsg(dt);
      updateTouchMarker(dt);
      const tNow = _elapsed;
      if (LOW) {
        // Transparent ambience is fill-rate heavy and visually forgiving. Step
        // it at a fixed 20Hz on Chromebooks while gameplay/camera remain 60Hz.
        _lowFxAccum += dt;
        _lowAmbAccum += dt;
        const fxHz = qualityCadence(20, 12);
        const ambHz = qualityCadence(12, 8);
        if (_lowFxAccum >= 1 / fxHz) {
          const fxDt = Math.min(_lowFxAccum, 0.14);
          _lowFxAccum = 0;
          updateSmoke(fxDt);
          updateButterflies(fxDt, tNow);
          updateDragonflies(fxDt, tNow);
          updateBees(fxDt, tNow);
          updateGroundMist(fxDt, tNow);
          updateClouds(fxDt, skyNightFactor);
        }
        if (_lowAmbAccum >= 1 / ambHz) {
          const ambDt = Math.min(_lowAmbAccum, 0.16);
          _lowAmbAccum = 0;
          updateAmbience(ambDt, tNow);
        }
      } else {
        updateSmoke(dt);
        updateButterflies(dt, tNow);
        updateDragonflies(dt, tNow);
        updateBees(dt, tNow);
        updateGroundMist(dt, tNow);
        updateAmbience(dt, tNow);
        updateClouds(dt, skyNightFactor);
      }
      updateSwayTime(tNow);   // one uniform write; the sway itself runs in the vertex shader
      // Occlusion ghosting: fade trees / huts that fall between the (now final)
      // camera eye and Chocky, so the still diorama view never loses sight of
      // him without the camera having to lurch in. Cheap scalar tests; runs in
      // every play camera mode (harmless where pull-in also acts — that just
      // dollies the occluder off the sightline, which un-fades it). The LOW
      // lever is OCCLUSION_FADE_DISABLED, not cadence: throttling the update
      // wouldn't change the transparent-pass cost, which is what hurts there.
      if (chocky) {
        const lookY = chocky.position.y + 0.9;   // upper body — what the eye frames
        updateOcclusionFade(
          camera.position.x, camera.position.y, camera.position.z,
          chocky.position.x, lookY, chocky.position.z, dt);
        updateStructureFade(camera.position, chocky.position.x, lookY, chocky.position.z, dt);
      }
      // Stream world chunks around the player a few times a second.
      if ((_frame % 20) === 0) updateChunkStreaming(player.pos.x, player.pos.z);
      updateCoin(dt, tNow);
      updateBirds(dt, tNow);
      wizardLightningFx.update(dt);
      updateWolfAshPiles(dt);
      if (LOW) {
        const enemyHz = qualityCadence(24, 15);
        const zoneHz = qualityCadence(15, 10);
        _lowEnemyAccum += dt;
        _lowZoneAccum += dt;
        _lowHudAccum += dt;
        if (_lowEnemyAccum >= 1 / enemyHz) {
          const enemyDt = Math.min(_lowEnemyAccum, 0.14);
          _lowEnemyAccum = 0;
          updateWolves(enemyDt, tNow);
          updateSkullface(enemyDt, tNow);
          updateShellShockers(enemyDt, tNow);
        }
        if (_lowZoneAccum >= 1 / zoneHz) {
          const zoneDt = Math.min(_lowZoneAccum, 0.18);
          _lowZoneAccum = 0;
          updateWordville(zoneDt, tNow, player.pos);
          updateGrove(zoneDt, tNow, player.pos);
          updateObby(zoneDt, tNow, player.pos);
          updateNumberton(zoneDt, tNow, player.pos);
          questApi.update(zoneDt, tNow);
          critterQuestApi.update(zoneDt, tNow);
          bunnyQuestApi.update(zoneDt, tNow);
        }
        if (_lowHudAccum >= 1 / 10) {
          _lowHudAccum = 0;
          updateEggHud();
        }
      } else {
        updateWolves(dt, tNow);
        updateSkullface(dt, tNow);
        updateShellShockers(dt, tNow);
        updateWordville(dt, tNow, player.pos);
        updateGrove(dt, tNow, player.pos);
        updateObby(dt, tNow, player.pos);
        updateNumberton(dt, tNow, player.pos);
        questApi.update(dt, tNow);
        critterQuestApi.update(dt, tNow);
        bunnyQuestApi.update(dt, tNow);
        updateEggHud();
      }
    } else if (!editorHooks.paused) {
      if (activeAction) {
        // Baked path: AnimationMixer drives the bones directly
        if (mixer) mixer.update(dt);
      } else if (currentAnim) {
        // Procedural path: reset then apply our code-driven anim
        resetPose();
        (ANIMS[currentAnim] || ANIMS.idle)(animTime);
      }
      // Re-apply any user bone overrides (works for both paths)
      applyBoneOverrides();
    }
    if (editorHooks.autoSpin && !gameMode) chocky.rotation.y += dt * 0.4;
    // Drive his facial expression (works in both edit and play mode).
    if (!editorHooks.paused) updateFace(dt);
  }

  // Only update OrbitControls when it's actually driving the camera
  if (!gameMode || cameraMode === 'free') {
    controls.update();
  }
  // Keep the sun's shadow frustum centred on Chocky (runs after updatePlayer, so
  // it uses this frame's position) before we re-render the depth map below.
  updateShadowFollow();
  if (skyWash.visible) skyWash.position.copy(camera.position);
  // Refresh the shadow map at a reduced cadence (see SHADOW_INTERVAL). On the
  // frames we skip, the previous depth map is reused — free, and imperceptible.
  if (renderer.shadowMap.enabled && (SHADOW_INTERVAL <= 1 || (_frame % SHADOW_INTERVAL) === 0)) {
    renderer.shadowMap.needsUpdate = true;
  }
  _frame++;
  if (LOW && gameMode) {
    _fpsHudAccum += dt;
    _fpsHudFrames++;
    if (_fpsHudAccum >= 0.5) {
      const fpsEl = document.getElementById('chromebookFps');
      if (fpsEl) fpsEl.textContent = `${Math.round(_fpsHudFrames / _fpsHudAccum)} FPS`;
      _fpsHudAccum = 0;
      _fpsHudFrames = 0;
    }
  }
  renderer.render(scene, camera);
  // Once-per-3s draw call diagnostic (low tier only — see if we're hitting target)
  if (LOW) {
    _perfTimer += dt;
    if (_perfTimer > 3) {
      _perfTimer = 0;
      const info = renderer.info;
      console.log(`[perf] calls=${info.render.calls} tris=${info.render.triangles}`);
    }
  }
}

// ============= STATIC-TRANSFORM FREEZE =============
// Everything boot-built is in the scene by now. Snapshot the matrices of all
// static propsGroup members (zone decor, signposts, letter blocks…) and the
// terrain meshes, and opt them out of three.js' per-frame matrix recompose —
// the same win the chunk system already takes (freezeStaticObject). Builders
// that animate transforms marked their subtrees with markDynamic(); in editor
// loads the gizmo-attachable sign/structure groups stay live too (mirrors the
// captureStructure EDITOR_ENABLED gate). Anything created after boot (wolves,
// eggs, editor spawns) defaults to matrixAutoUpdate=true and is unaffected.
{
  const exempt = EDITOR_ENABLED
    ? new Set([...signGroups.map((s) => s.group), ...editableStructures.map((s) => s.group)])
    : null;
  let frozen = freezeStatics(propsGroup, exempt ? (o) => exempt.has(o) : null);
  for (const o of [playFloor, townGround, playShadowOverlay, grid]) {
    o.updateMatrix();
    o.matrixAutoUpdate = false;
    frozen++;
  }
  console.log(`[perf] froze ${frozen} static transforms`);
}

requestAnimationFrame(tick);

// ====================================================================
// EDITOR TOOLING LOADER
// ====================================================================
if (EDITOR_ENABLED) {
  import('./editor/editor.js')
    .then(({ initEditor }) => {
      initEditor({
        hooks: editorHooks,
        scene, camera, renderer, controls,
        lights: { hemi, key, fill, rim },
        floor, signGroups, editableStructures, bones, restQ, bakedClips,
        // World editor surfaces: the terrain mesh + its geometry (playFloor and
        // the shadow overlay share it), the heightfield/chunk/collision runtime
        // APIs, and the live tree builder.
        terrainMesh: playFloor,
        terrainGeo,
        terrain: { heightAt, sculpt, redisplaceRegion, getHeightDeltas, setHeightDeltas, clearHeightDeltas },
        chunkApi: { addPropLive, removePropById, removePropNear, enumerateProps, resettleProps, movePropRecord, rebuildChunkAt },
        collisionApi: { addMovableObstacle, updateMovableObstacle, removeMovableObstacle },
        placeTree: placeTreeLive,
        // Runtime factories for the unified editor: create new signs (with live
        // editable text) and new/duplicated structures (collision auto-wired
        // through captureStructure). Each returns a record with a dispose().
        createSign, setSignText, createStructure,
        // Kit building kinds for the structure-type picker (world/kit.js).
        kitStructureKinds: KIT_STRUCTURE_KINDS,
        getChocky: () => chocky,
        resetPose, playBaked, stopBaked,
        setCurrentAnim: (v) => { currentAnim = v; },
        resetAnimTime: () => { animTime = 0; },
        setLift: (v) => { initialY = v; if (chockyRoot) chockyRoot.position.y = v; },
        getGameMode: () => gameMode,
        setPlayMode,
        // Show the village + hills (and frame them) without entering play mode,
        // so the ! object editor and terrain sculptor have something to click.
        setEditorWorldView,
        dayNight: {
          isEnabled: () => dayNightEnabled,
          setEnabled: (v) => { dayNightEnabled = v; },
          getTime: () => dayTime,
          setTime: (v) => { dayTime = v; },
          getNightFactor: () => skyNightFactor,
          setNightFactor: (v) => { skyNightFactor = v; },
          apply: applyDayNight,
          sunDirWorld,
        },
      });
    })
    .catch((err) => console.error('[editor] failed to load:', err));
}

// ====================================================================
// SERVICE WORKER (production builds only)
// ====================================================================
// __CHOCKY_BUILD__ is defined by vite at build time (see vite.config.js).
// The no-build page and the vite dev server never register the worker — a
// stale cache during development is worse than no cache at all. sw.js is
// generated into the build output by the chocky:service-worker plugin.
const IS_BUILD = typeof __CHOCKY_BUILD__ !== 'undefined' && __CHOCKY_BUILD__;
if ('serviceWorker' in navigator) {
  if (IS_BUILD) {
    const registerSW = () => navigator.serviceWorker.register('./sw.js').catch((err) => {
      // Non-fatal: the game runs fine without offline caching.
      console.warn('[sw] registration failed:', err);
    });
    // This module uses a top-level await (baked-world load) before reaching here,
    // which can push execution past the window 'load' event — in which case a
    // 'load' listener would never fire. Register immediately if load is already
    // done, otherwise wait for it.
    if (document.readyState === 'complete') registerSW();
    else window.addEventListener('load', registerSW);
  } else {
    // No-build and vite dev must not be controlled by an old production worker
    // from the same origin, or stale cached bundles can mask source edits.
    if (navigator.serviceWorker.getRegistrations) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const reg of regs) reg.unregister();
        if (regs.length) console.warn(`[sw] unregistered ${regs.length} stale build worker(s)`);
      }).catch(() => {});
    }
  }
}
